"use client";

import { useState, useEffect, useCallback, useRef, Fragment, type ReactNode } from "react";
import {
  DndContext,
  DragOverlay,
  MouseSensor,
  TouchSensor,
  pointerWithin,
  useDraggable,
  useDroppable,
  useSensor,
  useSensors,
  type DragEndEvent,
  type DragOverEvent,
  type DragStartEvent,
} from "@dnd-kit/core";
import SupportView from "./SupportView";
import ErrorsView from "./ErrorsView";
import {
  card as uiCard,
  field as uiField,
  lbl as uiLbl,
  bAccent as uiAccent,
  bSurface as uiSurface,
  bDanger as uiDanger,
  bSmall,
  motionIcon,
  PillRow,
  Switch,
  ArrowLeft,
  ArrowRight,
  CloseIcon,
} from "../_desk/ui";
import { KaitMark } from "../_desk/Brand";
import Rail, { type RailItem } from "../_desk/Rail";
import { compressImage } from "@/lib/ui/image";
import { CLONE_PARTS, CLONE_PART_HINTS, CLONE_PART_LABELS, type ClonePart } from "@/lib/network/clone";
import { plural } from "@/lib/format/plural";
import { spendMoney } from "@/lib/ai/spend";
import DateField from "../_desk/DateField";
import InventorySheetView from "../_desk/InventorySheetView";
import type { InventorySheet } from "@/lib/inventory/sheet-db";
import IikoCategoryMapEditor, { type CategoryMap, type SeenCategory } from "../_desk/IikoCategoryMapEditor";
import { ThemeToggle } from "./ThemeToggle";

// Консоль оператора (платформенный админ). Доступ — общий ключ ADMIN_API_KEY
// (заголовок x-admin-key). Отдельный desktop-экран за паролем, вне тенантов.
// Визуал — продолжение мобильного приложения: тёмная база + лаймовый акцент.

const KEY_STORAGE = "mise_operator_key";

type ApiResult<T = unknown> = { ok: true; data: T } | { ok: false; error: string };

async function opApi<T = unknown>(
  key: string,
  action: string,
  payload?: unknown,
): Promise<ApiResult<T>> {
  try {
    // Действие дублируется в адресе: по нему сервер выбирает лимит на размер
    // тела ДО чтения (загрузки файлов из консоли — каталог, меню, логотип —
    // весят больше общего лимита). Главным остаётся действие из тела.
    const res = await fetch(`/api?action=${encodeURIComponent(action)}`, {
      method: "POST",
      headers: { "content-type": "application/json", "x-admin-key": key },
      body: JSON.stringify({ action, payload }),
    });
    return (await res.json()) as ApiResult<T>;
  } catch {
    return { ok: false, error: "Нет связи с сервером" };
  }
}

// ── типы ответов ──
type Module = { key: string; label: string; defaultOn?: boolean; newTenant?: boolean };
type OwnerLite = { id: string; name: string; email: string | null; phone: string | null };
// slug — первый сегмент пути в адресе точки: sezon.klait.ru/patriki/desk
type VenueLite = { id: string; name: string; slug: string };
// Ответ копирования настройки между ресторанами сети (19.09).
type CloneResult = {
  source: string;
  target: string;
  results: { part: ClonePart; added: number; updated: number; skipped: number; note: string }[];
};
type TenantRow = {
  id: string;
  slug: string;
  name: string;
  logo: string | null;
  monthly_fee: string | null;
  feature_flags: Record<string, boolean>;
  created_at: string;
  // Папка ресторанов, если заведение в сети (Э1). null — одиночный клиент.
  group_id: string | null;
  archived: boolean;
  venues: VenueLite[];
  owners: OwnerLite[];
};
// Папка ресторанов: сводные цифры считает сервер (opListGroups), чтобы список
// не пересчитывал их на каждый рендер и не запрашивал вторым проходом.
type GroupRow = {
  id: string;
  slug: string;
  name: string;
  logo: string | null;
  created_at: string;
  // «Владелец сети» — если заполнен, форма онбординга автовыставит «владелец
  // как у папки» при добавлении нового ресторана (правка Марата 17.09).
  has_owner: boolean;
  // Общий адрес сети (Э2): `{slug}.klait.ru/{ресторан}`.
  domain_enabled: boolean;
  // Один бот на папку (Э4): у сети свой токен, рестораны без своего наследуют.
  has_bot: boolean;
  tenant_ids: string[];
  tenants_count: number;
  venues_count: number;
  monthly_fee_total: string;
};

// Хелпер под форму онбординга (17.09): у папки уже назначен владелец сети — новый
// ресторан наследует его по умолчанию.
function groupOwnerReady(g: GroupRow | undefined | null): boolean {
  return Boolean(g?.has_owner);
}
type OperatorAdminRow = {
  id: string;
  name: string;
  keyFp: string;
  isActive: boolean;
  note: string | null;
  lastSeenAt: string | null;
  createdAt: string;
};
type Recipient = { chat_id: string; role: string };
/**
 * Состояние Telegram у клиента.
 *
 * `has_token` — «бот работает», `own_token` — «работает СВОЙ бот заведения»,
 * `shared_ready` — «в окружении есть общий бот KLAIT». Одного флага не хватало:
 * общий бот выглядел в консоли как токен клиента.
 */
/** Ответ opTelegramHealth: что Telegram думает о нашем боте. */
type BotHealth = {
  reachable: boolean;
  username: string | null;
  own_bot: boolean;
  webhook_url: string | null;
  pending: number;
  last_error: string | null;
  last_error_at: string | null;
  // Кому бот СМОЖЕТ написать и не выключены ли каналы у точки (09.09). Живой
  // токен и заданный вебхук об этом не говорят ничего, а разбор «бот работает,
  // а человеку не пришло» упирался ровно в эти два вопроса.
  // Скачивание файлов — отдельный путь у Telegram и отдельная беда: команды
  // ходят и сообщения уходят, а фото-подтверждения не принимаются.
  files: { ok: boolean; detail: string };
  recipients: { total: number; linked: number; unlinked: string[]; unlinked_more: number };
  venues: { name: string; telegram_on: boolean; explicit: boolean }[];
};

type TelegramState = {
  has_token: boolean;
  own_token: boolean;
  shared_ready: boolean;
  recipients: Recipient[];
  reports_enabled: boolean;
  report_time: string;
  reminders_enabled: boolean;
  period_reports_enabled: boolean;
};
type Capability = { key: string; label: string; hint: string };
// Сосед по сети: из кого можно скопировать настройку. Цифры — чтобы оператор
// выбирал «тот, где всё заведено», а не наугад.
type SiblingRow = { id: string; name: string; slug: string; recipes: number; items: number; roles: number };
type TenantDetail = {
  siblings: SiblingRow[];
  tenant: { id: string; slug: string; name: string; logo: string | null; monthly_fee: string | null; ai_daily_limit?: number | null; ai_daily_limit_effective?: number; ai_daily_tokens?: number | null; ai_daily_tokens_effective?: number; ai_used_today?: number; ai_tokens_today?: number; ai_rub_per_ktoken?: number | null; created_at: string; group: { id: string; slug: string; name: string; domain_enabled?: boolean } | null; demo_stand: boolean; venues: VenueLite[]; owners: OwnerLite[] };
  feature_flags: Record<string, boolean>;
  modules: Module[];
  telegram: TelegramState;
  // Роли заведения со своими наборами прав. Раньше здесь была плоская сетка
  // поверх пресетов в коде; теперь право лежит прямо у роли, и список ролей
  // приходит с сервера — иначе свои роли клиента сюда бы не попали.
  roles: TenantRoleOut[];
  capabilities: Capability[];
};

type RoleTemplateOut = {
  key: string;
  name: string;
  hint: string;
  level: number;
  capabilities: string[];
  added: boolean;
};

type TenantRoleOut = {
  id: string;
  key: string;
  name: string;
  level: number;
  is_system: boolean;
  capabilities: string[];
};
type Category = { name: string; mode: "formula" | "count" };
type CatalogItem = {
  id: string;
  code: string | null;
  name: string;
  unit: string | null;
  category: string | null;
  // Профиль бутылки — для формулы замера открытых (объём × плотность + вес пустой).
  volume_ml: number | null;
  empty_g: number | null;
  full_g: number | null;
  density: number;
  density_source: "measured" | "manual" | "reference" | null;
  is_active: boolean;
  // Зона и штрихкод (20.09): opListCatalog их пока не выбирает из БД (в select
  // ниже этих колонок нет), поэтому в ответе списка они всегда приходят
  // undefined — тип optional, чтобы не выдавать это за факт. Форма правки
  // (CatalogEditor) поэтому шлёт их на сервер только если оператор реально
  // тронул поле — иначе правка одного имени молча откатывала бы зону на «бар»
  // или стирала бы штрихкод, которых список не показал.
  zone?: string | null;
  barcode?: string | null;
};
type VenueSummary = {
  catalog_count: number;
  recipe_count: number;
  categories: Category[];
  venue_name: string;
  blank: { org: string; warehouse: string };
  /** Касса iiko подключена к точке — каталог и меню приходят оттуда. */
  iiko_connected?: boolean;
};

const fmtDate = (s: string) => new Date(s).toLocaleDateString("ru-RU");
const fmtMoney = (n: number) => n.toLocaleString("ru-RU");

// Файл → чистый base64 (без data-URL префикса) для отправки в /api.
function readFileB64(file: File): Promise<string> {
  return new Promise((resolve, reject) => {
    const r = new FileReader();
    r.onload = () => {
      const res = String(r.result);
      resolve(res.includes(",") ? res.slice(res.indexOf(",") + 1) : res);
    };
    r.onerror = () => reject(new Error("Не удалось прочитать файл"));
    r.readAsDataURL(file);
  });
}

// ── дизайн-токены ──
//
// 08.08 консоль переведена на ОБЩИЕ примитивы десктопа (`desk/ui.tsx`). Раньше
// здесь жил свой набор поколением старше: плоские кнопки на `--op-ghost`,
// поля с обычной рамкой, радиус 20/12. Токены `--op-*` у консоли и десктопа и
// так были одни, расходилась только сборка из них — и расходилась молча:
// правку системы («серых кнопок нет», «поле — выемка») делали на десктопе, а
// консоль оставалась в прежнем виде и с каждой правкой отставала сильнее.
//
// Теперь источник один. Правка в `ui.tsx` доезжает в обе поверхности сразу.
//
// ⚠️ Псевдонимы (uiCard → card) оставлены НАМЕРЕННО: имена `card`, `field`,
// `lbl`, `bPrimary` стоят в двух с половиной тысячах строк разметки ниже, и
// переименовывать их значило бы получить диф, в котором содержательную правку
// не разглядеть.
const card = uiCard;
const field = uiField;
const lbl = uiLbl;
// Главное созидающее действие экрана — оранжевая с градиентом и свечением.
// Раньше `bPrimary` был тёмным: в консоли тёмная кнопка означала «сохранить»,
// на десктопе — «подтвердить в окне», и одно и то же слово значило разное.
const bPrimary = uiAccent;
// Вторичное — белая приподнятая. Серых кнопок в системе нет (Вариант A).
const bGhost = uiSurface;
const bDanger = uiDanger;
// Разрушение с подтверждением («УДАЛИТЬ» словом) — красная заливка. Своя, не
// из ui.tsx: там опасное действие мягкое, а здесь оно необратимо для клиента
// целиком, и кнопка обязана выглядеть тяжелее соседних.
const bDangerSolid =
  "inline-flex items-center justify-center gap-1.5 rounded-full h-10 px-5 text-[15px] font-semibold op-raise bg-[var(--op-danger)] text-white transition-[transform,background-color,color,box-shadow] duration-150 [transition-timing-function:var(--op-ease-out)] active:scale-[0.97] disabled:opacity-40 disabled:cursor-not-allowed focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[var(--op-ring)] hover:opacity-90";

// ── мелкие примитивы ──
// LogoMark (спарк-звёздочка прежнего имени) удалён 08.08 вместе с надписью
// «Mise»: знак продукта теперь один на все поверхности — `KaitMark` из
// `desk/Brand.tsx`.

// Галочка и крест — СВОИ SVG, а не «✓» и «✗» текстом (§4 консоли: значков
// глифами не рисуем). Глиф зависит от шрифта, не красится currentColor как
// линия и не масштабируется вместе с остальными значками — на разных машинах
// журнал выглядел по-разному.
function IcCheck({ className = "h-4 w-4" }: { className?: string }) {
  return (
    <svg className={className} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.4" strokeLinecap="round" strokeLinejoin="round" aria-hidden>
      <path d="M5 12.5l4.5 4.5L19 7.5" />
    </svg>
  );
}

function IcCross({ className = "h-4 w-4" }: { className?: string }) {
  return (
    <svg className={className} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.4" strokeLinecap="round" aria-hidden>
      <path d="M6.5 6.5l11 11M17.5 6.5l-11 11" />
    </svg>
  );
}

function IcUpload({ className = "h-4 w-4" }: { className?: string }) {
  return (
    <svg className={className} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden>
      <path d="M12 16V4m0 0L7 9m5-5l5 5M4 18v1.5A1.5 1.5 0 005.5 21h13a1.5 1.5 0 001.5-1.5V18" />
    </svg>
  );
}

// Аватар заведения: логотип, если загружен, иначе инициалы названия.
// Картинка приходит data-URL'ом (см. opSetTenantLogo), поэтому обычный <img>
// без next/image — оптимизировать нечего, файл уже в разметке.
function Avatar({
  name,
  logo,
  className = "h-11 w-11 text-sm",
}: { name: string; logo?: string | null; className?: string }) {
  const initials =
    name.split(/\s+/).filter(Boolean).slice(0, 2).map((w) => w[0]).join("").toUpperCase() || "?";
  if (logo) {
    return (
      // eslint-disable-next-line @next/next/no-img-element
      <img
        src={logo}
        alt={name}
        className={`shrink-0 rounded-2xl object-cover ${className}`}
      />
    );
  }
  return (
    <span className={`grid shrink-0 place-items-center rounded-2xl bg-[var(--op-accent-soft)] font-bold text-[var(--op-accent)] ${className}`}>
      {initials}
    </span>
  );
}

function Pill({ children, tone = "muted" }: { children: ReactNode; tone?: "muted" | "lime" | "amber" }) {
  const t =
    tone === "lime" ? "bg-[var(--op-accent-soft)] text-[var(--op-accent)]" : tone === "amber" ? "bg-[var(--op-amber-soft)] text-[var(--op-amber)]" : "bg-[var(--op-ghost)] text-[var(--op-faint)]";
  return <span className={`inline-flex items-center rounded-lg px-2.5 py-1 text-[11px] font-medium ${t}`}>{children}</span>;
}

// Местный Toggle удалён (правка Марата 13.08): переключатель теперь ОДИН на
// весь продукт — `Switch` из ../_desk/ui. Две копии одного тумблера разъедутся.

function Saved({ show }: { show: boolean }) {
  return show ? (
    <span className="inline-flex items-center gap-1 text-xs font-medium text-[var(--op-accent)]">
      <IcCheck className="h-3.5 w-3.5" /> Сохранено
    </span>
  ) : null;
}

// Переключатель темы переехал в общий `ThemeToggle` (08.08): он теперь ОДИН
// на консоль и на десктоп, референс Марата — пилюля с ездящей шайбой.
// Свой, местный, удалён — две копии одного тумблера гарантированно разъедутся.

function CardHead({ title, right }: { title: string; right?: ReactNode }) {
  return (
    <div className="flex items-center justify-between">
      <h3 className="text-sm font-semibold text-[var(--op-fg)]">{title}</h3>
      {right}
    </div>
  );
}

// ── диаграммы (лёгкий SVG, без внешних либ) ──
const PIE = ["var(--op-cat-1)", "var(--op-cat-2)", "var(--op-cat-3)", "var(--op-cat-4)", "var(--op-cat-5)", "var(--op-cat-6)"];

type Seg = { value: number; color: string; label: string };

// Пончик + легенда одним блоком: общий hover (наведение на сегмент ИЛИ строку
// легенды подсвечивает пару, центр показывает долю %). На входе дуги «прорисовываются»
// (transition stroke-dasharray со стаггером). Данные продублированы текстом в легенде —
// hover остаётся декоративным (ui-ux-pro-max §10 color-not-only / data-in-text).
function DonutChart({ segments, centerTop, centerMain }: { segments: Seg[]; centerTop?: string; centerMain: string }) {
  const [active, setActive] = useState<number | null>(null);
  const [drawn, setDrawn] = useState(false);
  useEffect(() => {
    const id = requestAnimationFrame(() => setDrawn(true));
    return () => cancelAnimationFrame(id);
  }, []);

  const total = segments.reduce((s, x) => s + x.value, 0) || 1;
  const R = 40, sw = 13, C = 2 * Math.PI * R;

  let acc = 0;
  const arcs = segments
    .map((seg, idx) => ({ seg, idx }))
    .filter((a) => a.seg.value > 0)
    .map((a, order) => {
      const len = (a.seg.value / total) * C;
      const gap = order === 0 && segments.filter((s) => s.value > 0).length <= 1 ? 0 : 2.5;
      const visible = Math.max(0.1, len - gap);
      const arc = { idx: a.idx, color: a.seg.color, offset: -acc, visible, order };
      acc += len;
      return arc;
    });

  const act = active != null ? segments[active] : null;
  const actPct = act ? Math.round((act.value / total) * 100) : 0;

  return (
    <div className="flex items-center gap-5">
      <div className="relative mx-auto h-[160px] w-[160px] shrink-0">
        <svg viewBox="0 0 100 100" className="h-full w-full -rotate-90">
          <circle cx="50" cy="50" r={R} fill="none" stroke="var(--op-line)" strokeWidth={sw} />
          {arcs.map((a) => (
            <circle
              key={a.idx}
              cx="50" cy="50" r={R} fill="none"
              stroke={a.color}
              strokeWidth={active === a.idx ? sw + 2 : sw}
              strokeDasharray={drawn ? `${a.visible} ${C - a.visible}` : `0 ${C}`}
              strokeDashoffset={a.offset}
              strokeLinecap="round"
              onMouseEnter={() => setActive(a.idx)}
              onMouseLeave={() => setActive(null)}
              style={{
                opacity: active != null && active !== a.idx ? 0.3 : 1,
                transition: `stroke-dasharray .7s cubic-bezier(.22,1,.36,1) ${a.order * 90}ms, opacity .2s ease, stroke-width .2s ease`,
              }}
            />
          ))}
        </svg>
        <div className="pointer-events-none absolute inset-0 grid place-content-center text-center">
          {act ? (
            <>
              <div className="max-w-[96px] truncate text-[10px] uppercase tracking-[0.12em] text-[var(--op-muted)]">{act.label}</div>
              <div className="text-xl font-bold leading-tight tabular-nums" style={{ color: act.color }}>{actPct}%</div>
              <div className="text-[11px] tabular-nums text-[var(--op-muted)]">{act.value.toLocaleString("ru-RU")}</div>
            </>
          ) : (
            <>
              {centerTop && <div className="text-[10px] uppercase tracking-[0.12em] text-[var(--op-faint)]">{centerTop}</div>}
              <div className="text-lg font-bold leading-tight tabular-nums">{centerMain}</div>
            </>
          )}
        </div>
      </div>
      <ul className="flex-1 space-y-0.5">
        {segments.map((it, i) => (
          <li
            key={i}
            onMouseEnter={() => setActive(i)}
            onMouseLeave={() => setActive(null)}
            className={`flex items-center gap-2 rounded-lg px-2 py-1 text-xs transition-colors ${active === i ? "bg-[var(--op-ghost)]" : ""} ${active != null && active !== i ? "opacity-40" : ""}`}
          >
            <span className="h-2.5 w-2.5 shrink-0 rounded-[3px]" style={{ background: it.color }} />
            <span className="flex-1 truncate text-[var(--op-muted)]">{it.label}</span>
            <span className="tabular-nums text-[var(--op-muted)]">{it.value.toLocaleString("ru-RU")}</span>
          </li>
        ))}
      </ul>
    </div>
  );
}

// Вертикальные столбцы (значение под каждым). Рост через transform:scaleY (не height —
// не трогает layout), стаггер по индексу. На пике — плавающий лайм-маркер значения.
function VBars({ items }: { items: { label: string; value: number }[] }) {
  const max = Math.max(1, ...items.map((i) => i.value));
  const peak = items.reduce((mi, it, i) => (it.value > items[mi].value ? i : mi), 0);
  return (
    <div>
      <div className="flex h-40 items-end gap-2.5 pt-4">
        {items.map((it, i) => (
          <div key={i} title={`${it.label}: ${it.value}`} className="relative flex h-full flex-1 items-end">
            {i === peak && it.value > 0 && (
              <span
                className="op-pop absolute left-1/2 top-0 z-10 -translate-x-1/2 -translate-y-1/2 whitespace-nowrap rounded-lg bg-[var(--op-accent)] px-2 py-0.5 text-[11px] font-bold tabular-nums text-[var(--op-accent-ink)] shadow-[0_4px_16px_-4px_var(--op-accent)]"
                style={{ animationDelay: ".55s", animationDuration: ".4s" }}
              >
                {it.value.toLocaleString("ru-RU")}
              </span>
            )}
            <div
              className="w-full rounded-t-lg bg-[var(--op-accent)]"
              style={{
                height: `${(it.value / max) * 100}%`,
                minHeight: "8px",
                transformOrigin: "bottom",
                animation: "op-grow-y .6s cubic-bezier(.22,1,.36,1) both",
                animationDelay: `${i * 60}ms`,
              }}
            />
          </div>
        ))}
      </div>
      <div className="mt-2 flex gap-2.5">
        {items.map((it, i) => (
          <div key={i} className="min-w-0 flex-1 text-center">
            <div className="text-sm font-semibold tabular-nums">{it.value}</div>
            <div className="truncate text-[10px] text-[var(--op-muted)]">{it.label}</div>
          </div>
        ))}
      </div>
    </div>
  );
}

// Горизонтальные полосы (доля).
function HBars({ items }: { items: { label: string; value: number; max: number }[] }) {
  return (
    <div className="space-y-3.5">
      {items.map((it, i) => (
        <div key={i}>
          <div className="mb-1.5 flex items-center justify-between text-xs">
            <span className="text-[var(--op-muted)]">{it.label}</span>
            <span className="tabular-nums text-[var(--op-muted)]">{it.value} из {it.max}</span>
          </div>
          <div className="h-2 overflow-hidden rounded-full bg-[var(--op-ghost)]">
            <div
              className="h-full rounded-full bg-[var(--op-accent)]"
              style={{
                width: `${it.max ? (it.value / it.max) * 100 : 0}%`,
                transformOrigin: "left",
                animation: "op-grow-x .6s cubic-bezier(.22,1,.36,1) both",
                animationDelay: `${i * 70}ms`,
              }}
            />
          </div>
        </div>
      ))}
    </div>
  );
}

function ChartCard({ title, children }: { title: string; children: ReactNode }) {
  return (
    <section className={`${card} space-y-4`}>
      <CardHead title={title} />
      {children}
    </section>
  );
}

// ── корень: шапка-навигация + маршрутизация видов ──
// Разделы консоли для `PillRow`. Вынесены из разметки константой: примитив
// меряет активную кнопку и пересчитывает подсветку при смене `items`, а массив,
// собранный в теле компонента, новый на каждый рендер.
type NavKey = "list" | "audit" | "admins" | "support" | "errors" | "spend";

// Разделы консоли — БОКОВОЙ РЕЛЬС, как в рабочем месте клиента (19.09,
// заказ пользователя «вдохновись оформлением из /desk»).
//
// Что было не так с верхней полосой пилюль: разделов пять, и все пять стояли
// в шапке вровень с логотипом и кнопкой «Выйти» — в одной строке три разные
// вещи (где я, кто я, уйти отсюда). Плюс контент был зажат в `max-w-6xl` по
// центру, и на широком мониторе список клиентов шёл узкой колонкой с двумя
// полями пустоты по бокам.
//
// Рельс решает и то и другое: разделы уехали в свой столбец и подписаны
// словами при наведении, а содержимое разложено на всю ширину, как в /desk.
const NAV_ITEMS: { key: NavKey; title: string; hint: string; icon: RailItem["icon"] }[] = [
  { key: "list", title: "Клиенты", hint: "Сети, рестораны и онбординг", icon: "network" },
  { key: "audit", title: "Журнал", hint: "Что делали операторы", icon: "journal" },
  // Расход на помощника по клиентам (Э7): платим за модель мы, и видеть это
  // должен тот, кто платит.
  { key: "spend", title: "Расход ИИ", hint: "Сколько стоит помощник по клиентам", icon: "analytics" },
  { key: "admins", title: "Администраторы", hint: "Ключи доступа в консоль", icon: "keys" },
  // Обращения из кнопки «Поддержка» (03.09).
  { key: "support", title: "Поддержка", hint: "Обращения от клиентов", icon: "reviews" },
  // Ошибки фронта у клиентов (07.09).
  { key: "errors", title: "Ошибки", hint: "Что упало у клиентов в браузере", icon: "alert" },
];

export default function OperatorPage() {
  const [key, setKey] = useState<string | null>(null);
  const [view, setView] = useState<"gate" | "list" | "onboard" | "detail" | "audit" | "admins" | "support" | "errors" | "spend">("gate");
  const [detailId, setDetailId] = useState<string | null>(null);
  // В какую сеть заводим ресторан. Задаётся кнопкой «+ Ресторан» на карточке
  // сети: оператор пришёл из конкретной сети, и выбирать её в форме второй раз
  // — лишний шаг, на котором ошибаются.
  const [onboardGroup, setOnboardGroup] = useState<string | null>(null);
  // Под каким администратором вошли (заказ Марата 08.08). Раньше в шапке
  // стояло безликое «оператор» — при персональных ключах это ничего не
  // говорило: в журнале имя есть, а на экране нет, и понять, своим ключом
  // сидишь или чужим, было нельзя.
  const [who, setWho] = useState<{ name: string; kind: string } | null>(null);

  useEffect(() => {
    const saved = sessionStorage.getItem(KEY_STORAGE);
    if (saved) {
      setKey(saved);
      setView("list");
    }
  }, []);

  // Имя тянем на КАЖДУЮ смену ключа, а не один раз при монтировании: после
  // «Выйти» и входа другим ключом в шапке иначе осталось бы прежнее имя.
  useEffect(() => {
    if (!key) {
      setWho(null);
      return;
    }
    let alive = true;
    opApi<{ name: string; kind: string }>(key, "opWhoAmI").then((r) => {
      if (alive && r.ok) setWho(r.data);
    });
    return () => {
      alive = false;
    };
  }, [key]);

  const onAuthed = useCallback((k: string) => {
    sessionStorage.setItem(KEY_STORAGE, k);
    setKey(k);
    setView("list");
  }, []);
  const logout = useCallback(() => {
    sessionStorage.removeItem(KEY_STORAGE);
    setKey(null);
    setView("gate");
  }, []);
  const openDetail = useCallback((id: string) => {
    setDetailId(id);
    setView("detail");
  }, []);
  const onboard = useCallback((groupId: string | null) => {
    setOnboardGroup(groupId);
    setView("onboard");
  }, []);

  const navActive = view === "list" || view === "detail" || view === "onboard";
  const navKey: NavKey = navActive ? "list" : (view as NavKey);

  // До входа рельса нет: показывать разделы, в которые нельзя зайти, незачем.
  if (!key || view === "gate") {
    return (
      <main className="mx-auto max-w-6xl px-5 pb-20 pt-8">
        <div className="mb-6 flex justify-end">
          <ThemeToggle />
        </div>
        <Gate onAuthed={onAuthed} />
      </main>
    );
  }

  return (
    <div className="flex min-h-dvh">
      <Rail items={NAV_ITEMS} current={navKey} onSelect={(k) => setView(k as NavKey)} />

      <div className="flex min-w-0 flex-1 flex-col">
        {/* Шапка — ПЕРВАЯ СТРОКА страницы на фоне, а не полоса поперёк экрана
            (тот же приём, что в /desk): слева ничего, справа — кто вошёл, тема
            и выход. Заголовок раздела рисует сам раздел, поэтому здесь его нет
            и дублирования не возникает. */}
        <header className="flex w-full items-center gap-2 pb-2 pl-8 pr-10 pt-4">
          <span className="hidden text-sm text-[var(--op-muted)] sm:inline">консоль оператора</span>
          <div className="ml-auto flex items-center gap-2">
            <ThemeToggle />
            {/* ИМЯ администратора вместо слова «оператор» (Марат 08.08). Пока
                имя едет с сервера, бейджа нет вовсе: показать «оператор» и
                через мгновение подменить его именем — это мигание на самом
                видном месте шапки. Корневой ключ приходит с именем «Корневой
                ключ» — так и пишем, выдавать аварийный вход за человека нельзя. */}
            {who && (
              <span
                className="hidden h-8 max-w-[220px] items-center gap-1.5 truncate rounded-full bg-[var(--op-accent-soft)] px-3.5 text-[13px] font-semibold text-[var(--op-accent)] sm:inline-flex"
                title={who.kind === "root" ? "Вход корневым ключом из настроек сервера" : undefined}
              >
                <span className="h-1.5 w-1.5 shrink-0 rounded-full bg-[var(--op-accent)]" />
                <span className="truncate">{who.name}</span>
              </span>
            )}
            {/* «Выйти» — третичное действие (`op-quiet`): оно не создающее и не
                разрушающее, и заливка ему не положена. */}
            <button
              onClick={logout}
              className="op-quiet inline-flex h-8 items-center rounded-full px-3.5 text-[13px] font-semibold text-[var(--op-muted)] transition-colors hover:bg-[var(--op-ghost)] hover:text-[var(--op-fg)]"
            >
              Выйти
            </button>
          </div>
        </header>

        <main className="w-full flex-1 pb-20 pl-8 pr-10 pt-2">
          {view === "list" && <ClientList apiKey={key} onOpen={openDetail} onOnboard={onboard} onUnauthorized={logout} />}
          {view === "audit" && <AuditView apiKey={key} />}
            {view === "spend" && <SpendView apiKey={key} />}
          {view === "admins" && <AdminsView apiKey={key} />}
          {view === "support" && <SupportView apiKey={key} />}
          {view === "errors" && <ErrorsView apiKey={key} />}
          {view === "onboard" && <Onboard apiKey={key} groupId={onboardGroup} onBack={() => setView("list")} />}
          {view === "detail" && detailId && <Detail apiKey={key} tenantId={detailId} onBack={() => setView("list")} onOpen={openDetail} />}
        </main>
      </div>
    </div>
  );
}

// ── экран ввода ключа ──
// ── иконки логина (SVG, не эмодзи — §4) ──
function IcLock({ s = 15 }: { s?: number }) {
  return (
    <svg width={s} height={s} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" aria-hidden>
      <rect x="4" y="11" width="16" height="10" rx="2.2" /><path d="M8 11V8a4 4 0 0 1 8 0v3" />
    </svg>
  );
}
function IcEye({ off = false }: { off?: boolean }) {
  return (
    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" aria-hidden>
      {off ? (
        <><path d="M10.6 5.1A9.7 9.7 0 0 1 12 5c5.5 0 9 5.2 9 7-.3 1-1.2 2.4-2.6 3.6M6.6 6.6C4.2 8 3 10.2 3 12c0 1.8 3.5 7 9 7 1.4 0 2.6-.3 3.7-.8M3 3l18 18M9.9 9.9a3 3 0 0 0 4.2 4.2" /></>
      ) : (
        <><path d="M2 12s3.5-7 10-7 10 7 10 7-3.5 7-10 7-10-7-10-7z" /><circle cx="12" cy="12" r="3" /></>
      )}
    </svg>
  );
}

function Gate({ onAuthed }: { onAuthed: (k: string) => void }) {
  const [val, setVal] = useState("");
  const [err, setErr] = useState("");
  const [busy, setBusy] = useState(false);
  const [show, setShow] = useState(false);

  async function submit() {
    const k = val.trim();
    if (!k) return;
    setBusy(true);
    setErr("");
    const r = await opApi(k, "opListTenants");
    setBusy(false);
    if (r.ok) onAuthed(k);
    else setErr("Неверный ключ доступа");
  }

  return (
    <div className="mx-auto flex min-h-[74vh] max-w-[400px] flex-col justify-center">
      {/* Бейдж со знаком KLAIT и замком «защищённая зона». Знак на тёмном
          бейдже БЕЛЫЙ: синий на графите даёт ~3:1 и превращается в пятно. */}
      <div className="op-rise mb-8 flex flex-col items-center text-center">
        <span className="relative grid h-16 w-16 place-items-center rounded-[20px] bg-[var(--op-primary)] text-white [box-shadow:var(--op-shadow-lg)]">
          <KaitMark className="h-[22px] w-auto" />
          <span className="absolute -bottom-1.5 -right-1.5 grid h-6 w-6 place-items-center rounded-full bg-[var(--op-lime)] text-[var(--op-lime-ink)] ring-4 ring-[var(--op-bg)]">
            <IcLock s={11} />
          </span>
        </span>
        <h1 className="mt-5 text-[27px] font-bold leading-tight tracking-tight text-[var(--op-fg)]">Консоль оператора</h1>
        <p className="mt-2 text-[13.5px] leading-relaxed text-[var(--op-muted)]">Платформенное управление заведениями-клиентами KLAIT.</p>
      </div>

      {/* Карточка входа */}
      <div className={`op-rise ${card} space-y-4`} style={{ animationDelay: "70ms" }}>
        <label className="block space-y-2">
          <span className={lbl}>Ключ оператора</span>
          <div className="relative">
            <span className="pointer-events-none absolute left-3.5 top-1/2 -translate-y-1/2 text-[var(--op-faint)]">
              <IcLock />
            </span>
            <input
              type={show ? "text" : "password"}
              className={`${field} pl-10 pr-11 font-mono`}
              value={val}
              placeholder="ADMIN_API_KEY"
              onChange={(e) => { setVal(e.target.value); if (err) setErr(""); }}
              onKeyDown={(e) => e.key === "Enter" && submit()}
              autoFocus
            />
            <button
              type="button"
              onClick={() => setShow((s) => !s)}
              aria-label={show ? "Скрыть ключ" : "Показать ключ"}
              className="absolute right-2 top-1/2 grid h-7 w-7 -translate-y-1/2 place-items-center rounded-lg text-[var(--op-faint)] outline-none transition-colors hover:text-[var(--op-fg)] focus-visible:ring-2 focus-visible:ring-[var(--op-ring)]"
            >
              <IcEye off={show} />
            </button>
          </div>
        </label>
        {err && (
          <p className="flex items-center gap-2 rounded-lg bg-[var(--op-danger-soft)] px-3 py-2 text-sm text-[var(--op-danger-text)]">
            <span className="grid h-4 w-4 shrink-0 place-items-center rounded-full bg-[var(--op-danger)] text-[10px] font-bold text-white">!</span>
            {err}
          </p>
        )}
        <button disabled={busy || !val.trim()} onClick={submit} className={`${bPrimary} w-full`}>
          {busy ? (
            <span className="inline-flex items-center gap-2">
              <span className="h-4 w-4 animate-spin rounded-full border-2 border-current border-t-transparent" />
              Проверяю…
            </span>
          ) : (
            "Войти в консоль"
          )}
        </button>
      </div>

      {/* Подпись «Защищённая зона · доступ по платформенному ключу» убрана
          (Марат 08.08): она пересказывала экран словами из документации —
          человек и так стоит перед полем для ключа. */}
    </div>
  );
}

// ── список клиентов ──
//
// Редизайн 10.09. Что было не так и что с этим сделано:
//
//  · клиента ДОЛГО ИСКАТЬ — карточки шли сплошной сеткой по дате заведения,
//    поиска не было вовсе. Теперь строка поиска сверху: имя, адрес, владелец.
//  · МАЛО ВИДНО НА ЭКРАНЕ — четыре крупные плитки и два графика занимали
//    первый экран целиком, до списка надо было листать. Цифры ужаты в одну
//    строку, графики убраны под «Сводку» (свёрнута по умолчанию), карточки
//    стали плотнее и идут по три в ряд на широком экране.
//  · папки ресторанов (Э1) — переключатель «Все / По папкам», перетаскивание
//    карточек и обязательная альтернатива ему в меню карточки.

// Общее начало двух названий — предложение имени папки при её создании
// (п. 2 плана). «Пински Бар» + «Пински Хайд» → «Пински».
//
// Режем по границе СЛОВА, а не по символам: побуквенный префикс дал бы
// «Пински Ба» — имя, которое оператор всё равно допишет руками. Ничего общего
// нет — возвращаем пустую строку, и диалог просто откроется с пустым полем.
function commonNamePrefix(a: string, b: string): string {
  const wa = a.trim().split(/\s+/);
  const wb = b.trim().split(/\s+/);
  const out: string[] = [];
  for (let i = 0; i < Math.min(wa.length, wb.length); i++) {
    if (wa[i].toLowerCase() !== wb[i].toLowerCase()) break;
    out.push(wa[i]);
  }
  return out.join(" ");
}

// Название → предложение слага. Кириллица в имени хоста не живёт, поэтому
// транслитерируем; всё, что не легло в [a-z0-9-], выкидываем.
const TRANSLIT: Record<string, string> = {
  а: "a", б: "b", в: "v", г: "g", д: "d", е: "e", ё: "e", ж: "zh", з: "z",
  и: "i", й: "y", к: "k", л: "l", м: "m", н: "n", о: "o", п: "p", р: "r",
  с: "s", т: "t", у: "u", ф: "f", х: "h", ц: "c", ч: "ch", ш: "sh", щ: "sch",
  ъ: "", ы: "y", ь: "", э: "e", ю: "yu", я: "ya",
};
function slugSuggest(name: string): string {
  const t = name
    .toLowerCase()
    .split("")
    .map((ch) => TRANSLIT[ch] ?? ch)
    .join("")
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/^-+|-+$/g, "");
  return t.slice(0, 63);
}

// Подходит ли клиент под строку поиска. Ищем по названию, адресу и владельцам:
// оператору звонят и называют любое из трёх.
function matches(t: TenantRow, q: string): boolean {
  if (!q) return true;
  const n = q.trim().toLowerCase();
  if (!n) return true;
  return (
    t.name.toLowerCase().includes(n) ||
    t.slug.toLowerCase().includes(n) ||
    t.owners.some((o) => o.name.toLowerCase().includes(n))
  );
}

/** Сеть подходит под поиск по своему имени или адресу («пински»). */
function matchesGroup(g: GroupRow, q: string): boolean {
  const n = q.trim().toLowerCase();
  if (!n) return true;
  return g.name.toLowerCase().includes(n) || g.slug.toLowerCase().includes(n);
}

function IcSearch({ className = "h-4 w-4" }: { className?: string }) {
  return (
    <svg className={className} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" aria-hidden>
      <circle cx="11" cy="11" r="7" /><path d="m20 20-3.2-3.2" />
    </svg>
  );
}

function IcFolder({ className = "h-4 w-4" }: { className?: string }) {
  return (
    <svg className={className} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" aria-hidden>
      <path d="M3 7.5A1.5 1.5 0 0 1 4.5 6h4l2 2.5h7A1.5 1.5 0 0 1 19 10v7a1.5 1.5 0 0 1-1.5 1.5h-13A1.5 1.5 0 0 1 3 17z" />
    </svg>
  );
}

function IcChevron({ open }: { open: boolean }) {
  return (
    <svg
      className={`h-4 w-4 transition-transform duration-200 ${open ? "rotate-90" : ""}`}
      viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden
    >
      <path d="m9 6 6 6-6 6" />
    </svg>
  );
}

function IcDots({ className = "h-4 w-4" }: { className?: string }) {
  return (
    <svg className={className} viewBox="0 0 24 24" fill="currentColor" aria-hidden>
      <circle cx="12" cy="5" r="1.7" /><circle cx="12" cy="12" r="1.7" /><circle cx="12" cy="19" r="1.7" />
    </svg>
  );
}

// Компактная строка цифр вместо четырёх плиток в пол-экрана. Плитки остались
// в «Сводке» — там они уместны, а над списком нужен только счёт.
function StatStrip({ items }: { items: { label: string; value: number | string }[] }) {
  return (
    <div className="mb-4 flex flex-wrap items-center gap-x-5 gap-y-2 rounded-2xl border border-[var(--op-line)] bg-[var(--op-surface)] px-4 py-2.5">
      {items.map((it, i) => (
        <Fragment key={it.label}>
          {i > 0 && <span className="h-4 w-px bg-[var(--op-line)]" aria-hidden />}
          <span className="flex items-baseline gap-1.5">
            <span className="text-[17px] font-bold tabular-nums leading-none">{it.value}</span>
            <span className="text-xs text-[var(--op-muted)]">{it.label}</span>
          </span>
        </Fragment>
      ))}
    </div>
  );
}

// Карточка клиента. Плотнее прежней: логотип меньше, модули свёрнуты в счёт
// «6 из 9» вместо девяти пилюль подряд — они занимали половину карточки и при
// этом ничего не говорили, пока не прочитаешь каждую.
function TenantCard({
  t,
  modules,
  onOpen,
  onRestore,
  onMenu,
  draggable,
  dimmed,
  highlight,
}: {
  t: TenantRow;
  modules: Module[];
  onOpen: (id: string) => void;
  onRestore: (id: string) => void;
  onMenu: (id: string, el: HTMLElement) => void;
  draggable: boolean;
  dimmed: boolean;
  highlight: "none" | "into" | "pair";
}) {
  const on = modules.filter((m) => t.feature_flags[m.key]).length;
  const ring =
    highlight === "into"
      ? "border-[var(--op-accent)] bg-[var(--op-accent-soft)]"
      : highlight === "pair"
        ? "border-[var(--op-accent)] border-dashed"
        : "border-[var(--op-line)] bg-[var(--op-surface)]";
  return (
    <article
      onClick={() => !t.archived && onOpen(t.id)}
      className={`group relative rounded-2xl border p-3 transition-[transform,border-color,background-color,opacity] duration-200 ${ring} ${
        t.archived ? "opacity-60" : "cursor-pointer hover:-translate-y-0.5 hover:border-[var(--op-line-strong)] hover:bg-[var(--op-elevated)]"
      } ${dimmed ? "opacity-40" : ""} ${draggable ? "touch-none" : ""}`}
    >
      <div className="flex items-center gap-2.5">
        <Avatar name={t.name} logo={t.logo} className="h-9 w-9 text-xs" />
        <div className="min-w-0 flex-1">
          <div className="flex items-center gap-1.5">
            <h3 className="truncate text-[15px] font-semibold leading-tight">{t.name}</h3>
            {t.archived && <Pill tone="amber">архив</Pill>}
          </div>
          <div className="truncate font-mono text-[11px] text-[var(--op-faint)]">{t.slug}</div>
        </div>
        {!t.archived && (
          <button
            // Альтернатива перетаскиванию — обязательна (п. 2 плана): drag с
            // клавиатуры и на планшете неудобен, а консоль открывают и с
            // ноутбука в дороге.
            onClick={(e) => { e.stopPropagation(); onMenu(t.id, e.currentTarget); }}
            aria-label={`Действия: ${t.name}`}
            className="op-quiet grid h-7 w-7 shrink-0 place-items-center rounded-lg text-[var(--op-dim)] transition-colors hover:bg-[var(--op-ghost)] hover:text-[var(--op-fg)]"
          >
            <IcDots />
          </button>
        )}
      </div>
      <div className="mt-2 flex flex-wrap items-center gap-x-2 gap-y-0.5 text-[11px] text-[var(--op-muted)]">
        <span>{t.venues.length} точк(и)</span>
        <span className="text-[var(--op-dim)]">•</span>
        <span className="truncate">{t.owners.map((o) => o.name).join(", ") || "без владельца"}</span>
        {!t.archived && (
          <>
            <span className="text-[var(--op-dim)]">•</span>
            <span>{on} из {modules.length} модулей</span>
          </>
        )}
        <span className="text-[var(--op-dim)]">•</span>
        <span className="whitespace-nowrap">{fmtDate(t.created_at)}</span>
      </div>
      {t.archived && (
        <button onClick={(e) => { e.stopPropagation(); onRestore(t.id); }} className={`${bGhost} mt-2.5 h-8 px-3 text-[13px]`}>
          Восстановить
        </button>
      )}
    </article>
  );
}

// Обёртка карточки в перетаскивание. Отдельным компонентом, потому что
// `useDraggable`/`useDroppable` — хуки: внутри `tenants.map` их не вызвать.
// Карточка одновременно и груз, и цель: бросок карточки НА карточку создаёт
// папку из двух заведений.
function DraggableTenant(props: {
  t: TenantRow;
  modules: Module[];
  onOpen: (id: string) => void;
  onRestore: (id: string) => void;
  onMenu: (id: string, el: HTMLElement) => void;
  activeId: string | null;
  overId: string | null;
}) {
  const { t, activeId, overId } = props;
  const { attributes, listeners, setNodeRef: dragRef, isDragging } = useDraggable({
    id: `tenant:${t.id}`,
    disabled: t.archived,
  });
  const { setNodeRef: dropRef } = useDroppable({ id: `tenant:${t.id}`, disabled: t.archived });

  const isOver = overId === `tenant:${t.id}` && activeId !== `tenant:${t.id}`;
  return (
    <div
      ref={(el) => { dragRef(el); dropRef(el); }}
      {...listeners}
      {...attributes}
      className="op-rise"
    >
      <TenantCard
        {...props}
        draggable={!t.archived}
        dimmed={isDragging}
        highlight={isOver ? "pair" : "none"}
      />
    </div>
  );
}

// Папка ресторанов: раскрывающийся контейнер со сводными цифрами в шапке.
// Шапка — цель для броска: бросили карточку сюда, ресторан вошёл в сеть.
/**
 * Владелец сети (Э2-lite, 17.09): человек-владелец папки. Выбирается из
 * владельцев её ресторанов; после назначения новые рестораны папки можно
 * заводить «владелец как у папки», а его учётки в других ресторанах
 * связываются в одного человека по совпадению контактов.
 */
// Общий адрес сети (Э2, 18.09): тумблер на карточке папки. Включение
// переводит страницы ресторанов с их сабдоменов на `{папка}.klait.ru/{ресторан}`
// — ярлыки на телефонах придётся переставить, поэтому спрашиваем.
function GroupDomainLine({ apiKey, group }: { apiKey: string; group: GroupRow }) {
  const [on, setOn] = useState(group.domain_enabled);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");
  async function toggle() {
    const next = !on;
    if (
      next &&
      !window.confirm(
        `Включить общий адрес ${group.slug}.klait.ru? Рестораны сети будут открываться как ${group.slug}.klait.ru/<ресторан>/…, старые адреса переведут туда автоматически. Ярлыки на телефонах сотрудников нужно будет переставить.`,
      )
    )
      return;
    setBusy(true);
    setErr("");
    const r = await opApi<{ domain_enabled: boolean }>(apiKey, "opSetGroupDomain", { group_id: group.id, enabled: next });
    setBusy(false);
    if (!r.ok) return setErr(r.error);
    setOn(r.data.domain_enabled);
  }
  return (
    <div className="mb-3 flex flex-wrap items-center gap-2 text-xs">
      <span className="text-[var(--op-muted)]">Общий адрес сети:</span>
      <span className={on ? "font-medium text-[var(--op-fg)]" : "text-[var(--op-faint)]"}>
        {on ? `${group.slug}.klait.ru/<ресторан>` : "выключен — у каждого ресторана свой сабдомен"}
      </span>
      <button disabled={busy} onClick={() => void toggle()} className={`${bGhost} ml-auto py-1 text-xs`}>
        {busy ? "…" : on ? "Выключить" : "Включить"}
      </button>
      {err && <span className="text-[var(--op-danger-text)]">{err}</span>}
    </div>
  );
}

// Один бот на папку (Э4, правило 5 плана): токен задаётся здесь и наследуется
// ресторанами без своего — у человека с двумя ресторанами один чат.
function GroupTelegramLine({ apiKey, group }: { apiKey: string; group: GroupRow }) {
  const [has, setHas] = useState(group.has_bot);
  const [token, setToken] = useState("");
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");
  async function save(clear: boolean) {
    setBusy(true);
    setErr("");
    const r = await opApi<{ has_bot: boolean }>(apiKey, "opSetGroupTelegram", { group_id: group.id, bot_token: clear ? "" : token.trim() });
    setBusy(false);
    if (!r.ok) return setErr(r.error);
    setHas(r.data.has_bot);
    setToken("");
  }
  return (
    <div className="mb-3 flex flex-wrap items-center gap-2 text-xs">
      <span className="text-[var(--op-muted)]">Telegram-бот сети:</span>
      <span className={has ? "font-medium text-[var(--op-fg)]" : "text-[var(--op-faint)]"}>{has ? "свой токен задан — рестораны без своего наследуют" : "не задан — рестораны используют свой или общий"}</span>
      <input className={`${field} ml-auto w-[260px] py-1 text-xs`} placeholder="123456:ABC… (токен от @BotFather)" value={token} onChange={(e) => setToken(e.target.value)} />
      <button disabled={!token.trim() || busy} onClick={() => void save(false)} className={`${bGhost} py-1 text-xs`}>
        {busy ? "…" : "Сохранить"}
      </button>
      {has && (
        <button disabled={busy} onClick={() => void save(true)} className={`${bGhost} py-1 text-xs`}>
          Снять
        </button>
      )}
      {err && <span className="text-[var(--op-danger-text)]">{err}</span>}
    </div>
  );
}

// Связать две учётки разных ресторанов папки в одного человека (Э3): бармен,
// заведённый в каждом ресторане отдельно, после связи переключается между
// ними без входа, а бот и «уволить из сети» видят его как одного.
function GroupLinkAccountsLine({ apiKey, tenants }: { apiKey: string; tenants: { id: string; name: string }[] }) {
  const [open, setOpen] = useState(false);
  const [side, setSide] = useState<{ a: string; b: string; ua: string; ub: string }>({ a: "", b: "", ua: "", ub: "" });
  const [emps, setEmps] = useState<Record<string, { id: string; name: string; email: string | null; phone: string | null }[]>>({});
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState("");
  async function loadEmps(tenantId: string) {
    if (!tenantId || emps[tenantId]) return;
    const r = await opApi<{ employees: { id: string; name: string; email: string | null; phone: string | null }[] }>(apiKey, "opListEmployees", { tenant_id: tenantId });
    if (r.ok) setEmps((m) => ({ ...m, [tenantId]: r.data.employees }));
  }
  async function link() {
    setBusy(true);
    setMsg("");
    const r = await opApi<{ ok: true }>(apiKey, "opLinkAccounts", { user_id_a: side.ua, user_id_b: side.ub });
    setBusy(false);
    setMsg(r.ok ? "Связано: теперь это один человек в обоих ресторанах" : r.error);
  }
  if (tenants.length < 2) return null;
  const pickEmp = (tid: string, val: string, on: (v: string) => void) => (
    <select className={`${field} w-[200px] py-1 text-xs`} value={val} onChange={(e) => on(e.target.value)} disabled={!tid}>
      <option value="">{tid ? "Сотрудник…" : "Сначала ресторан"}</option>
      {(emps[tid] ?? []).map((e) => (
        <option key={e.id} value={e.id}>{e.name}{e.email ? ` (${e.email})` : e.phone ? ` (${e.phone})` : ""}</option>
      ))}
    </select>
  );
  const pickTenant = (val: string, on: (v: string) => void) => (
    <select className={`${field} w-[160px] py-1 text-xs`} value={val} onChange={(e) => { on(e.target.value); void loadEmps(e.target.value); }}>
      <option value="">Ресторан…</option>
      {tenants.map((t) => (
        <option key={t.id} value={t.id}>{t.name}</option>
      ))}
    </select>
  );
  return (
    <div className="mb-3 text-xs">
      <button className="text-[var(--op-muted)] underline" onClick={() => setOpen((v) => !v)}>
        {open ? "Скрыть связь учёток" : "Связать учётки одного человека в двух ресторанах…"}
      </button>
      {open && (
        <div className="mt-2 flex flex-wrap items-center gap-2">
          {pickTenant(side.a, (v) => setSide((s) => ({ ...s, a: v, ua: "" })))}
          {pickEmp(side.a, side.ua, (v) => setSide((s) => ({ ...s, ua: v })))}
          <span className="text-[var(--op-faint)]">=</span>
          {pickTenant(side.b, (v) => setSide((s) => ({ ...s, b: v, ub: "" })))}
          {pickEmp(side.b, side.ub, (v) => setSide((s) => ({ ...s, ub: v })))}
          <button disabled={!side.ua || !side.ub || side.a === side.b || busy} onClick={() => void link()} className={`${bGhost} py-1 text-xs`}>
            {busy ? "…" : "Связать"}
          </button>
          {msg && <span className={msg.startsWith("Связано") ? "text-[var(--op-fg)]" : "text-[var(--op-danger-text)]"}>{msg}</span>}
        </div>
      )}
    </div>
  );
}

// Роли и права на всю папку (Э5, п. 6 плана): правка здесь доезжает до
// всех ресторанов, кроме прав, снятых локально; «применить ко всем» стирает
// локальные правки; «снять с папки» оставляет роль ресторанам как свою.
type GroupRoleRow = { id: string; key: string; name: string; level: number; capabilities: string[]; zones: string[]; members: number; differs: { tenantId: string; name: string; removed: string[] }[] };
const GROUP_ZONES: [string, string][] = [["bar", "Бар"], ["hall", "Зал"], ["kitchen", "Кухня"], ["housekeeping", "Хозчасть"]];
function GroupRolesCard({ apiKey, groupId }: { apiKey: string; groupId: string }) {
  const [data, setData] = useState<{ roles: GroupRoleRow[]; capabilities: { key: string; label: string; hint: string }[]; restaurants: number } | null>(null);
  const [open, setOpen] = useState(false);
  const [draft, setDraft] = useState<{ id: string | null; name: string; level: string; caps: Set<string>; zones: Set<string> } | null>(null);
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState("");
  const load = useCallback(async () => {
    const r = await opApi<{ roles: GroupRoleRow[]; capabilities: { key: string; label: string; hint: string }[]; restaurants: number }>(apiKey, "opListGroupRoles", { group_id: groupId });
    if (r.ok) setData(r.data);
    else setMsg(r.error);
  }, [apiKey, groupId]);
  useEffect(() => {
    if (open) void load();
  }, [open, load]);
  async function save() {
    if (!draft) return;
    setBusy(true);
    setMsg("");
    const r = await opApi<{ spread: { created: number; updated: number } }>(apiKey, "opUpsertGroupRole", {
      group_id: groupId,
      role_id: draft.id,
      name: draft.name.trim(),
      level: Number(draft.level),
      capabilities: [...draft.caps],
      zones: [...draft.zones],
    });
    setBusy(false);
    if (!r.ok) return setMsg(r.error);
    setMsg(`Разложено: новых ${r.data.spread.created}, обновлено ${r.data.spread.updated}`);
    setDraft(null);
    await load();
  }
  async function run(action: "opApplyGroupRole" | "opUnlinkGroupRole", roleId: string, confirmText: string) {
    if (!window.confirm(confirmText)) return;
    setBusy(true);
    setMsg("");
    const r = await opApi<{ ok: true }>(apiKey, action, { group_id: groupId, role_id: roleId });
    setBusy(false);
    if (!r.ok) return setMsg(r.error);
    await load();
  }
  return (
    <div className="mb-3 text-xs">
      <button className="text-[var(--op-muted)] underline" onClick={() => setOpen((v) => !v)}>
        {open ? "Скрыть роли сети" : "Роли и права на всю сеть…"}
      </button>
      {open && (
        <div className="mt-2 space-y-2">
          {!data && !msg && <span className="text-[var(--op-faint)]">…</span>}
          {data && data.roles.length === 0 && !draft && <p className="text-[var(--op-faint)]">Ролей сети пока нет — добавьте первую: она появится во всех {data.restaurants} ресторанах сети.</p>}
          {data && data.roles.map((r) => (
            <div key={r.id} className="flex flex-wrap items-center gap-2 rounded-lg border border-[var(--op-line)] px-2 py-1.5">
              <span className="font-medium text-[var(--op-fg)]">{r.name}</span>
              <span className="text-[var(--op-faint)]">ур. {r.level} · прав {r.capabilities.length} · людей {r.members}</span>
              {r.differs.length > 0 && (
                <span className="text-[var(--op-muted)]" title={r.differs.map((d) => `${d.name}: снято ${d.removed.join(", ")}`).join("\n")}>
                  · изменена в: {r.differs.map((d) => d.name).join(", ")}
                </span>
              )}
              <span className="ml-auto flex gap-1">
                <button className={`${bGhost} py-0.5 text-[11px]`} disabled={busy} onClick={() => setDraft({ id: r.id, name: r.name, level: String(r.level), caps: new Set(r.capabilities), zones: new Set(r.zones) })}>Изменить</button>
                {r.differs.length > 0 && (
                  <button className={`${bGhost} py-0.5 text-[11px]`} disabled={busy} onClick={() => void run("opApplyGroupRole", r.id, `Применить «${r.name}» ко всем ресторанам? Локальные правки в ${r.differs.length} ресторан(ах) будут стёрты.`)}>Применить ко всем</button>
                )}
                <button className={`${bGhost} py-0.5 text-[11px]`} disabled={busy} onClick={() => void run("opUnlinkGroupRole", r.id, `Снять роль «${r.name}» с сети? У ресторанов она останется своей, правки сети до неё больше не дойдут.`)}>Снять с сети</button>
              </span>
            </div>
          ))}
          {!draft && data && (
            <button className={`${bGhost} py-1 text-xs`} disabled={busy} onClick={() => setDraft({ id: null, name: "", level: "30", caps: new Set(), zones: new Set() })}>+ Роль сети</button>
          )}
          {draft && data && (
            <div className="space-y-2 rounded-lg border border-[var(--op-line)] p-2">
              <div className="flex flex-wrap gap-2">
                <input className={`${field} w-[220px] py-1 text-xs`} placeholder="Название роли" value={draft.name} onChange={(e) => setDraft({ ...draft, name: e.target.value })} />
                <input className={`${field} w-[90px] py-1 text-xs`} placeholder="Уровень" value={draft.level} onChange={(e) => setDraft({ ...draft, level: e.target.value })} title="Меньше — старше; 10 управляющий, 30 сотрудник" />
                <span className="flex items-center gap-2">
                  {GROUP_ZONES.map(([k, v]) => (
                    <label key={k} className="flex items-center gap-1">
                      <input type="checkbox" checked={draft.zones.has(k)} onChange={(e) => { const z = new Set(draft.zones); if (e.target.checked) z.add(k); else z.delete(k); setDraft({ ...draft, zones: z }); }} />
                      {v}
                    </label>
                  ))}
                </span>
              </div>
              <div className="grid gap-1 sm:grid-cols-2 lg:grid-cols-3">
                {data.capabilities.map((c) => (
                  <label key={c.key} className="flex items-start gap-1.5" title={c.hint}>
                    <input type="checkbox" className="mt-0.5" checked={draft.caps.has(c.key)} onChange={(e) => { const s = new Set(draft.caps); if (e.target.checked) s.add(c.key); else s.delete(c.key); setDraft({ ...draft, caps: s }); }} />
                    <span>{c.label}</span>
                  </label>
                ))}
              </div>
              <div className="flex gap-2">
                <button className={`${bGhost} py-1 text-xs`} disabled={busy || !draft.name.trim()} onClick={() => void save()}>{busy ? "…" : draft.id ? "Сохранить и разложить" : "Создать во всех ресторанах"}</button>
                <button className={`${bGhost} py-1 text-xs`} disabled={busy} onClick={() => setDraft(null)}>Отмена</button>
              </div>
            </div>
          )}
          {msg && <p className={msg.startsWith("Разложено") ? "text-[var(--op-fg)]" : "text-[var(--op-danger-text)]"}>{msg}</p>}
        </div>
      )}
    </div>
  );
}

function GroupOwnerLine({ apiKey, groupId, tenantsCount }: { apiKey: string; groupId: string; tenantsCount: number }) {
  const [data, setData] = useState<{ owner_person_id: string | null; owners: { user_id: string; name: string; login: string | null; is_group_owner: boolean; tenant: { id: string; name: string; slug: string } }[] } | null>(null);
  const [pick, setPick] = useState("");
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");
  const load = useCallback(async () => {
    const r = await opApi<{ owner_person_id: string | null; owners: { user_id: string; name: string; login: string | null; is_group_owner: boolean; tenant: { id: string; name: string; slug: string } }[] }>(apiKey, "opListGroupOwners", { group_id: groupId });
    if (r.ok) setData(r.data);
    else setErr(r.error);
  }, [apiKey, groupId]);
  useEffect(() => {
    void load();
  }, [load]);
  async function assign() {
    if (!pick) return;
    setBusy(true);
    setErr("");
    const r = await opApi<{ linked: number }>(apiKey, "opSetGroupOwner", { group_id: groupId, user_id: pick });
    setBusy(false);
    if (!r.ok) return setErr(r.error);
    setPick("");
    await load();
  }
  if (!data) return err ? <p className="mb-2 text-xs text-[var(--op-danger-text)]">{err}</p> : null;
  const current = data.owners.filter((o) => o.is_group_owner);
  const others = data.owners.filter((o) => !o.is_group_owner);
  // Сколько ресторанов сети покрыто учётками владельца. Учётки связываются по
  // совпадению почты или телефона (opSetGroupOwner), и если в разных
  // ресторанах он заведён под разной почтой — связка молча не случится.
  const covered = current.length;
  const gap = tenantsCount > 0 && covered > 0 && covered < tenantsCount;

  return (
    <div className="mb-3 space-y-1.5 text-xs">
      <div className="flex flex-wrap items-center gap-2">
        <span className="text-[var(--op-muted)]">Владелец сети:</span>
        {current.length > 0 ? (
          <span className="font-medium text-[var(--op-fg)]">
            {current[0].name}
            <span className="ml-1 text-[var(--op-faint)]">
              · учётки в {current.map((c) => c.tenant.name).join(", ")}
              {tenantsCount > 0 ? ` (${covered} из ${tenantsCount})` : ""}
            </span>
          </span>
        ) : (
          <span className="text-[var(--op-faint)]">не назначен</span>
        )}

        {/* 🔴 Мёртвый список вместо объяснения (Никита, 21.09).
            Назначать можно ТОЛЬКО из владельцев ресторанов этой сети. Когда
            выбирать не из кого — а это обычный случай сети из одного
            ресторана, — раскрывался пустой «Сменить…», и это читалось как
            «не работает». Список показываем, когда есть что показать. */}
        {others.length > 0 ? (
          <>
            <select className={`${field} ml-auto w-auto py-1 text-xs`} value={pick} onChange={(e) => setPick(e.target.value)}>
              <option value="">{current.length > 0 ? "Сменить…" : "Назначить из владельцев ресторанов…"}</option>
              {others.map((o) => (
                <option key={o.user_id} value={o.user_id}>
                  {o.name} — {o.tenant.name}{o.login ? ` (${o.login})` : ""}
                </option>
              ))}
            </select>
            <button disabled={!pick || busy} onClick={() => void assign()} className={`${bGhost} py-1 text-xs`}>
              {busy ? "…" : "Назначить"}
            </button>
          </>
        ) : (
          <span className="ml-auto text-[var(--op-faint)]">
            {current.length > 0
              ? "менять не на кого — других владельцев ресторанов в сети нет"
              : "назначать некого: в ресторанах сети нет ни одного владельца"}
          </span>
        )}
      </div>

      {/* Что это вообще даёт. Без этой строки «владелец сети» — просто ярлык:
          на экране не было ни слова о том, что он получает. */}
      <p className="text-[var(--op-faint)]">
        Владелец сети видит сводку по всем ресторанам — выручку, ФОТ и дисциплину рядом. Остальным
        она закрыта, даже управляющему ресторана этой же сети. Назначить можно только того, кто уже
        владелец одного из ресторанов.
      </p>

      {gap && (
        <p className="text-[var(--op-warn,#9a6a00)]">
          Учётки владельца есть не во всех ресторанах сети ({covered} из {tenantsCount}). Учётки
          связываются по совпадению почты или телефона: если в остальных ресторанах он заведён под
          другими — свяжите вручную, иначе он войдёт не везде.
        </p>
      )}

      {err && <p className="text-[var(--op-danger-text)]">{err}</p>}
    </div>
  );
}

function GroupBlock({
  apiKey,
  g,
  tenants,
  modules,
  open,
  onToggle,
  onOpen,
  onRestore,
  onMenu,
  onRename,
  onAdd,
  onDelete,
  activeId,
  overId,
}: {
  apiKey: string;
  g: GroupRow;
  tenants: TenantRow[];
  modules: Module[];
  open: boolean;
  onToggle: (id: string) => void;
  onOpen: (id: string) => void;
  onRestore: (id: string) => void;
  onMenu: (id: string, el: HTMLElement) => void;
  onRename: (g: GroupRow) => void;
  /** Завести ещё один ресторан в эту сеть. */
  onAdd: (groupId: string) => void;
  onDelete: (g: GroupRow) => void;
  activeId: string | null;
  overId: string | null;
}) {
  const { setNodeRef } = useDroppable({ id: `group:${g.id}` });
  const isOver = overId === `group:${g.id}`;
  return (
    <section
      ref={setNodeRef}
      className={`rounded-2xl border transition-colors duration-200 ${
        isOver ? "border-[var(--op-accent)] bg-[var(--op-accent-soft)]" : "border-[var(--op-line)] bg-[var(--op-surface-2)]"
      }`}
    >
      <header className="flex items-center gap-2.5 p-3">
        <button
          onClick={() => onToggle(g.id)}
          aria-expanded={open}
          className="op-quiet flex min-w-0 flex-1 items-center gap-2.5 text-left"
        >
          <span className="text-[var(--op-dim)]"><IcChevron open={open} /></span>
          <Avatar name={g.name} logo={g.logo} className="h-9 w-9 text-xs" />
          <span className="min-w-0 flex-1">
            <span className="flex items-center gap-1.5">
              <span className="truncate text-[15px] font-semibold leading-tight">{g.name}</span>
              <span className="text-[var(--op-accent)]"><IcFolder className="h-3.5 w-3.5" /></span>
            </span>
            <span className="block truncate font-mono text-[11px] text-[var(--op-faint)]">{g.slug}</span>
          </span>
        </button>
        <div className="flex shrink-0 items-center gap-x-3 text-[13px] text-[var(--op-muted)]">
          <span className="hidden sm:inline">
            {g.tenants_count} {plural(g.tenants_count, "ресторан", "ресторана", "ресторанов")}
          </span>
          {/* «Точки» показываем ТОЛЬКО когда их больше, чем ресторанов: с 19.09
              у ресторана ровно одна точка, и подпись «3 ресторана · 3 точки»
              — шум. Больше — значит у кого-то остались старые локации, и это
              как раз повод их заметить и вынести. */}
          {g.venues_count > g.tenants_count && (
            <span className="hidden md:inline text-[var(--op-attn)]" title="У ресторанов остались старые точки — их можно вынести в отдельные рестораны">
              {g.venues_count} точек
            </span>
          )}
          {/* Сумма тарифов сети — то, ради чего сеть и открывают в консоли. */}
          <span className="hidden lg:inline">{fmtMoney(Number(g.monthly_fee_total))} ₽/мес</span>
          {/* «+ Ресторан» прямо на сети: оператор пришёл сюда из конкретной
              сети, и гонять его в общую форму, где сеть надо выбрать снова, —
              лишний шаг ровно там, где ошибаются. */}
          <button onClick={() => onAdd(g.id)} className={`${bSmall} shrink-0`}>+ Ресторан</button>
          <button onClick={() => onRename(g)} className="op-quiet rounded-lg px-2 py-1 hover:bg-[var(--op-ghost)] hover:text-[var(--op-fg)]">
            Переименовать
          </button>
          <button
            onClick={() => onDelete(g)}
            // Непустую сеть сервер удалить не даст (opDeleteGroup) — не
            // показываем кнопку, которая заведомо ответит отказом.
            disabled={g.tenants_count > 0}
            title={g.tenants_count > 0 ? "Сначала вынесите рестораны из сети" : undefined}
            className="op-quiet rounded-lg px-2 py-1 hover:bg-[var(--op-ghost)] hover:text-[var(--op-danger-text)] disabled:cursor-not-allowed disabled:opacity-40"
          >
            Удалить
          </button>
        </div>
      </header>
      {open && (
        <div className="border-t border-[var(--op-line)] p-3">
          <GroupOwnerLine apiKey={apiKey} groupId={g.id} tenantsCount={g.tenants_count} />
          <GroupDomainLine apiKey={apiKey} group={g} />
          <GroupTelegramLine apiKey={apiKey} group={g} />
          <GroupLinkAccountsLine apiKey={apiKey} tenants={tenants} />
          <GroupRolesCard apiKey={apiKey} groupId={g.id} />
          {tenants.length === 0 ? (
            <p className="py-3 text-center text-[13px] text-[var(--op-faint)]">
              В сети пока нет ресторанов — нажмите «+ Ресторан» или перетащите сюда карточку из «Без сети».
            </p>
          ) : (
            <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 xl:grid-cols-3">
              {tenants.map((t) => (
                <DraggableTenant
                  key={t.id}
                  t={t}
                  modules={modules}
                  onOpen={onOpen}
                  onRestore={onRestore}
                  onMenu={onMenu}
                  activeId={activeId}
                  overId={overId}
                />
              ))}
            </div>
          )}
        </div>
      )}
    </section>
  );
}

// Полоса «вынуть из папки». Появляется только пока несут карточку, лежащую в
// папке: в остальное время это была бы мёртвая зона посреди экрана.
function UngroupZone({ overId }: { overId: string | null }) {
  const { setNodeRef } = useDroppable({ id: "ungrouped" });
  const isOver = overId === "ungrouped";
  return (
    <div
      ref={setNodeRef}
      className={`mb-3 rounded-2xl border border-dashed px-4 py-3 text-center text-xs transition-colors duration-200 ${
        isOver
          ? "border-[var(--op-accent)] bg-[var(--op-accent-soft)] text-[var(--op-accent)]"
          : "border-[var(--op-line)] text-[var(--op-faint)]"
      }`}
    >
      Отпустите здесь, чтобы вынуть заведение из сети
    </div>
  );
}

// Меню карточки — та самая альтернатива перетаскиванию. Позиционируется по
// кнопке, из которой открыто (position: fixed): карточка лежит в прокручиваемой
// сетке, и абсолютное позиционирование внутри неё уехало бы вместе с ней.
function CardMenu({
  anchor,
  tenant,
  groups,
  onMove,
  onClose,
}: {
  anchor: DOMRect;
  tenant: TenantRow;
  groups: GroupRow[];
  onMove: (groupId: string | null) => void;
  onClose: () => void;
}) {
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => { if (e.key === "Escape") onClose(); };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [onClose]);

  return (
    <>
      {/* Подложка на весь экран: клик мимо закрывает меню. */}
      <div className="fixed inset-0 z-40" onClick={onClose} />
      <div
        role="menu"
        style={{
          // Прижимаем к правому краю кнопки и не даём вылезти за экран слева.
          top: Math.min(anchor.bottom + 6, window.innerHeight - 12),
          left: Math.max(12, anchor.right - 240),
          width: 240,
        }}
        className="fixed z-50 overflow-hidden rounded-2xl border border-[var(--op-line)] bg-[var(--op-surface)] py-1 shadow-[0_18px_40px_-16px_rgba(0,0,0,0.45)]"
      >
        <p className="px-3 py-1.5 text-[11px] uppercase tracking-[0.12em] text-[var(--op-faint)]">Переместить в сеть</p>
        {groups.length === 0 && (
          <p className="px-3 pb-2 text-xs text-[var(--op-muted)]">
            Папок пока нет. Перетащите одну карточку на другую — предложу создать.
          </p>
        )}
        {groups.map((g) => (
          <button
            key={g.id}
            role="menuitem"
            disabled={tenant.group_id === g.id}
            onClick={() => onMove(g.id)}
            className="flex w-full items-center gap-2 px-3 py-2 text-left text-[13px] transition-colors hover:bg-[var(--op-ghost)] disabled:opacity-40"
          >
            <IcFolder className="h-3.5 w-3.5 shrink-0 text-[var(--op-accent)]" />
            <span className="truncate">{g.name}</span>
            {tenant.group_id === g.id && <span className="ml-auto text-[11px] text-[var(--op-faint)]">здесь</span>}
          </button>
        ))}
        {tenant.group_id !== null && (
          <>
            <div className="my-1 h-px bg-[var(--op-line)]" />
            <button
              role="menuitem"
              onClick={() => onMove(null)}
              className="w-full px-3 py-2 text-left text-[13px] transition-colors hover:bg-[var(--op-ghost)]"
            >
              Вынуть из сети
            </button>
          </>
        )}
      </div>
    </>
  );
}

// Диалог создания папки (бросок карточки на карточку) и переименования.
// Слаг задаётся ОДИН РАЗ и потом не меняется — он же адрес сети, поэтому при
// переименовании поля слага нет вовсе (см. opRenameGroup).
function GroupDialog({
  mode,
  initialName,
  onSubmit,
  onClose,
  busy,
  err,
}: {
  mode: "create" | "rename";
  initialName: string;
  onSubmit: (name: string, slug: string) => void;
  onClose: () => void;
  busy: boolean;
  err: string;
}) {
  const [name, setName] = useState(initialName);
  // Слаг подставляется из имени, пока оператор его не тронул руками: обычно
  // подстановка верна, а когда нет — правка не должна пропадать при следующей
  // букве в названии.
  const [slug, setSlug] = useState(() => slugSuggest(initialName));
  const [slugTouched, setSlugTouched] = useState(false);

  return (
    <div className="fixed inset-0 z-50 grid place-items-center bg-black/40 p-4 backdrop-blur-sm" onClick={onClose}>
      <div className={`${card} w-full max-w-md`} onClick={(e) => e.stopPropagation()}>
        <CardHead
          title={mode === "create" ? "Создать сеть ресторанов" : "Переименовать сеть"}
          right={
            <button onClick={onClose} aria-label="Закрыть" className="op-quiet grid h-7 w-7 place-items-center rounded-lg text-[var(--op-dim)] hover:bg-[var(--op-ghost)]">
              <CloseIcon />
            </button>
          }
        />
        <p className="mt-2 text-xs text-[var(--op-muted)]">
          {mode === "create"
            ? "Сеть ресторанов объединяет рестораны одного ресторатора. Меню, склад и задачи у каждого остаются своими."
            : "Адрес сети не меняется: по нему у людей уже стоят ярлыки и разосланы ссылки."}
        </p>

        <label className={`${lbl} mt-4 block`}>Название</label>
        <input
          autoFocus
          value={name}
          onChange={(e) => {
            setName(e.target.value);
            if (!slugTouched) setSlug(slugSuggest(e.target.value));
          }}
          className={`${field} mt-1 w-full`}
          placeholder="Пински"
        />

        {mode === "create" && (
          <>
            <label className={`${lbl} mt-3 block`}>Адрес сети</label>
            <div className="mt-1 flex items-center gap-2">
              <input
                value={slug}
                onChange={(e) => { setSlug(e.target.value); setSlugTouched(true); }}
                className={`${field} w-full font-mono`}
                placeholder="pinski"
              />
              <span className="shrink-0 font-mono text-xs text-[var(--op-faint)]">.klait.ru</span>
            </div>
            <p className="mt-1.5 text-[11px] text-[var(--op-faint)]">
              Задаётся один раз и потом не меняется: с него будут работать ярлыки на телефонах сотрудников.
            </p>
          </>
        )}

        {err && <p className="mt-3 text-sm text-[var(--op-danger-text)]">{err}</p>}

        <div className="mt-5 flex justify-end gap-2">
          <button onClick={onClose} className={bGhost}>Отмена</button>
          <button
            onClick={() => onSubmit(name.trim(), slug.trim())}
            disabled={busy || name.trim() === "" || (mode === "create" && slug.trim() === "")}
            className={bPrimary}
          >
            {mode === "create" ? "Создать сеть" : "Сохранить"}
          </button>
        </div>
      </div>
    </div>
  );
}

function ClientList({
  apiKey,
  onOpen,
  onOnboard,
  onUnauthorized,
}: {
  apiKey: string;
  onOpen: (id: string) => void;
  /** Завести ресторан: в конкретную сеть (id) или новую (null). */
  onOnboard: (groupId: string | null) => void;
  onUnauthorized: () => void;
}) {
  const [tenants, setTenants] = useState<TenantRow[] | null>(null);
  const [groups, setGroups] = useState<GroupRow[]>([]);
  const [modules, setModules] = useState<Module[]>([]);
  const [err, setErr] = useState("");

  const [q, setQ] = useState("");
  // Сводка свёрнута по умолчанию: до неё доходят редко, а места она занимала
  // ровно столько, что список уезжал под сгиб экрана.
  const [summary, setSummary] = useState(false);
  const [openGroups, setOpenGroups] = useState<Set<string>>(new Set());

  const [activeId, setActiveId] = useState<string | null>(null);
  const [overId, setOverId] = useState<string | null>(null);
  const [menu, setMenu] = useState<{ tenantId: string; rect: DOMRect } | null>(null);
  const [dialog, setDialog] = useState<
    | { mode: "create"; name: string; members: string[] }
    | { mode: "rename"; name: string; groupId: string }
    | null
  >(null);
  const [dialogBusy, setDialogBusy] = useState(false);
  const [dialogErr, setDialogErr] = useState("");

  const load = useCallback(async () => {
    // Клиенты и папки — два запроса, но один экран: ждём оба, иначе список
    // мигнёт «вне папок» на всё, пока едут папки.
    const [rt, rg] = await Promise.all([
      opApi<{ tenants: TenantRow[]; modules: Module[] }>(apiKey, "opListTenants"),
      opApi<{ groups: GroupRow[] }>(apiKey, "opListGroups"),
    ]);
    if (rt.ok) {
      setTenants(rt.data.tenants);
      setModules(rt.data.modules);
    } else {
      setErr(rt.error);
      if (rt.error === "Требуется ключ администратора") onUnauthorized();
      return;
    }
    if (rg.ok) setGroups(rg.data.groups);
  }, [apiKey, onUnauthorized]);

  useEffect(() => {
    load();
  }, [load]);

  const sensors = useSensors(
    // Те же пороги, что на доске задач: несколько пикселей мышью, удержание
    // пальцем. Иначе каждый клик по карточке — перенос на пиксель, и карточка
    // клиента перестаёт открываться.
    useSensor(MouseSensor, { activationConstraint: { distance: 4 } }),
    useSensor(TouchSensor, { activationConstraint: { delay: 220, tolerance: 8 } }),
  );

  async function restore(id: string) {
    const r = await opApi(apiKey, "opRestoreTenant", { tenant_id: id });
    if (r.ok) load();
  }

  async function moveTenant(tenantId: string, groupId: string | null) {
    const r = await opApi(apiKey, "opMoveTenantToGroup", { tenant_id: tenantId, group_id: groupId });
    if (!r.ok) { setErr(r.error); return; }
    setErr("");
    await load();
  }

  async function submitDialog(name: string, slug: string) {
    if (!dialog) return;
    setDialogBusy(true);
    setDialogErr("");
    if (dialog.mode === "rename") {
      const r = await opApi(apiKey, "opRenameGroup", { group_id: dialog.groupId, name });
      setDialogBusy(false);
      if (!r.ok) { setDialogErr(r.error); return; }
      setDialog(null);
      await load();
      return;
    }
    const r = await opApi<{ group: GroupRow }>(apiKey, "opCreateGroup", { name, slug });
    if (!r.ok) { setDialogBusy(false); setDialogErr(r.error); return; }
    // Папка создана — складываем в неё оба заведения, из которых её собрали.
    // По одному, а не пачкой: отдельного действия на список нет, а два запроса
    // здесь дешевле, чем шестое op*-действие ради одного экрана.
    const gid = r.data.group.id;
    for (const id of dialog.members) {
      const m = await opApi(apiKey, "opMoveTenantToGroup", { tenant_id: id, group_id: gid });
      if (!m.ok) { setDialogBusy(false); setDialogErr(m.error); return; }
    }
    setDialogBusy(false);
    setDialog(null);
    setOpenGroups((s) => new Set(s).add(gid));
    await load();
  }

  function onDragEnd(e: DragEndEvent) {
    const activeRaw = String(e.active.id);
    setActiveId(null);
    setOverId(null);
    if (!e.over) return;
    const overRaw = String(e.over.id);
    if (overRaw === activeRaw) return;

    const tenantId = activeRaw.slice("tenant:".length);
    const dragged = tenants?.find((t) => t.id === tenantId);
    if (!dragged) return;

    if (overRaw === "ungrouped") {
      if (dragged.group_id !== null) void moveTenant(tenantId, null);
      return;
    }
    if (overRaw.startsWith("group:")) {
      const gid = overRaw.slice("group:".length);
      if (dragged.group_id !== gid) void moveTenant(tenantId, gid);
      return;
    }
    if (overRaw.startsWith("tenant:")) {
      const targetId = overRaw.slice("tenant:".length);
      const target = tenants?.find((t) => t.id === targetId);
      if (!target) return;
      // Цель уже в папке — бросок означает «туда же», а не «сделать ещё одну».
      if (target.group_id !== null) {
        if (dragged.group_id !== target.group_id) void moveTenant(tenantId, target.group_id);
        return;
      }
      // Оба вне папок — предлагаем собрать из них сеть. Имя по умолчанию —
      // общее начало названий (п. 2 плана), оператор правит.
      setDialogErr("");
      setDialog({
        mode: "create",
        name: commonNamePrefix(dragged.name, target.name),
        members: [targetId, tenantId],
      });
    }
  }

  const all = tenants ?? [];
  const active = all.filter((t) => !t.archived);
  const found = all.filter((t) => matches(t, q));
  const foundActive = found.filter((t) => !t.archived);
  const foundArchived = found.filter((t) => t.archived);

  const venues = active.reduce((s, t) => s + t.venues.length, 0);
  const owners = active.reduce((s, t) => s + t.owners.length, 0);
  const archived = all.length - active.length;
  const clientSegs: Seg[] = [
    { value: active.length, color: "var(--op-cat-1)", label: "Активные" },
    { value: archived, color: "var(--op-cat-2)", label: "В архиве" },
  ];

  const menuTenant = menu ? all.find((t) => t.id === menu.tenantId) ?? null : null;
  const draggedTenant = activeId ? all.find((t) => `tenant:${t.id}` === activeId) ?? null : null;
  // Полосу «вынуть» показываем, только когда несут карточку, лежащую в папке.
  const showUngroup = draggedTenant !== null && draggedTenant.group_id !== null;
  const ungrouped = foundActive.filter((t) => t.group_id === null);
  const searching = q.trim() !== "";
  // При поиске показываем только те сети, где что-то нашлось, — либо сама сеть
  // по имени, либо ресторан внутри.
  const shownGroups = searching ? groups.filter((g) => matchesGroup(g, q) || foundActive.some((t) => t.group_id === g.id)) : groups;

  return (
    <section>
      <header className="mb-4 flex flex-wrap items-end justify-between gap-3">
        <div>
          <h1 className="text-2xl font-bold tracking-tight sm:text-3xl">Клиенты</h1>
          <p className="mt-1 text-sm text-[var(--op-muted)]">
            Сеть — ресторан — его настройка. Сеть из одного ресторана это нормально: второй добавится в неё же, и адрес первого не изменится.
          </p>
        </div>
        <button onClick={() => onOnboard(null)} className={bPrimary}>+ Новая сеть</button>
      </header>

      {/* Поиск — одной строкой над содержимым. Переключателя «Все / По сетям»
          больше нет (19.09): раскладка одна, потому что и устройство одно —
          рестораны живут в сетях. «Без сети» ниже — только у клиентов,
          заведённых до этого решения. */}
      <div className="mb-4 flex flex-wrap items-center gap-2">
        <div className="relative min-w-[220px] flex-1">
          <span className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-[var(--op-dim)]"><IcSearch /></span>
          <input
            value={q}
            onChange={(e) => setQ(e.target.value)}
            placeholder="Поиск: название, адрес, владелец"
            aria-label="Поиск клиента"
            className={`${field} w-full pl-9`}
          />
        </div>
        <button
          onClick={() => setSummary((v) => !v)}
          aria-expanded={summary}
          className="op-quiet inline-flex h-8 items-center gap-1.5 rounded-full px-3.5 text-[13px] font-semibold text-[var(--op-muted)] transition-colors hover:bg-[var(--op-ghost)] hover:text-[var(--op-fg)]"
        >
          <IcChevron open={summary} /> Сводка
        </button>
      </div>

      {tenants && (
        <StatStrip
          items={[
            { label: "клиентов", value: active.length },
            { label: "папок", value: groups.length },
            { label: "точек", value: venues },
            { label: "владельцев", value: owners },
            { label: "в архиве", value: archived },
          ]}
        />
      )}

      {/* Графики: раньше занимали первый экран, теперь по требованию. */}
      {tenants && tenants.length > 0 && summary && (
        <div className="mb-4 grid gap-4 md:grid-cols-2">
          <ChartCard title="Клиенты">
            <DonutChart segments={clientSegs} centerTop="всего" centerMain={String(tenants.length)} />
          </ChartCard>
          <ChartCard title="Подключение модулей">
            <HBars items={modules.map((m) => ({ label: m.label, value: active.filter((t) => t.feature_flags[m.key]).length, max: active.length }))} />
          </ChartCard>
        </div>
      )}

      {err && <p className="mb-3 text-sm text-[var(--op-danger-text)]">{err}</p>}
      {!tenants && !err && <SkeletonCards />}
      {tenants?.length === 0 && <EmptyState onOnboard={onOnboard} />}
      {/* «Ничего не нашлось» — только если не нашлось И среди ресторанов, И
          среди сетей: по запросу «пински» карточка сети показывается, даже
          когда ни один ресторан в неё именем не попал. */}
      {tenants && tenants.length > 0 && found.length === 0 && shownGroups.length === 0 && (
        <p className="py-10 text-center text-sm text-[var(--op-muted)]">
          По запросу «{q}» ничего не нашлось.
        </p>
      )}

      <DndContext
        sensors={sensors}
        collisionDetection={pointerWithin}
        onDragStart={(e: DragStartEvent) => { setActiveId(String(e.active.id)); setOverId(null); }}
        onDragOver={(e: DragOverEvent) => setOverId(e.over ? String(e.over.id) : null)}
        onDragEnd={onDragEnd}
        onDragCancel={() => { setActiveId(null); setOverId(null); }}
      >
        {showUngroup && <UngroupZone overId={overId} />}

        {/* Во время поиска сети РАСКРЫВАЮТСЯ, а пустые прячутся.
            Иначе (находка при самопроверке 19.09) поиск по ресторану в
            свёрнутой сети не показывал ничего: карточка нашлась, но лежала
            внутри закрытого блока. Раньше это спасал режим «Все» плоским
            списком — его больше нет. */}
        <div className="mb-3 grid gap-3">
          {shownGroups.map((g) => (
              <GroupBlock
                key={g.id}
                apiKey={apiKey}
                g={g}
                // Сеть нашлась сама (по имени) — показываем её ресторанами
                // целиком: человек искал сеть, а не ресторан в ней.
                tenants={(searching && matchesGroup(g, q) ? active : foundActive).filter((t) => t.group_id === g.id)}
                modules={modules}
                open={searching ? true : openGroups.has(g.id)}
                onToggle={(id) =>
                  setOpenGroups((s) => {
                    const n = new Set(s);
                    if (n.has(id)) n.delete(id); else n.add(id);
                    return n;
                  })
                }
                onOpen={onOpen}
                onRestore={restore}
                onMenu={(id, el) => setMenu({ tenantId: id, rect: el.getBoundingClientRect() })}
                onRename={(gr) => { setDialogErr(""); setDialog({ mode: "rename", name: gr.name, groupId: gr.id }); }}
                onAdd={onOnboard}
                onDelete={async (gr) => {
                  const r = await opApi(apiKey, "opDeleteGroup", { group_id: gr.id });
                  if (!r.ok) setErr(r.error); else { setErr(""); await load(); }
                }}
                activeId={activeId}
                overId={overId}
              />
          ))}
          {shownGroups.length === 0 && groups.length > 0 && searching && found.length > 0 && (
            <p className="rounded-2xl border border-dashed border-[var(--op-line)] px-4 py-6 text-center text-[13px] text-[var(--op-faint)]">
              По запросу «{q}» в сетях ничего не нашлось.
            </p>
          )}
          {groups.length === 0 && tenants && tenants.length > 0 && (
            <p className="rounded-2xl border border-dashed border-[var(--op-line)] px-4 py-6 text-center text-[13px] text-[var(--op-faint)]">
              Сетей пока нет. Перетащите карточку одного ресторана на другой — предложу собрать из них сеть.
            </p>
          )}
        </div>

        {/* «Без сети» — клиенты, заведённые до решения 19.09. Новых здесь не
            появляется: форма заводит ресторан только в сеть. Заголовок с
            объяснением, а не сухое «Вне папок»: оператор должен понимать, что
            это хвост, а не второй сорт клиентов. */}
        {ungrouped.length > 0 && (
          <div className="mb-2 mt-5 flex flex-wrap items-baseline gap-x-2 px-1">
            <p className="text-[11px] uppercase tracking-[0.12em] text-[var(--op-faint)]">Без сети — {ungrouped.length}</p>
            <p className="text-[12px] text-[var(--op-faint)]">заведены до перехода на сети; перетащите в сеть или оставьте как есть</p>
          </div>
        )}
        <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 xl:grid-cols-3">
          {ungrouped.map((t) => (
            <DraggableTenant
              key={t.id}
              t={t}
              modules={modules}
              onOpen={onOpen}
              onRestore={restore}
              onMenu={(id, el) => setMenu({ tenantId: id, rect: el.getBoundingClientRect() })}
              activeId={activeId}
              overId={overId}
            />
          ))}
        </div>

        {/* Призрак под курсором: без него карточка просто гаснет на месте и
            перенос читается как рывок (тот же вывод, что на доске задач). */}
        <DragOverlay dropAnimation={null}>
          {draggedTenant && (
            <div className="w-[280px] rotate-1 opacity-95">
              <TenantCard
                t={draggedTenant}
                modules={modules}
                onOpen={() => {}}
                onRestore={() => {}}
                onMenu={() => {}}
                draggable={false}
                dimmed={false}
                highlight="none"
              />
            </div>
          )}
        </DragOverlay>
      </DndContext>

      {foundArchived.length > 0 && (
        <>
          <p className="mb-2 mt-6 px-1 text-[11px] uppercase tracking-[0.12em] text-[var(--op-faint)]">
            В архиве — {foundArchived.length}
          </p>
          <div className="grid grid-cols-1 gap-2.5 sm:grid-cols-2 xl:grid-cols-3">
            {foundArchived.map((t) => (
              <TenantCard
                key={t.id}
                t={t}
                modules={modules}
                onOpen={onOpen}
                onRestore={restore}
                onMenu={() => {}}
                draggable={false}
                dimmed={false}
                highlight="none"
              />
            ))}
          </div>
        </>
      )}

      {menu && menuTenant && (
        <CardMenu
          anchor={menu.rect}
          tenant={menuTenant}
          groups={groups}
          onMove={(gid) => { setMenu(null); void moveTenant(menuTenant.id, gid); }}
          onClose={() => setMenu(null)}
        />
      )}

      {dialog && (
        <GroupDialog
          mode={dialog.mode}
          initialName={dialog.name}
          busy={dialogBusy}
          err={dialogErr}
          onSubmit={submitDialog}
          onClose={() => { setDialog(null); setDialogErr(""); }}
        />
      )}
    </section>
  );
}

function SkeletonCards() {
  return (
    <div className="grid grid-cols-1 gap-3 md:grid-cols-2">
      {[0, 1, 2, 3].map((i) => (
        <div key={i} className="h-[116px] animate-pulse rounded-[20px] border border-[var(--op-line)] bg-[var(--op-surface-2)]" />
      ))}
    </div>
  );
}

function EmptyState({ onOnboard }: { onOnboard: (groupId: string | null) => void }) {
  return (
    <div className={`${card} flex flex-col items-center py-12 text-center`}>
      <Avatar name="M i" className="h-14 w-14 text-lg" />
      <p className="mt-4 font-semibold">Пока нет клиентов</p>
      <p className="mt-1 max-w-sm text-sm text-[var(--op-muted)]">Заведите первого — одной формой создадутся сеть, ресторан и владелец. Сеть из одного ресторана это нормально: второй добавится в неё же.</p>
      <button onClick={() => onOnboard(null)} className={`${bPrimary} mt-5`}>+ Завести первого</button>
    </div>
  );
}

// ── журнал операторских действий (Фаза S) ──
type AuditEntry = {
  id: string;
  action: string;
  ok: boolean;
  error: string | null;
  ip: string | null;
  actor_fp: string | null;
  target_tenant_id: string | null;
  target_venue_id: string | null;
  summary: unknown;
  created_at: string;
};

// Человеческие названия операций для журнала.
//
// ⚠️ Список обязан покрывать ВСЕ пишущие op-действия. Незакрытое действие
// печатается в журнале своим кодом — 08.08 так и было: строки «opDeleteRole
// opDeleteRole» и «opRemoveEmployee opRemoveEmployee». Это нарушает правило
// Марата «технических надписей в интерфейсе нет», причём в самом видном месте:
// журнал читают, когда разбираются, кто что натворил.
//
// Заводя новое действие оператора — добавить сюда строку.
const ACTION_LABEL: Record<string, string> = {
  onboardTenant: "Онбординг клиента",
  // Роли заведения
  opCreateRole: "Новая роль",
  opUpdateRole: "Правка роли",
  opDeleteRole: "Удаление роли",
  opAddRoleFromTemplate: "Роль из заготовки",
  // Сотрудники
  opCreateEmployee: "Новый сотрудник",
  opUpdateEmployee: "Правка сотрудника",
  opRemoveEmployee: "Увольнение сотрудника",
  opResetEmployeePassword: "Сброс пароля сотрудника",
  opResetEmployeePin: "Сброс PIN сотрудника",
  // Смены
  opCreateShift: "Создание смены",
  opDeleteShift: "Удаление смены",
  opAssignShift: "Назначение на смену",
  opUnassignShift: "Снятие со смены",
  // Администраторы консоли
  opCreateAdmin: "Новый администратор",
  opUpdateAdmin: "Правка администратора",
  opDeleteAdmin: "Отзыв администратора",
  opRotateAdminKey: "Новый ключ администратора",
  opMarkDemoStand: "Заведение объявлено демо-стендом",
  opSeedDemo: "Наполнение демо-данными",
  // Заведение и данные
  opSetTenantFee: "Абонплата",
  opSetTenantAiLimit: "Лимит ИИ",
  opSetTenantLogo: "Логотип заведения",
  opCreateItem: "Позиция каталога",
  opUpdateItem: "Правка позиции каталога",
  opUpdateMenuItem: "Правка позиции меню",
  opSetTenantFlags: "Модули",
  opSetTenantTelegram: "Telegram: бот, расписание, получатели",
  opTelegramTest: "Telegram: пробное сообщение",
  opTelegramSetupMenu: "Telegram: меню команд бота",
  opSetTenantPermissions: "Права ролей",
  opRenameTenant: "Переименование заведения",
  opRenameVenue: "Переименование точки",
  // opCreateVenue снят 19.09 (точки больше не заводят), но в журнале остаются
  // прошлые записи — подпись нужна, иначе там висит голый ключ действия.
  opCreateVenue: "Создание точки (снято 19.09)",
  opArchiveVenue: "Закрытие точки",
  opCopyTenantSetup: "Копирование настройки из ресторана сети",
  opSplitVenueToTenant: "Вынос точки в отдельный ресторан",
  opSetBlankMeta: "Шапка бланка",
  opArchiveTenant: "Архивация",
  opRestoreTenant: "Восстановление",
  opDeleteTenant: "Удаление навсегда",
  opResetOwnerPassword: "Сброс пароля владельца",
  opApplyStarterSettings: "Стартовый набор",
  opSetVenuePaySource: "Кто считает зарплату",
  opCreateCategory: "Категория учёта",
  opImportMenu: "Импорт меню",
  opImportInventoryCatalog: "Импорт каталога",
  opImportBottleProfiles: "Импорт весов бутылок",
  opSetItemCategory: "Категория позиции",
  opTelegramDiscover: "Поиск чатов Telegram",
};

/**
 * Администраторы консоли — свой ключ на человека (заказ Марата 07.08).
 *
 * До этого ключ был один на всех: в журнале оставался отпечаток, а не имя, и
 * отозвать доступ у одного человека, не сменив ключ всем, было нельзя.
 *
 * ⚠️ КЛЮЧ ПОКАЗЫВАЕТСЯ ОДИН РАЗ. В базе лежит только необратимый хеш, поэтому
 * «посмотреть ключ позже» нельзя даже нам. Экран говорит об этом прямо и даёт
 * скопировать ключ, пока он на виду; забыли — «Новый ключ» выдаёт другой, а
 * прежний в ту же секунду перестаёт работать.
 */
function AdminsView({ apiKey }: { apiKey: string }) {
  const [rows, setRows] = useState<OperatorAdminRow[] | null>(null);
  const [err, setErr] = useState("");
  const [busy, setBusy] = useState(false);
  const [name, setName] = useState("");
  const [note, setNote] = useState("");
  // Показанный один раз ключ: держим в состоянии экрана и нигде больше.
  const [fresh, setFresh] = useState<{ id: string; name: string; key: string } | null>(null);
  const [copied, setCopied] = useState(false);

  const load = useCallback(async () => {
    const r = await opApi<{ admins: OperatorAdminRow[] }>(apiKey, "opListAdmins");
    if (r.ok) setRows(r.data.admins);
    else setErr(r.error);
  }, [apiKey]);

  useEffect(() => {
    load();
  }, [load]);

  async function create() {
    if (!name.trim()) return;
    setBusy(true);
    setErr("");
    const r = await opApi<{ admin: OperatorAdminRow; key: string }>(apiKey, "opCreateAdmin", {
      name: name.trim(),
      note: note.trim() || undefined,
    });
    setBusy(false);
    if (!r.ok) {
      setErr(r.error);
      return;
    }
    setFresh({ id: r.data.admin.id, name: r.data.admin.name, key: r.data.key });
    setName("");
    setNote("");
    setCopied(false);
    load();
  }

  async function update(id: string, patch: Record<string, unknown>) {
    setBusy(true);
    const r = await opApi(apiKey, "opUpdateAdmin", { admin_id: id, ...patch });
    setBusy(false);
    if (!r.ok) setErr(r.error);
    else load();
  }

  async function rotate(row: OperatorAdminRow) {
    if (!confirm(`Выдать «${row.name}» новый ключ? Прежний перестанет работать сразу.`)) return;
    setBusy(true);
    const r = await opApi<{ admin: OperatorAdminRow; key: string }>(apiKey, "opRotateAdminKey", {
      admin_id: row.id,
    });
    setBusy(false);
    if (!r.ok) {
      setErr(r.error);
      return;
    }
    setFresh({ id: row.id, name: row.name, key: r.data.key });
    setCopied(false);
    load();
  }

  async function remove(row: OperatorAdminRow) {
    if (!confirm(`Удалить «${row.name}»? Записи журнала останутся.`)) return;
    setBusy(true);
    const r = await opApi(apiKey, "opDeleteAdmin", { admin_id: row.id });
    setBusy(false);
    if (!r.ok) setErr(r.error);
    else load();
  }

  return (
    <section>
      <header className="mb-6">
        <h1 className="text-2xl font-bold tracking-tight sm:text-3xl">Администраторы</h1>
        <p className="mt-1 text-sm text-[var(--op-muted)]">
          У каждого свой ключ: в журнале видно имя, а не отпечаток, и доступ
          отзывается по одному человеку. Корневой ключ из настроек сервера
          продолжает работать как аварийный вход.
        </p>
      </header>

      {err && <p className="mb-4 text-sm text-[var(--op-danger-text)]">{err}</p>}

      {fresh && (
        <div className={`${card} mb-4 border-[var(--op-accent)] bg-[var(--op-accent-soft)]`}>
          <div className={lbl}>Ключ для «{fresh.name}» — показывается один раз</div>
          <div className="mt-2 flex flex-wrap items-center gap-2">
            <code className="min-w-0 flex-1 truncate rounded-xl bg-[var(--op-surface)] px-3 py-2.5 font-mono text-[13px] text-[var(--op-fg)]">
              {fresh.key}
            </code>
            <button
              className={bPrimary}
              onClick={() => {
                navigator.clipboard?.writeText(fresh.key);
                setCopied(true);
              }}
            >
              {copied ? "Скопировано" : "Скопировать"}
            </button>
            <button className={bGhost} onClick={() => setFresh(null)}>
              Скрыть
            </button>
          </div>
          <p className="mt-2 text-xs text-[var(--op-muted)]">
            Передайте его человеку лично. Второй раз ключ не покажется: в базе
            хранится только хеш — потерянный ключ заменяется новым.
          </p>
        </div>
      )}

      <div className={`${card} mb-4 space-y-3`}>
        <div className={lbl}>Новый администратор</div>
        <div className="grid gap-2 sm:grid-cols-[1fr_1fr_auto]">
          <input
            className={field}
            placeholder="Имя, например «Никита»"
            value={name}
            onChange={(e) => setName(e.target.value)}
          />
          <input
            className={field}
            placeholder="Пометка: за что отвечает"
            value={note}
            onChange={(e) => setNote(e.target.value)}
          />
          <button className={bPrimary} onClick={create} disabled={busy || !name.trim()}>
            Выдать ключ
          </button>
        </div>
      </div>

      {!rows && !err && <div className="h-40 animate-pulse rounded-[20px] border border-[var(--op-line)] bg-[var(--op-surface-2)]" />}
      {rows?.length === 0 && (
        <div className={`${card} text-sm text-[var(--op-muted)]`}>
          Персональных ключей пока нет — в консоль входят корневым.
        </div>
      )}

      {rows && rows.length > 0 && (
        <div className="space-y-2">
          {rows.map((a) => (
            <div key={a.id} className={`${card} flex flex-wrap items-center gap-3`}>
              <span className="min-w-0 flex-1">
                <span className="flex items-center gap-2">
                  <span className="truncate text-sm font-semibold text-[var(--op-fg)]">{a.name}</span>
                  {!a.isActive && (
                    <span className="rounded-full bg-[var(--op-danger-soft)] px-2 py-0.5 text-[10px] font-bold uppercase text-[var(--op-danger-text)]">
                      отключён
                    </span>
                  )}
                </span>
                <span className="mt-0.5 block truncate text-xs text-[var(--op-muted)]">
                  {a.note ? `${a.note} · ` : ""}отпечаток {a.keyFp} ·{" "}
                  {a.lastSeenAt ? `был ${new Date(a.lastSeenAt).toLocaleString("ru-RU")}` : "ещё не входил"}
                </span>
              </span>
              <button className={bGhost} onClick={() => update(a.id, { is_active: !a.isActive })} disabled={busy}>
                {a.isActive ? "Отключить" : "Включить"}
              </button>
              <button className={bGhost} onClick={() => rotate(a)} disabled={busy}>
                Новый ключ
              </button>
              <button className={bDanger} onClick={() => remove(a)} disabled={busy}>
                Удалить
              </button>
            </div>
          ))}
        </div>
      )}
    </section>
  );
}

type SpendRow = {
  tenantId: string;
  name: string;
  slug: string;
  calls: number;
  tokens: number;
  rub: number | null;
  activeDays: number;
  monthlyFee: number | null;
};

type SpendReport = {
  from: string;
  to: string;
  rows: SpendRow[];
  totals: { calls: number; tokens: number; rub: number | null; tenants: number };
  rubPerMillionTokens: number | null;
};

/**
 * «Расход ИИ» — во сколько обходится помощник по каждому клиенту (Э7).
 *
 * Зачем экран. Токены писались в базу с первого дня, но не читались: продаём
 * ИИ-модуль отдельной строкой, а сколько он стоит нам по каждому клиенту —
 * никто не знал. Здесь видно и расход, и абонплату рядом, то есть маржу.
 *
 * Рубли показываются, только если задана цена за миллион токенов
 * (AI_RUB_PER_MTOKEN). Без неё столбец честно пуст: выдуманная цена в консоли
 * хуже, чем прочерк, — по ней принимают решение о стоимости подписки.
 */
type GapItem = { question: string; count: number; tenants: number; kind: string };
type GapReport = { from: string; to: string; total: number; items: GapItem[] };

const LS_PRICE = "klait_op_ai_rub_per_m";
const LS_FEE = "klait_op_ai_fee";
function readLs(key: string): string {
  try {
    return localStorage.getItem(key) ?? "";
  } catch {
    return "";
  }
}
function writeLs(key: string, v: string): void {
  try {
    localStorage.setItem(key, v);
  } catch {
    // Приватное окно — калькулятор работает, просто не запомнит.
  }
}

function MoneyTile({ label, value, hint, tone }: { label: string; value: string; hint?: string; tone?: "bad" }) {
  return (
    <div className="rounded-2xl bg-[var(--op-surface-2)] px-4 py-3">
      <div className="text-xs text-[var(--op-muted)]">{label}</div>
      <div className={`mt-1 text-xl font-bold tabular-nums ${tone === "bad" ? "text-[var(--op-danger-text)]" : ""}`}>{value}</div>
      {hint && <div className="mt-0.5 text-xs text-[var(--op-faint)]">{hint}</div>}
    </div>
  );
}

function SpendView({ apiKey }: { apiKey: string }) {
  const [days, setDays] = useState(30);
  const [data, setData] = useState<SpendReport | null>(null);
  const [gaps, setGaps] = useState<GapReport | null>(null);
  const [err, setErr] = useState("");
  // Калькулятор (28.09): цена за миллион токенов и ИИ-абонплата правятся прямо
  // здесь. Цена по умолчанию — из AI_RUB_PER_MTOKEN, абонплата — 5 000 ₽
  // (решение по цене 20.09). Запоминаются в браузере оператора: это его
  // «что если», а не настройка продукта.
  const [priceIn, setPriceIn] = useState(() => readLs(LS_PRICE));
  const [feeIn, setFeeIn] = useState(() => readLs(LS_FEE) || "5000");
  // Цену с сервера подставляем ОДИН раз — иначе стёртое поле тут же
  // заполнялось бы обратно и очистить его было бы нельзя.
  const pricePrefilled = useRef(false);
  useEffect(() => {
    if (!data || pricePrefilled.current) return;
    pricePrefilled.current = true;
    if (priceIn === "" && data.rubPerMillionTokens !== null) setPriceIn(String(data.rubPerMillionTokens));
  }, [data, priceIn]);
  const parseMoney = (v: string) => {
    const n = Number(v.replace(/\s/g, "").replace(",", "."));
    return Number.isFinite(n) && n > 0 ? n : null;
  };
  const price = parseMoney(priceIn);
  const fee = parseMoney(feeIn) ?? 0;
  // Длина периода — из самого отчёта, а не из выбранной кнопки: на один кадр
  // после переключения 30-дневный отчёт иначе делился бы на 7.
  const periodDays = data ? Math.round((Date.parse(data.to) - Date.parse(data.from)) / 86_400_000) + 1 : days;
  const money = data ? spendMoney(data.rows, { perMillion: price, periodDays, aiFee: fee }) : null;
  const moneyOf = new Map(money?.rows.map((r) => [r.tenantId, r]) ?? []);

  useEffect(() => {
    setData(null);
    opApi<SpendReport>(apiKey, "opAiSpend", { days }).then((r) => {
      if (r.ok) setData(r.data);
      else setErr(r.error);
    });
    // Пробелы в умениях — рядом с расходом: это две стороны одного вопроса
    // «что происходит с помощником». Отдельный раздел ради таблицы из пяти
    // строк заводить незачем.
    opApi<GapReport>(apiKey, "opAiGaps", { days }).then((r) => {
      if (r.ok) setGaps(r.data);
    });
  }, [apiKey, days]);

  const rub = (v: number | null) => (v === null ? "—" : `${v.toLocaleString("ru-RU", { maximumFractionDigits: 2 })} ₽`);
  const num = (v: number) => v.toLocaleString("ru-RU");

  return (
    <section>
      <header className="mb-6">
        <h1 className="text-2xl font-bold tracking-tight sm:text-3xl">Расход на помощника</h1>
        <p className="mt-1 text-sm text-[var(--op-muted)]">
          Сколько вопросов и токенов потратил каждый клиент. Платим за модель мы, поэтому рядом стоит абонплата — видно
          маржу.
        </p>
      </header>

      {/* ── Калькулятор денег: итог в рублях всего и прогноз на месяц ── */}
      <div className="mb-6 rounded-2xl border border-[var(--op-card-line)] p-4">
        <div className="flex flex-wrap items-end gap-4">
          <label className="text-sm">
            <span className="mb-1 block text-[var(--op-muted)]">Цена модели, ₽ за 1 млн токенов</span>
            <input
              className="h-10 w-40 rounded-xl border border-[var(--op-card-line)] bg-[var(--op-surface-2)] px-3 tabular-nums"
              inputMode="decimal"
              placeholder="например, 40"
              value={priceIn}
              onChange={(e) => {
                setPriceIn(e.target.value);
                writeLs(LS_PRICE, e.target.value);
              }}
            />
          </label>
          <label className="text-sm">
            <span className="mb-1 block text-[var(--op-muted)]">ИИ-абонплата клиента, ₽ в месяц</span>
            <input
              className="h-10 w-40 rounded-xl border border-[var(--op-card-line)] bg-[var(--op-surface-2)] px-3 tabular-nums"
              inputMode="decimal"
              value={feeIn}
              onChange={(e) => {
                setFeeIn(e.target.value);
                writeLs(LS_FEE, e.target.value);
              }}
            />
          </label>
          <p className="max-w-md text-xs text-[var(--op-faint)]">
            {data?.rubPerMillionTokens != null
              ? `На сервере задано ${data.rubPerMillionTokens.toLocaleString("ru-RU")} ₽ за 1 млн (AI_RUB_PER_MTOKEN). `
              : "Цена на сервере не задана (AI_RUB_PER_MTOKEN) — впишите её здесь. "}
            Прогноз на месяц — средний расход в день за выбранный период × 30,4; сегодняшний неполный день и дни до подключения клиента его немного занижают.
          </p>
        </div>
        {money && (
          <div className="mt-4 grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
            <MoneyTile label={`Потратили за ${periodDays} дн.`} value={rub(money.totals.costPeriod)} />
            <MoneyTile label="Прогноз расхода в месяц" value={rub(money.totals.costMonth)} />
            <MoneyTile
              label={`ИИ-абонплата в месяц, ${money.totals.payers} ${plural(money.totals.payers, "клиент", "клиента", "клиентов")} с вопросами`}
              value={rub(money.totals.feeMonth)}
            />
            <MoneyTile
              label="Остаётся в месяц"
              value={rub(money.totals.marginMonth)}
              tone={money.totals.marginMonth !== null && money.totals.marginMonth < 0 ? "bad" : undefined}
              hint={money.totals.perCall !== null ? `вопрос в среднем стоит ${rub(money.totals.perCall)}` : undefined}
            />
          </div>
        )}
        {price === null && <p className="mt-3 text-xs text-[var(--op-muted)]">Пока цена не вписана, рублей нет — только токены.</p>}
      </div>

      <div className="mb-4 flex gap-2">
        {[7, 30, 90].map((d) => (
          <button
            key={d}
            type="button"
            onClick={() => setDays(d)}
            className={`rounded-xl px-3 py-1.5 text-sm ${d === days ? "bg-[var(--op-accent)] text-[var(--op-on-accent)]" : "bg-[var(--op-surface-2)] text-[var(--op-muted)]"}`}
          >
            {d} дн.
          </button>
        ))}
      </div>

      {err && <p className="text-sm text-[var(--op-danger-text)]">{err}</p>}
      {!data && !err && <p className="text-sm text-[var(--op-muted)]">Считаем…</p>}

      {data && data.rows.length === 0 && (
        <p className="text-sm text-[var(--op-muted)]">
          За период {data.from} — {data.to} помощником никто не пользовался.
        </p>
      )}

      {data && data.rows.length > 0 && (
        <div className="overflow-x-auto rounded-2xl border border-[var(--op-card-line)]">
          <table className="w-full text-sm">
            <thead className="text-left text-[var(--op-muted)]">
              <tr className="border-b border-[var(--op-card-line)]">
                <th className="px-4 py-2 font-medium">Клиент</th>
                <th className="px-4 py-2 font-medium">Вопросов</th>
                <th className="px-4 py-2 font-medium">Токенов</th>
                <th className="px-4 py-2 font-medium">Стоило за период</th>
                <th className="px-4 py-2 font-medium">В месяц (прогноз)</th>
                <th className="px-4 py-2 font-medium">₽ за вопрос</th>
                <th className="px-4 py-2 font-medium">Остаётся от ИИ-абонплаты</th>
                <th className="px-4 py-2 font-medium">Дней с вопросами</th>
                <th className="px-4 py-2 font-medium">Абонплата</th>
              </tr>
            </thead>
            <tbody>
              {data.rows.map((r) => (
                <tr key={r.tenantId} className="border-b border-[var(--op-card-line)] last:border-0">
                  <td className="px-4 py-2">
                    {r.name} <span className="text-[var(--op-faint)]">/{r.slug}</span>
                  </td>
                  <td className="px-4 py-2 tabular-nums">{num(r.calls)}</td>
                  <td className="px-4 py-2 tabular-nums">{num(r.tokens)}</td>
                  <td className="px-4 py-2 tabular-nums">{rub(moneyOf.get(r.tenantId)?.costPeriod ?? null)}</td>
                  <td className="px-4 py-2 tabular-nums">{rub(moneyOf.get(r.tenantId)?.costMonth ?? null)}</td>
                  <td className="px-4 py-2 tabular-nums">{rub(moneyOf.get(r.tenantId)?.perCall ?? null)}</td>
                  <td className={`px-4 py-2 tabular-nums ${(moneyOf.get(r.tenantId)?.marginMonth ?? 0) < 0 ? "text-[var(--op-danger-text)]" : ""}`}>
                    {rub(moneyOf.get(r.tenantId)?.marginMonth ?? null)}
                  </td>
                  <td className="px-4 py-2 tabular-nums">{r.activeDays}</td>
                  <td className="px-4 py-2 tabular-nums">{r.monthlyFee === null ? "—" : `${num(r.monthlyFee)} ₽`}</td>
                </tr>
              ))}
              <tr className="bg-[var(--op-surface-2)] font-semibold">
                <td className="px-4 py-2">Итого за {data.from} — {data.to}</td>
                <td className="px-4 py-2 tabular-nums">{num(data.totals.calls)}</td>
                <td className="px-4 py-2 tabular-nums">{num(data.totals.tokens)}</td>
                <td className="px-4 py-2 tabular-nums">{rub(money?.totals.costPeriod ?? null)}</td>
                <td className="px-4 py-2 tabular-nums">{rub(money?.totals.costMonth ?? null)}</td>
                <td className="px-4 py-2 tabular-nums">{rub(money?.totals.perCall ?? null)}</td>
                <td className="px-4 py-2 tabular-nums">{rub(money?.totals.marginMonth ?? null)}</td>
                <td className="px-4 py-2 tabular-nums">
                  {data.totals.tenants} {plural(data.totals.tenants, "клиент", "клиента", "клиентов")}
                </td>
                <td className="px-4 py-2" />
              </tr>
            </tbody>
          </table>
        </div>
      )}

      {/* О чём спрашивают, а помощник не умеет. Это список работ по модулю,
          посчитанный по тому, что люди спрашивают В РАБОТЕ, а не по нашим
          догадкам о том, что им нужно. */}
      {gaps && gaps.items.length > 0 && (
        <div className="mt-8">
          <h2 className="text-lg font-semibold">О чём спрашивают, а он не умеет</h2>
          <p className="mt-1 text-sm text-[var(--op-muted)]">
            {gaps.total} вопросов без ответа за период. Близкие формулировки сложены. Отсюда берутся приоритеты развития
            помощника — по фактам, а не по догадкам.
          </p>
          <ul className="mt-3 space-y-1.5">
            {gaps.items.slice(0, 15).map((g, i) => (
              <li key={i} className="flex items-baseline gap-3 text-sm">
                <span className="w-16 shrink-0 tabular-nums text-[var(--op-muted)]">
                  {g.count}× · {g.tenants}
                </span>
                <span>{g.question}</span>
              </li>
            ))}
          </ul>
          <p className="mt-2 text-xs text-[var(--op-faint)]">
            Первое число — сколько раз спросили, второе — у скольких клиентов. Кто спрашивал, не хранится.
          </p>
        </div>
      )}
    </section>
  );
}

function AuditView({ apiKey }: { apiKey: string }) {
  const [entries, setEntries] = useState<AuditEntry[] | null>(null);
  const [err, setErr] = useState("");
  const [open, setOpen] = useState<string | null>(null);

  useEffect(() => {
    opApi<{ entries: AuditEntry[] }>(apiKey, "opListAuditLog", { limit: 150 }).then((r) => {
      if (r.ok) setEntries(r.data.entries);
      else setErr(r.error);
    });
  }, [apiKey]);

  return (
    <section>
      <header className="mb-6">
        <h1 className="text-2xl font-bold tracking-tight sm:text-3xl">Журнал действий</h1>
        <p className="mt-1 text-sm text-[var(--op-muted)]">
          Кто / что / когда по операторским операциям. Чувствительные (сброс пароля, архив, удаление) фиксируются всегда. Клик по строке — детали.
        </p>
      </header>

      {err && <p className="text-sm text-[var(--op-danger-text)]">{err}</p>}
      {!entries && !err && <div className="h-64 animate-pulse rounded-[20px] border border-[var(--op-line)] bg-[var(--op-surface-2)]" />}
      {entries?.length === 0 && <div className={`${card} text-sm text-[var(--op-muted)]`}>Пока нет записей.</div>}

      {entries && entries.length > 0 && (
        <div className="overflow-hidden rounded-[20px] border border-[var(--op-line)] bg-[var(--op-surface)]">
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-[var(--op-line)] text-left text-[11px] uppercase tracking-[0.12em] text-[var(--op-faint)]">
                  <th className="px-5 py-3 font-medium">Время</th>
                  <th className="px-3 py-3 font-medium">Действие</th>
                  <th className="px-3 py-3 font-medium">Цель</th>
                  <th className="px-3 py-3 font-medium">IP</th>
                  <th className="px-5 py-3 text-center font-medium">Итог</th>
                </tr>
              </thead>
              <tbody>
                {entries.map((e) => (
                  <Fragment key={e.id}>
                    <tr
                      onClick={() => setOpen(open === e.id ? null : e.id)}
                      className="cursor-pointer border-b border-[var(--op-line)] transition-colors last:border-0 hover:bg-[var(--op-ghost)]"
                    >
                      <td className="whitespace-nowrap px-5 py-3 tabular-nums text-[var(--op-muted)]">{new Date(e.created_at).toLocaleString("ru-RU")}</td>
                      <td className="px-3 py-3">
                        {/* Код действия показываем ТОЛЬКО когда человеческого
                            названия нет: иначе строка читалась «opDeleteRole
                            opDeleteRole». Он не украшение, а запасной выход на
                            случай действия, не заведённого в ACTION_LABEL. */}
                        <span className="font-medium">{ACTION_LABEL[e.action] ?? e.action}</span>
                      </td>
                      <td className="px-3 py-3 font-mono text-xs text-[var(--op-muted)]">
                        {e.target_tenant_id ? e.target_tenant_id.slice(0, 8) : "—"}
                        {e.target_venue_id ? ` · ${e.target_venue_id.slice(0, 8)}` : ""}
                      </td>
                      <td className="px-3 py-3 font-mono text-xs text-[var(--op-muted)]">{e.ip ?? "—"}</td>
                      <td className="px-5 py-3 text-center">
                        {e.ok ? (
                          <span className="inline-flex text-[var(--op-accent)]"><IcCheck /></span>
                        ) : (
                          <span title={e.error ?? ""} className="inline-flex text-[var(--op-danger-text)]"><IcCross /></span>
                        )}
                      </td>
                    </tr>
                    {open === e.id && (
                      <tr className="border-b border-[var(--op-line)] bg-[var(--op-surface-2)]">
                        <td colSpan={5} className="px-5 py-3">
                          <div className="grid gap-1 text-xs text-[var(--op-muted)]">
                            {!e.ok && e.error && <div className="text-[var(--op-danger-text)]">Ошибка: {e.error}</div>}
                            <div>Ключ (отпечаток): <span className="font-mono text-[var(--op-muted)]">{e.actor_fp ?? "—"}</span></div>
                            <div className="break-all font-mono text-[var(--op-muted)]">{JSON.stringify(e.summary)}</div>
                          </div>
                        </td>
                      </tr>
                    )}
                  </Fragment>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </section>
  );
}

// ── онбординг нового клиента ──
//
// «Одна точка / сеть» (правка Марата 17.09): решение принимается ДО того, как
// оператор набирает данные ресторана. У одиночного ресторана слаг заводит
// сабдомен (`hide.klait.ru`); у ресторана в сети слаг работает первым
// сегментом пути под доменом сети (`pinski.klait.ru/hide/`, Э2). Заводить
// сабдомен под ресторан, который через минуту уходит в папку, — это лишний
// адрес и лишний ярлык у клиента, а обратно его не собрать.
// ── Новый клиент ──
//
// Правка 19.09 (решение пользователя «оставим только логику сети»): КАЖДЫЙ
// клиент живёт в сети, даже одиночный ресторан. Поэтому режимов осталось два —
// «новая сеть» и «в существующую», — а «один ресторан» исчез как отдельный
// путь: это сеть из одного ресторана, и она заводится той же формой.
//
// Что это даёт оператору. Раньше выбор «одна точка / сеть» делался в первую
// минуту знакомства с клиентом и задавал адрес НАВСЕГДА: угадал — хорошо, не
// угадал — у ресторатора со вторым заведением или чужой сабдомен, или точка
// внутри чужого бренда. Теперь угадывать нечего: второй ресторан всегда
// добавляется в ту же сеть, и адрес от этого не меняется.
//
// Поле «название точки» убрано: точка у ресторана одна и называется как он.
type OnboardMode = "new-network" | "existing-network";

function Onboard({ apiKey, groupId: preset, onBack }: { apiKey: string; groupId: string | null; onBack: () => void }) {
  const [mode, setMode] = useState<OnboardMode>(preset ? "existing-network" : "new-network");
  const [modules, setModules] = useState<Module[]>([]);
  const [f, setF] = useState({ slug: "", name: "", ownerName: "", email: "", phone: "", password: "" });
  const [mods, setMods] = useState<Record<string, boolean>>({});
  const [groups, setGroups] = useState<GroupRow[]>([]);
  const [groupId, setGroupId] = useState(preset ?? "");
  const [newGroup, setNewGroup] = useState({ name: "", slug: "" });
  const [fromGroup, setFromGroup] = useState(false);
  // Откуда копировать настройку (19.09). Виден только при добавлении в
  // существующую сеть: копировать в первый ресторан сети неоткуда.
  const [copyFrom, setCopyFrom] = useState("");
  const [copyParts, setCopyParts] = useState<Record<string, boolean>>(Object.fromEntries(CLONE_PARTS.map((p) => [p, true])));
  const [err, setErr] = useState("");
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState<null | { owner: OwnerLite; group: { name: string; slug: string } | null; address: string; copied: CloneResult | null }>(null);

  const reloadGroups = useCallback(async () => {
    const r = await opApi<{ groups: GroupRow[] }>(apiKey, "opListGroups");
    if (r.ok) setGroups(r.data.groups);
  }, [apiKey]);

  useEffect(() => {
    opApi<{ modules: Module[] }>(apiKey, "opListTenants").then((r) => {
      if (r.ok) {
        setModules(r.data.modules);
        // Галочка по умолчанию = defaultOn модуля из реестра, а не «всё включено».
        // Иначе модуль, который в реестре помечен «включается клиенту осознанно»
        // (обязательный профиль, KPI по показателям), приезжал бы включённым
        // каждому новому клиенту — то есть реестр говорил бы одно, а онбординг
        // делал другое.
        // 28.09: инвентаризация, график и ТТК у НОВОГО клиента включены сразу
        // (`newTenant` в реестре), хотя у действующих отсутствие ключа — «выкл».
        setMods(Object.fromEntries(r.data.modules.map((m) => [m.key, m.newTenant ?? m.defaultOn !== false])));
      }
    });
    void reloadGroups();
  }, [apiKey, reloadGroups]);

  useEffect(() => {
    if (mode === "new-network") setGroupId("");
  }, [mode]);

  const selectedGroup = groups.find((g) => g.id === groupId);
  const groupHasOwner = mode === "existing-network" && groupOwnerReady(selectedGroup);
  useEffect(() => {
    if (groupHasOwner) setFromGroup(true);
  }, [groupHasOwner]);

  // Рестораны выбранной сети — из кого копировать настройку.
  const [siblings, setSiblings] = useState<{ id: string; name: string }[]>([]);
  useEffect(() => {
    if (mode !== "existing-network" || !groupId) {
      setSiblings([]);
      setCopyFrom("");
      return;
    }
    const g = groups.find((x) => x.id === groupId);
    // Имена берём из общего списка клиентов — второго запроса ради двух строк
    // не нужно: `opListTenants` уже загружен на этом экране выше.
    opApi<{ tenants: TenantRow[] }>(apiKey, "opListTenants").then((r) => {
      if (!r.ok) return;
      const list = r.data.tenants.filter((t) => !t.archived && g?.tenant_ids.includes(t.id)).map((t) => ({ id: t.id, name: t.name }));
      setSiblings(list);
      setCopyFrom(list[0]?.id ?? "");
    });
  }, [apiKey, mode, groupId, groups]);

  const set = (k: keyof typeof f) => (e: React.ChangeEvent<HTMLInputElement>) => setF((s) => ({ ...s, [k]: e.target.value }));

  // Сеть из одного ресторана — самый частый случай, и заполнять её имя дважды
  // человек не должен: название и адрес ресторана подставляются из сети, пока
  // их не тронули руками.
  function setGroupName(name: string) {
    setNewGroup((s) => ({ ...s, name }));
    setF((s) => (s.name === "" || s.name === newGroup.name ? { ...s, name } : s));
  }
  function setGroupSlug(slug: string) {
    setNewGroup((s) => ({ ...s, slug }));
    setF((s) => (s.slug === "" || s.slug === newGroup.slug ? { ...s, slug } : s));
  }

  async function submit() {
    setBusy(true);
    setErr("");
    const r = await opApi<{ owner: OwnerLite; group: { id: string; slug: string; name: string } | null; tenant: { id: string; slug: string } }>(apiKey, "onboardTenant", {
      tenant: {
        slug: f.slug.trim(),
        name: f.name.trim(),
        // Список модулей не загрузился — не шлём пустой: сервер тогда выключил
        // бы всё, а без списка сам ставит «как у нового клиента» (ревью 28.09).
        modules: modules.length > 0 ? modules.filter((m) => mods[m.key]).map((m) => m.key) : undefined,
        ...(mode === "new-network"
          ? { group: { name: newGroup.name.trim(), slug: newGroup.slug.trim() } }
          : { group_id: groupId }),
      },
      owner:
        mode === "existing-network" && fromGroup
          ? { from_group: true }
          : { name: f.ownerName.trim(), email: f.email.trim() || undefined, phone: f.phone.trim() || undefined, password: f.password },
    });
    if (!r.ok) {
      setBusy(false);
      setErr(r.error);
      return;
    }
    // Копирование — ОТДЕЛЬНЫМ шагом после создания, а не частью той же
    // транзакции: упавшее копирование не должно отменять заведённый ресторан.
    // Оператор увидит, что клиент создан, а настройка не перенеслась, и
    // повторит копирование с его карточки.
    let copied: CloneResult | null = null;
    const parts = CLONE_PARTS.filter((p) => copyParts[p]);
    if (mode === "existing-network" && copyFrom && parts.length > 0) {
      const c = await opApi<CloneResult>(apiKey, "opCopyTenantSetup", { tenant_id: r.data.tenant.id, source_tenant_id: copyFrom, parts });
      if (c.ok) copied = c.data;
      else setErr(`Клиент заведён, но копирование не прошло: ${c.error}`);
    }
    const group = r.data.group ?? (selectedGroup ? { name: selectedGroup.name, slug: selectedGroup.slug } : null);
    setBusy(false);
    await reloadGroups();
    setDone({
      owner: r.data.owner,
      group: r.data.group ? { name: r.data.group.name, slug: r.data.group.slug } : null,
      address: group ? `${group.slug}.klait.ru/${r.data.tenant.slug}` : `${r.data.tenant.slug}.klait.ru`,
      copied,
    });
  }

  if (done) {
    return (
      <div className="op-rise mx-auto max-w-lg">
        <div className={`${card} space-y-4 text-center`}>
          <span className="mx-auto grid h-12 w-12 place-items-center rounded-2xl bg-[var(--op-accent-soft)] text-[var(--op-accent)]"><IcCheck className="h-6 w-6" /></span>
          <div>
            <p className="text-lg font-semibold">Ресторан заведён</p>
            <p className="mt-1 font-mono text-[13px] text-[var(--op-accent)]">{done.address}</p>
            <p className="mt-2 text-sm text-[var(--op-muted)]">
              Владелец <b className="text-[var(--op-fg)]">{done.owner.name}</b> · вход {done.owner.email ?? done.owner.phone} · пароль — тот, что вы задали.
            </p>
            {done.group && (
              <p className="mt-1 text-sm text-[var(--op-muted)]">
                Сеть <b className="text-[var(--op-fg)]">{done.group.name}</b> создана, владелец сети назначен — следующие рестораны подхватят его сами.
              </p>
            )}
            {done.copied && (
              <ul className="mt-3 space-y-1 rounded-2xl bg-[var(--op-ghost)] p-3 text-left text-[13px] text-[var(--op-muted)]">
                <li className="font-medium text-[var(--op-fg)]">Скопировано из «{done.copied.source}»:</li>
                {done.copied.results.map((x) => (
                  <li key={x.part}>· {CLONE_PART_LABELS[x.part]} — {x.note}</li>
                ))}
              </ul>
            )}
          </div>
          <button onClick={onBack} className={`${bPrimary} w-full`}>К списку клиентов</button>
        </div>
      </div>
    );
  }

  const showOwnerFields = !(mode === "existing-network" && fromGroup && groupHasOwner);
  const canSubmit =
    !busy &&
    f.name.trim() &&
    f.slug.trim() &&
    (mode !== "new-network" || (newGroup.name.trim() && newGroup.slug.trim())) &&
    (mode !== "existing-network" || groupId) &&
    (!showOwnerFields || (f.ownerName.trim() && (f.email.trim() || f.phone.trim()) && f.password.length >= 8));

  const netSlug = mode === "new-network" ? newGroup.slug.trim() || "seti" : selectedGroup?.slug ?? "seti";

  return (
    <div className="mx-auto max-w-3xl">
      <header className="mb-5 flex items-center justify-between gap-3">
        <div>
          <h1 className="text-2xl font-bold tracking-tight">Новый ресторан</h1>
          <p className="mt-1 text-sm text-[var(--op-muted)]">
            Каждый ресторан живёт в сети — даже если он в ней один. Второй ресторан того же ресторатора добавляется сюда же, и адрес первого от этого не меняется.
          </p>
        </div>
        <button onClick={onBack} className={`${bGhost} group shrink-0`}><ArrowLeft className={`${motionIcon} h-[18px] w-[18px] group-hover:-translate-x-0.5`} />назад</button>
      </header>

      <div className={`${card} space-y-6`}>
        <div className="space-y-2">
          <span className={lbl}>Куда заводим</span>
          <div className="grid gap-2 sm:grid-cols-2">
            <ModeCard active={mode === "new-network"} onClick={() => setMode("new-network")} title="Новая сеть" hint="Первый ресторан нового ресторатора. Одиночный клиент — это тоже сеть, просто из одного." />
            <ModeCard active={mode === "existing-network"} onClick={() => setMode("existing-network")} title="В существующую сеть" hint="Второй адрес уже заведённого ресторатора: Hide на Патриках → Hide на Большой Никитской." disabled={groups.length === 0} />
          </div>
        </div>

        {mode === "new-network" && (
          <div className="grid gap-4 sm:grid-cols-2">
            <Labeled label="Название сети"><input className={field} value={newGroup.name} onChange={(e) => setGroupName(e.target.value)} placeholder="Пински — или просто «Лаваш», если ресторан один" /></Labeled>
            <Labeled label="Адрес сети"><input className={field} value={newGroup.slug} onChange={(e) => setGroupSlug(e.target.value)} placeholder="pinski (будет pinski.klait.ru)" /></Labeled>
            <p className="text-[12px] text-[var(--op-faint)] sm:col-span-2">
              Адрес сети — домен на всех её рестораны, задаётся один раз. Ресторан внутри открывается как <span className="font-mono">{netSlug}.klait.ru/{f.slug.trim() || "hide"}/</span>, ярлык на телефоне
              у сотрудника один. Владельцем сети станет владелец этого первого ресторана.
            </p>
          </div>
        )}

        {mode === "existing-network" && (
          <Labeled label="Сеть ресторанов">
            <select className={field} value={groupId} onChange={(e) => setGroupId(e.target.value)}>
              <option value="">Выберите сеть…</option>
              {groups.map((g) => (
                <option key={g.id} value={g.id}>{g.name} · {g.slug} · {g.tenants_count} ресторан(а){g.has_owner ? "" : " · нет владельца"}</option>
              ))}
            </select>
            {selectedGroup && (
              <p className="mt-1 text-[12px] text-[var(--op-faint)]">
                {selectedGroup.has_owner
                  ? "У сети есть владелец — новый ресторан наследует его по умолчанию."
                  : "У сети пока не назначен владелец — заполните владельца этого ресторана, он и станет владельцем сети."}
              </p>
            )}
          </Labeled>
        )}

        <div className="grid gap-4 sm:grid-cols-2">
          <Labeled label="Название ресторана"><input className={field} value={f.name} onChange={set("name")} placeholder="Hide на Патриках" /></Labeled>
          <Labeled label="Адрес ресторана в сети">
            <input className={field} value={f.slug} onChange={set("slug")} placeholder={`hide → ${netSlug}.klait.ru/hide/`} />
          </Labeled>
          <p className="text-[12px] text-[var(--op-faint)] sm:col-span-2">
            Точку заводить не нужно — она у ресторана одна и называется как он. Второй адрес бренда заводится отдельным рестораном в этой же сети.
          </p>
          {mode === "existing-network" && groupHasOwner && (
            <label className="flex items-center gap-2 text-sm sm:col-span-2">
              <input type="checkbox" checked={fromGroup} onChange={(e) => setFromGroup(e.target.checked)} />
              <span>Владелец — как у сети: та же учётка, контакты и пароль, что у владельца сети (по умолчанию так)</span>
            </label>
          )}
          {showOwnerFields && (
            <>
              <Labeled label="Имя владельца"><input className={field} value={f.ownerName} onChange={set("ownerName")} placeholder="Ваше имя" /></Labeled>
              <Labeled label="Email владельца"><input className={field} value={f.email} onChange={set("email")} placeholder="owner@example.com" /></Labeled>
              <Labeled label="Телефон (или email)"><input className={field} value={f.phone} onChange={set("phone")} placeholder="+7…" /></Labeled>
              <div className="sm:col-span-2"><Labeled label="Пароль владельца (8+)"><input className={field} type="text" value={f.password} onChange={set("password")} placeholder="пароль владельца (постоянный)" /></Labeled></div>
            </>
          )}
        </div>

        {/* Копирование настройки из соседа по сети — заказ 19.09. Стоит именно
            здесь: заполнять второй Hide с нуля незачем, а вспоминать про
            копирование потом, с карточки, оператор не станет. */}
        {mode === "existing-network" && siblings.length > 0 && (
          <div className="space-y-2 rounded-2xl bg-[var(--op-ghost)] p-4">
            <span className={lbl}>Скопировать настройку из ресторана сети</span>
            <select className={field} value={copyFrom} onChange={(e) => setCopyFrom(e.target.value)}>
              <option value="">Не копировать — заведу с нуля</option>
              {siblings.map((s) => (
                <option key={s.id} value={s.id}>Из «{s.name}»</option>
              ))}
            </select>
            {copyFrom && (
              <>
                <div className="flex flex-wrap gap-2 pt-1">
                  {CLONE_PARTS.map((p) => (
                    <button
                      key={p}
                      type="button"
                      onClick={() => setCopyParts((s) => ({ ...s, [p]: !s[p] }))}
                      title={CLONE_PART_HINTS[p]}
                      className={`rounded-lg px-3 py-1.5 text-xs font-medium transition-colors ${copyParts[p] ? "bg-[var(--op-accent-soft)] text-[var(--op-accent)]" : "bg-[var(--op-surface)] text-[var(--op-muted)] hover:text-[var(--op-fg)]"}`}
                    >
                      {CLONE_PART_LABELS[p]}
                    </button>
                  ))}
                </div>
                <p className="text-[12px] text-[var(--op-faint)]">
                  Копия — это ПЕРВОЕ заполнение, а не связь: дальше рестораны живут врозь, и правка в одном во второй не доедет. Постоянно общее в сети — роли сети, люди и бот.
                </p>
              </>
            )}
          </div>
        )}

        <div className="space-y-2">
          <span className={lbl}>Модули</span>
          <div className="flex flex-wrap gap-2">
            {modules.map((m) => (
              <button
                key={m.key}
                type="button"
                onClick={() => setMods((s) => ({ ...s, [m.key]: !s[m.key] }))}
                className={`rounded-lg px-3 py-1.5 text-xs font-medium transition-colors ${mods[m.key] ? "bg-[var(--op-accent-soft)] text-[var(--op-accent)]" : "bg-[var(--op-ghost)] text-[var(--op-muted)] hover:text-[var(--op-fg)]"}`}
              >
                {m.label}
              </button>
            ))}
          </div>
        </div>
        {err && <p className="text-sm text-[var(--op-danger-text)]">{err}</p>}
        <button disabled={!canSubmit} onClick={submit} className={bPrimary}>{busy ? "Создаю…" : "Завести ресторан"}</button>
      </div>
    </div>
  );
}

/** Одна карточка выбора «куда заводим» в онбординге (17.09, переписана 19.09). */
function ModeCard({ active, onClick, title, hint, disabled = false }: { active: boolean; onClick: () => void; title: string; hint: string; disabled?: boolean }) {
  return (
    <button
      type="button"
      disabled={disabled}
      onClick={onClick}
      className={`rounded-2xl px-4 py-3 text-left transition-colors ${
        active
          ? "bg-[var(--op-accent-soft)] text-[var(--op-accent)] ring-2 ring-[var(--op-accent)]"
          : disabled
          ? "bg-[var(--op-ghost)] text-[var(--op-faint)]"
          : "bg-[var(--op-ghost)] text-[var(--op-fg)] hover:text-[var(--op-fg)]"
      }`}
    >
      <div className="text-sm font-semibold">{title}</div>
      <div className="mt-0.5 text-[11px] text-[var(--op-faint)]">{hint}</div>
    </button>
  );
}

function Labeled({ label, children }: { label: string; children: ReactNode }) {
  return (
    <label className="block space-y-1.5">
      <span className={lbl}>{label}</span>
      {children}
    </label>
  );
}

// ── детально по клиенту ──
type DetailTab = "main" | "venues" | "people" | "shifts" | "data" | "stock" | "telegram";

// Вкладка «Точки» — УСЛОВНАЯ (19.09). У ресторана точка одна, и целый раздел
// под неё это раздел про ничего. Показывается только тем клиентам, у кого
// точек осталось несколько, и называется по делу: их надо не заводить, а
// разобрать — вынести в отдельные рестораны сети.
function detailTabs(venues: number): { key: DetailTab; title: string }[] {
  return [
    { key: "main", title: "Ресторан" },
    ...(venues > 1 ? ([{ key: "venues", title: "Старые точки" }] as const) : []),
    { key: "people", title: "Люди и права" },
    { key: "shifts", title: "Смены" },
    { key: "data", title: "Данные" },
    // Склад (23.09): инвентаризации клиента — только просмотр, без входа
    // под его учёткой.
    { key: "stock", title: "Склад" },
    { key: "telegram", title: "Telegram" },
  ];
}

function Detail({ apiKey, tenantId, onBack, onOpen }: { apiKey: string; tenantId: string; onBack: () => void; onOpen: (id: string) => void }) {
  const [d, setD] = useState<TenantDetail | null>(null);
  const [err, setErr] = useState("");
  const [tab, setTab] = useState<DetailTab>("main");
  const tabs = detailTabs(d?.tenant.venues.length ?? 1);
  // Вкладка «Старые точки» может исчезнуть прямо под руками — последнюю точку
  // вынесли в отдельный ресторан. Возвращаемся на «Ресторан», а не показываем
  // пустоту под несуществующей пилюлей.
  const shownTab = tabs.some((t) => t.key === tab) ? tab : "main";

  const load = useCallback(async () => {
    const r = await opApi<TenantDetail>(apiKey, "opGetTenant", { tenant_id: tenantId });
    if (r.ok) setD(r.data);
    else setErr(r.error);
  }, [apiKey, tenantId]);

  useEffect(() => {
    load();
  }, [load]);

  if (err) return <div className={`${card} text-sm text-[var(--op-danger-text)]`}>{err}</div>;
  if (!d) return <div className="h-40 animate-pulse rounded-[20px] border border-[var(--op-line)] bg-[var(--op-surface-2)]" />;

  return (
    <div>
      <header className="mb-4 flex flex-wrap items-center justify-between gap-3">
        <div className="flex min-w-0 items-center gap-3">
          <Avatar name={d.tenant.name} logo={d.tenant.logo} className="h-12 w-12 text-base" />
          <div className="min-w-0">
            <h1 className="truncate text-xl font-bold tracking-tight sm:text-2xl">{d.tenant.name}</h1>
            <p className="flex flex-wrap items-center gap-x-2 text-[13px] text-[var(--op-faint)]">
              {/* ПОЛНЫЙ адрес, а не голый слаг (19.09): на сетевом домене
                  ресторан открывается как `pinski.klait.ru/hide`, и слаг сам
                  по себе на вопрос «куда идти» не отвечает. */}
              <span className="truncate font-mono text-[var(--op-accent)]">
                {d.tenant.group && d.tenant.group.domain_enabled !== false ? `${d.tenant.group.slug}.klait.ru/${d.tenant.slug}` : `${d.tenant.slug}.klait.ru`}
              </span>
              {/* Ресторан сети видно сразу: иначе ему заведут второй бот и свой
                  тариф, хотя и то и другое задаётся на сеть ресторанов. */}
              {d.tenant.group && (
                <span className="inline-flex items-center gap-1 rounded-lg bg-[var(--op-accent-soft)] px-2 py-0.5 text-[11px] font-medium text-[var(--op-accent)]">
                  <IcFolder className="h-3 w-3" /> {d.tenant.group.name}
                </span>
              )}
            </p>
          </div>
        </div>
        <button onClick={onBack} className={`${bGhost} group`}><ArrowLeft className={`${motionIcon} h-[18px] w-[18px] group-hover:-translate-x-0.5`} />к списку</button>
      </header>

      {/* Разделы вместо одного полотна (редизайн 10.09).
          Раньше карточка вываливала всё сразу: модули, Telegram, права ролей,
          заведение, локации, сотрудники, смены, данные точки — восемь тяжёлых
          блоков подряд, и до нужного надо было листать через все остальные.
          Содержимое НЕ менялось, изменилось только то, сколько его показано
          за раз. Состояние вкладки местное: возвращаясь из списка, оператор
          чаще всего идёт в тот же раздел, но помнить это между клиентами
          неправильно — у другого клиента и повод другой. */}
      <PillRow items={tabs} value={shownTab} onChange={setTab} className="mb-4" ariaLabel="Разделы карточки клиента" />

      {shownTab === "main" && (
        <div className="space-y-4">
          {/* Соседи по сети — строкой над карточками: «где я в сети» это
              первый вопрос, с которым сюда приходят, и ответ на него не
              должен лежать во вкладке. */}
          {d.tenant.group && d.siblings.length > 0 && (
            <section className={`${card} flex flex-wrap items-center gap-2`}>
              <span className={lbl}>Рядом в сети «{d.tenant.group.name}»</span>
              {d.siblings.map((sib) => (
                <button key={sib.id} onClick={() => onOpen(sib.id)} className={bSmall} title={`Открыть ${sib.name}`}>
                  {sib.name}
                </button>
              ))}
            </section>
          )}
          {/* Заполнение по образцу — сразу под соседями: ради этого в карточку
              нового ресторана сети чаще всего и заходят. */}
          {d.siblings.length > 0 && <CopyFromCard apiKey={apiKey} tenantId={tenantId} siblings={d.siblings} onDone={load} />}
          <div className="grid grid-cols-1 gap-4 lg:grid-cols-2">
            {/* onSaved — перечитать карточку после смены логотипа, чтобы превью
                и шапка обновились без перезагрузки страницы. */}
            <TenantAdmin apiKey={apiKey} tenantId={tenantId} name={d.tenant.name} slug={d.tenant.slug} logo={d.tenant.logo} monthlyFee={d.tenant.monthly_fee} aiDailyLimit={d.tenant.ai_daily_limit ?? null} aiLimitEffective={d.tenant.ai_daily_limit_effective ?? null} aiDailyTokens={d.tenant.ai_daily_tokens ?? null} aiTokensEffective={d.tenant.ai_daily_tokens_effective ?? null} aiUsedToday={d.tenant.ai_used_today ?? 0} aiTokensToday={d.tenant.ai_tokens_today ?? 0} aiRubPerKtoken={d.tenant.ai_rub_per_ktoken ?? null} demoStand={d.tenant.demo_stand} venues={d.tenant.venues} onSaved={load} onArchived={onBack} />
            <FlagsCard apiKey={apiKey} tenantId={tenantId} detail={d} />
            <PaySourceOpCard apiKey={apiKey} tenantId={tenantId} />
          </div>
        </div>
      )}

      {/* Старые точки: не заводим, а разбираем — выносим в отдельные рестораны
          сети либо закрываем (19.09). */}
      {shownTab === "venues" && <VenuesCard apiKey={apiKey} tenantId={tenantId} tenantSlug={d.tenant.slug} inGroup={Boolean(d.tenant.group)} onChanged={load} onOpen={onOpen} />}

      {/* space-y, а не голые соседи: EmployeesCard и RolesCard своих внешних
          отступов не имеют, и в прежней раскладке блоки стояли встык. */}
      {shownTab === "people" && (
        <div className="space-y-4">
          <EmployeesCard
            apiKey={apiKey}
            tenantId={tenantId}
            venues={d.tenant.venues}
            roles={d.roles}
            appUrl={`https://${d.tenant.group && d.tenant.group.domain_enabled !== false ? `${d.tenant.group.slug}.klait.ru/${d.tenant.slug}` : `${d.tenant.slug}.klait.ru`}/app`}
          />
          {/* Роли и их права — один блок: слева список, справа тумблеры выбранной. */}
          <RolesCard apiKey={apiKey} tenantId={tenantId} capabilities={d.capabilities} onChanged={load} />
        </div>
      )}

      {shownTab === "shifts" && <ShiftsCard apiKey={apiKey} tenantId={tenantId} venues={d.tenant.venues} />}
      {shownTab === "data" && <VenueData apiKey={apiKey} tenantId={tenantId} venues={d.tenant.venues} />}
      {shownTab === "stock" && <VenueStock apiKey={apiKey} tenantId={tenantId} venues={d.tenant.venues} />}
      {shownTab === "telegram" && <TelegramCard apiKey={apiKey} tenantId={tenantId} detail={d} />}
    </div>
  );
}

// ── Склад клиента: инвентаризации, только просмотр (23.09) ──
//
// Марат искал инвентаризации клиента в консоли и не находил: они жили только
// в кабинете заведения, а входа «как владелец» нет. Свод — тот же компонент,
// что у владельца на десктопе, чтобы оператор видел ровно то же самое.
type OpInvSession = {
  id: string;
  name: string;
  startedAt: string | null;
  closedAt: string | null;
  finished: boolean;
  isActive: boolean;
  startedBy: string | null;
  positions: number;
};

function VenueStock({ apiKey, tenantId, venues }: { apiKey: string; tenantId: string; venues: VenueLite[] }) {
  const [venueId, setVenueId] = useState(venues[0]?.id ?? "");
  const [rows, setRows] = useState<OpInvSession[] | null>(null);
  const [err, setErr] = useState("");
  const [opened, setOpened] = useState<string | null>(null);

  useEffect(() => {
    if (!venueId) return;
    let alive = true;
    setRows(null);
    setOpened(null);
    opApi<{ sessions: OpInvSession[]; total: number }>(apiKey, "opListInventories", { tenant_id: tenantId, venue_id: venueId }).then((r) => {
      if (!alive) return;
      if (r.ok) setRows(r.data.sessions);
      else setErr(r.error);
    });
    return () => {
      alive = false;
    };
  }, [apiKey, tenantId, venueId]);

  const fmt = (iso: string | null) =>
    iso ? new Date(iso).toLocaleString("ru-RU", { day: "2-digit", month: "2-digit", year: "2-digit", hour: "2-digit", minute: "2-digit" }) : "—";

  if (venues.length === 0) return <section className={`${card} text-sm text-[var(--op-muted)]`}>У заведения нет точек.</section>;

  return (
    <section className={`${card} space-y-4`}>
      <div className="flex flex-wrap items-center justify-between gap-3">
        <CardHead title="Инвентаризации" />
        {venues.length > 1 ? (
          <select className={`${field} w-auto`} value={venueId} onChange={(e) => setVenueId(e.target.value)}>
            {venues.map((v) => <option key={v.id} value={v.id}>{v.name}</option>)}
          </select>
        ) : (
          <Pill>{venues[0].name}</Pill>
        )}
      </div>
      <p className="text-[12px] text-[var(--op-muted)]">Только просмотр: здесь ничего не меняется. Отправить в iiko или скачать бланк может владелец в своём кабинете.</p>
      {err && <p className="text-sm text-[var(--op-danger-text)]">{err}</p>}
      {!rows ? (
        <p className="text-sm text-[var(--op-muted)]">Загружаю…</p>
      ) : rows.length === 0 ? (
        <p className="text-sm text-[var(--op-muted)]">Инвентаризаций в этой точке пока нет.</p>
      ) : (
        <div className="space-y-2">
          {rows.map((r) => (
            <div key={r.id} className="rounded-2xl bg-[var(--op-surface-2)]">
              <button
                className="flex w-full flex-wrap items-baseline gap-x-3 gap-y-1 px-4 py-3 text-left"
                onClick={() => setOpened(opened === r.id ? null : r.id)}
                aria-expanded={opened === r.id}
              >
                <span className="text-[15px] font-semibold text-[var(--op-fg)]">{r.name}</span>
                {r.isActive && <span className="rounded-full bg-[var(--op-ghost)] px-2 py-0.5 text-[11px] font-semibold text-[var(--op-fg)]">идёт сейчас</span>}
                <span className="text-[12px] text-[var(--op-muted)]">
                  начата {fmt(r.startedAt)}
                  {r.finished ? ` · завершена ${fmt(r.closedAt)}` : ""}
                  {r.startedBy ? ` · ${r.startedBy}` : ""} · позиций {r.positions}
                </span>
              </button>
              {opened === r.id && (
                <div className="px-3 pb-3">
                  <InventorySheetView
                    sessionId={r.id}
                    fetcher={(sessionId) =>
                      opApi<InventorySheet>(apiKey, "opGetInventorySheet", { tenant_id: tenantId, venue_id: venueId, sessionId })
                    }
                  />
                </div>
              )}
            </div>
          ))}
        </div>
      )}
    </section>
  );
}

// ── Заполнить по образцу (19.09) ──
//
// Заказ пользователя: «скопировал бы роли, доступы, меню из Hide на Патриках в
// Hide на Большой Никитской». Прямое следствие решения «только логика сети»:
// второй адрес бренда теперь отдельный ресторан, и без переноса его пришлось
// бы набивать с нуля.
//
// Стоит в карточке ресторана, а не только в онбординге: копируют не всегда
// сразу — бывает, ресторан завели, а меню в образце доделали через неделю.
function CopyFromCard({
  apiKey,
  tenantId,
  siblings,
  onDone,
}: {
  apiKey: string;
  tenantId: string;
  siblings: SiblingRow[];
  onDone: () => void;
}) {
  const [open, setOpen] = useState(false);
  const [from, setFrom] = useState(siblings[0]?.id ?? "");
  const [parts, setParts] = useState<Record<string, boolean>>(Object.fromEntries(CLONE_PARTS.map((p) => [p, true])));
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");
  const [res, setRes] = useState<CloneResult | null>(null);

  const chosen = CLONE_PARTS.filter((p) => parts[p]);
  const source = siblings.find((s) => s.id === from) ?? null;

  async function run() {
    if (!source) return;
    if (!confirm(`Скопировать настройку из «${source.name}»? Совпадающее по названию не трогается, но модули и права ролей будут заменены на те, что в образце.`)) return;
    setBusy(true);
    setErr("");
    setRes(null);
    const r = await opApi<CloneResult>(apiKey, "opCopyTenantSetup", { tenant_id: tenantId, source_tenant_id: from, parts: chosen });
    setBusy(false);
    if (!r.ok) {
      setErr(r.error);
      return;
    }
    setRes(r.data);
    onDone();
  }

  return (
    <section className={`${card} space-y-3`}>
      <CardHead
        title="Заполнить по образцу"
        right={
          <button onClick={() => setOpen((v) => !v)} className="op-quiet inline-flex items-center gap-1.5 rounded-full px-2 py-1 text-[13px] font-semibold text-[var(--op-muted)] hover:bg-[var(--op-ghost)] hover:text-[var(--op-fg)]">
            <IcChevron open={open} /> {open ? "свернуть" : "открыть"}
          </button>
        }
      />
      <p className="text-[13px] text-[var(--op-muted)]">
        Перенести роли, меню, чек-листы и настройки из другого ресторана этой сети. Это ПЕРВОЕ заполнение, а не связь: дальше рестораны живут врозь. Постоянно общее в сети — роли сети, люди и бот.
      </p>

      {open && (
        <>
          <div className="grid gap-2 sm:grid-cols-2">
            <select className={field} value={from} onChange={(e) => setFrom(e.target.value)}>
              {siblings.map((s) => (
                <option key={s.id} value={s.id}>
                  Из «{s.name}» — {s.recipes} в меню, {s.items} на складе, {s.roles} ролей
                </option>
              ))}
            </select>
            <button className={bPrimary} disabled={busy || !from || chosen.length === 0} onClick={run}>
              {busy ? "Копирую…" : "Скопировать"}
            </button>
          </div>
          <div className="flex flex-wrap gap-2">
            {CLONE_PARTS.map((p) => (
              <button
                key={p}
                type="button"
                onClick={() => setParts((s) => ({ ...s, [p]: !s[p] }))}
                title={CLONE_PART_HINTS[p]}
                className={`rounded-lg px-3 py-1.5 text-xs font-medium transition-colors ${parts[p] ? "bg-[var(--op-accent-soft)] text-[var(--op-accent)]" : "bg-[var(--op-ghost)] text-[var(--op-muted)] hover:text-[var(--op-fg)]"}`}
              >
                {CLONE_PART_LABELS[p]}
              </button>
            ))}
          </div>
          {err && <p className="text-[13px] text-[var(--op-danger-text)]">{err}</p>}
          {res && (
            <ul className="space-y-1 rounded-2xl bg-[var(--op-ghost)] p-3 text-[13px] text-[var(--op-muted)]">
              <li className="font-medium text-[var(--op-fg)]">Скопировано из «{res.source}»:</li>
              {res.results.map((x) => (
                <li key={x.part}>· {CLONE_PART_LABELS[x.part]} — {x.note}</li>
              ))}
            </ul>
          )}
        </>
      )}
    </section>
  );
}

// ── Старые точки ──
//
// Точек внутри ресторана больше НЕ ЗАВОДЯТ (19.09, решение пользователя
// «оставим только логику сети»): второй адрес бренда — отдельный ресторан в
// сети. Этот раздел показывается только тем клиентам, у кого точек осталось
// несколько, и нужен, чтобы их разобрать:
//
//  · «Вынести в ресторан» — точка со всеми своими данными (меню, склад,
//    смены, люди, задачи, чек-листы) становится самостоятельным рестораном
//    той же сети. НЕОБРАТИМО, поэтому подтверждение слагом заведения;
//  · «Закрыть» — как раньше: доступы снимаются, данные остаются в базе.
function VenuesCard({
  apiKey,
  tenantId,
  tenantSlug,
  inGroup,
  onChanged,
  onOpen,
}: {
  apiKey: string;
  tenantId: string;
  tenantSlug: string;
  inGroup: boolean;
  onChanged: () => void;
  onOpen: (id: string) => void;
}) {
  const [rows, setRows] = useState<
    { id: string; name: string; slug: string; address: string | null; employees: number }[] | null
  >(null);
  const [split, setSplit] = useState<null | { venueId: string; venueName: string; name: string; slug: string; confirm: string }>(null);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");
  const [done, setDone] = useState<null | { name: string; moved: string[]; copied: string[]; rows: number; tenantId: string }>(null);

  const load = useCallback(async () => {
    const r = await opApi<{ venues: typeof rows }>(apiKey, "opListVenues", { tenant_id: tenantId });
    if (r.ok) setRows(r.data.venues);
    else setErr(r.error);
  }, [apiKey, tenantId]);

  useEffect(() => {
    load();
  }, [load]);

  async function archive(id: string, label: string) {
    if (
      !confirm(
        `Закрыть точку «${label}»? Доступы сотрудников к ней снимутся. Смены, инвентаризации и списания останутся в базе — отчётность за прошлые месяцы сойдётся.`,
      )
    ) {
      return;
    }
    setBusy(true);
    setErr("");
    const r = await opApi(apiKey, "opArchiveVenue", { tenant_id: tenantId, venueId: id });
    setBusy(false);
    if (!r.ok) {
      setErr(r.error);
      return;
    }
    await load();
    onChanged();
  }

  async function runSplit() {
    if (!split) return;
    setBusy(true);
    setErr("");
    const r = await opApi<{
      tenant: { id: string; slug: string; name: string };
      moved: { name: string }[];
      copied: { name: string }[];
      rows: number;
    }>(apiKey, "opSplitVenueToTenant", {
      tenant_id: tenantId,
      venue_id: split.venueId,
      name: split.name.trim(),
      slug: split.slug.trim(),
      confirm_slug: split.confirm.trim(),
    });
    setBusy(false);
    if (!r.ok) {
      setErr(r.error);
      return;
    }
    setDone({
      name: r.data.tenant.name,
      moved: r.data.moved.map((x) => x.name),
      copied: r.data.copied.map((x) => x.name),
      rows: r.data.rows,
      tenantId: r.data.tenant.id,
    });
    setSplit(null);
    await load();
    onChanged();
  }

  return (
    <section className={`${card} mb-4 space-y-3`}>
      <CardHead title="Старые точки" />

      <p className="text-[13px] text-[var(--op-muted)]">
        У этого ресторана несколько точек — так заводили до 19.09. Новых точек больше не создают: второй адрес бренда это отдельный ресторан в сети. Точку можно вынести в такой ресторан — со всеми
        её данными — либо закрыть.
      </p>

      {!rows && <p className="text-[13px] text-[var(--op-muted)]">Загрузка…</p>}

      {rows && (
        <ul className="space-y-2">
          {rows.map((v, i) => (
            <li key={v.id} className="flex flex-wrap items-center gap-2 rounded-2xl bg-[var(--op-surface-2)] px-3 py-2.5 text-[13px]">
              <span className="flex-1 truncate text-[var(--op-fg)]">
                <b className="font-semibold">{v.name}</b>
                <span className="ml-1.5 font-mono text-[var(--op-accent)]">/{v.slug}</span>
                <span className="ml-1.5 text-[var(--op-faint)]">
                  {v.address || "адрес не указан"} · {v.employees} чел.
                </span>
              </span>
              {/* Первую точку не выносим: она и есть сам ресторан. Вынести её
                  значило бы оставить исходное заведение пустой оболочкой. */}
              {i > 0 && (
                <button
                  disabled={busy}
                  onClick={() => setSplit({ venueId: v.id, venueName: v.name, name: v.name, slug: v.slug, confirm: "" })}
                  className={bSmall}
                  title="Сделать из точки отдельный ресторан этой сети"
                >
                  Вынести в ресторан
                </button>
              )}
              {rows.length > 1 && (
                <button disabled={busy} onClick={() => archive(v.id, v.name)} className={`${bGhost} h-8 px-3 text-[13px]`} title="Закрыть точку">
                  Закрыть
                </button>
              )}
            </li>
          ))}
        </ul>
      )}

      {err && <p className="text-[13px] text-[var(--op-danger-text)]">{err}</p>}

      {done && (
        <div className="space-y-2 rounded-2xl bg-[var(--op-accent-soft)] p-4 text-[13px] text-[var(--op-fg)]">
          <p className="font-semibold">Точка вынесена в ресторан «{done.name}»</p>
          <p className="text-[var(--op-muted)]">Перенесено строк: {done.rows}.</p>
          {done.moved.length > 0 && <p className="text-[var(--op-muted)]">Переехали целиком: {done.moved.join(", ")}.</p>}
          {done.copied.length > 0 && (
            <p className="text-[var(--op-muted)]">Работают в обоих ресторанах (учётка заведена и там, и там): {done.copied.join(", ")}.</p>
          )}
          <button className={bSmall} onClick={() => onOpen(done.tenantId)}>Открыть «{done.name}»</button>
        </div>
      )}

      {split && (
        <div className="space-y-3 rounded-2xl border border-[var(--op-line-strong)] bg-[var(--op-surface-2)] p-4">
          <p className="text-[15px] font-semibold">Вынести «{split.venueName}» в отдельный ресторан</p>
          {!inGroup ? (
            <p className="text-[13px] text-[var(--op-danger-text)]">
              Сначала добавьте этот ресторан в сеть — вынесенная точка станет рестораном той же сети. Сделать это можно в списке клиентов: перетащите карточку в сеть.
            </p>
          ) : (
            <>
              <p className="text-[13px] text-[var(--op-muted)]">
                Переедут меню, склад, смены, задачи, чек-листы и накладные этой точки. Сотрудники, работающие только здесь, переедут вместе с ней; работающие и в других точках получат вторую учётку,
                связанную с первой. Владелец останется в обоих. <b className="text-[var(--op-fg)]">Отменить это будет нельзя.</b>
              </p>
              <div className="grid gap-2 sm:grid-cols-2">
                <Labeled label="Название нового ресторана">
                  <input className={field} value={split.name} onChange={(e) => setSplit({ ...split, name: e.target.value })} />
                </Labeled>
                <Labeled label="Адрес в сети">
                  <input className={field} value={split.slug} onChange={(e) => setSplit({ ...split, slug: e.target.value })} />
                </Labeled>
                <div className="sm:col-span-2">
                  <Labeled label={`Для подтверждения наберите адрес этого заведения: ${tenantSlug}`}>
                    <input className={field} value={split.confirm} onChange={(e) => setSplit({ ...split, confirm: e.target.value })} placeholder={tenantSlug} />
                  </Labeled>
                </div>
              </div>
              <div className="flex flex-wrap gap-2">
                <button className={bDangerSolid} disabled={busy || split.confirm.trim() !== tenantSlug || !split.name.trim() || !split.slug.trim()} onClick={runSplit}>
                  {busy ? "Переношу…" : "Вынести в ресторан"}
                </button>
                <button className={bGhost} disabled={busy} onClick={() => setSplit(null)}>
                  Отмена
                </button>
              </div>
            </>
          )}
        </div>
      )}
    </section>
  );
}

// ── Смены заведения (остаток среза 3a) ──
//
// Зачем это в консоли: при поддержке мы всё равно ведём клиента по экрану
// голосом — «нажмите там, выберите тут». Поставить смену за него быстрее и
// надёжнее, особенно на онбординге, когда график пустой и человек не понимает,
// с чего начать.
//
// Показываем ПЛОСКИЙ список по дням, а не календарь: оператор приходит сюда с
// конкретным вопросом («на завтра никого нет»), а не планировать месяц.

type OpShift = {
  id: string;
  date: string;
  kind: string;
  zone: string;
  capacity: number;
  booked: number;
  claims: { userId: string; userName: string; status: string }[];
};

const SHIFT_ZONES: { k: string; label: string }[] = [
  { k: "bar", label: "Бар" },
  { k: "hall", label: "Зал" },
  { k: "kitchen", label: "Кухня" },
  { k: "housekeeping", label: "Уборка" },
];

const SHIFT_KINDS: { k: string; label: string }[] = [
  { k: "day", label: "День" },
  { k: "night", label: "Ночь" },
];

function ymNow(): string {
  return new Date().toISOString().slice(0, 7);
}

function shiftMonth(ym: string, delta: number): string {
  const [y, m] = ym.split("-").map(Number);
  const d = new Date(Date.UTC(y, m - 1 + delta, 1));
  return d.toISOString().slice(0, 7);
}

function ShiftsCard({ apiKey, tenantId, venues }: { apiKey: string; tenantId: string; venues: VenueLite[] }) {
  const [venueId, setVenueId] = useState(venues[0]?.id ?? "");
  const [ym, setYm] = useState(ymNow());
  const [rows, setRows] = useState<OpShift[] | null>(null);
  const [people, setPeople] = useState<EmployeeRow[]>([]);
  const [err, setErr] = useState("");
  const [busy, setBusy] = useState("");
  const [addOpen, setAddOpen] = useState(false);
  const [nf, setNf] = useState({ date: "", kind: "day", zone: "bar", capacity: "1" });

  const load = useCallback(async () => {
    setErr("");
    const [s, e] = await Promise.all([
      opApi<{ shifts: OpShift[] }>(apiKey, "opGetSchedule", { tenant_id: tenantId, venue_id: venueId, yearMonth: ym }),
      opApi<{ employees: EmployeeRow[] }>(apiKey, "opListEmployees", { tenant_id: tenantId }),
    ]);
    if (s.ok) setRows(s.data.shifts); else { setErr(s.error); setRows([]); }
    if (e.ok) setPeople(e.data.employees.filter((x) => x.is_active));
  }, [apiKey, tenantId, venueId, ym]);

  useEffect(() => { if (venueId) load(); }, [load, venueId]);

  async function act(action: string, payload: Record<string, unknown>, key: string) {
    setBusy(key); setErr("");
    const r = await opApi(apiKey, action, { tenant_id: tenantId, venue_id: venueId, ...payload });
    setBusy("");
    if (r.ok) load(); else setErr(r.error);
  }

  async function add() {
    if (!nf.date) { setErr("Выберите дату смены"); return; }
    setBusy("add"); setErr("");
    const r = await opApi(apiKey, "opCreateShift", {
      tenant_id: tenantId, venue_id: venueId,
      date: nf.date, kind: nf.kind, zone: nf.zone, capacity: Number(nf.capacity) || 1,
    });
    setBusy("");
    if (!r.ok) { setErr(r.error); return; }
    setAddOpen(false);
    setNf({ date: "", kind: "day", zone: "bar", capacity: "1" });
    load();
  }

  const sel = "rounded-lg border border-[var(--op-line-2)] bg-[var(--op-surface)] px-2 py-1 text-xs text-[var(--op-fg)] outline-none focus:border-[var(--op-accent)]";

  // Люди этой точки: назначать на смену бармена соседнего заведения нельзя,
  // и показывать его в списке — значит предлагать ошибку.
  const venuePeople = people.filter((p) => p.memberships.some((m) => m.venue_id === venueId));

  return (
    <section className={`${card} space-y-3`}>
      <CardHead
        title="Смены"
        right={
          <div className="flex items-center gap-1.5">
            {venues.length > 1 && (
              <select className={sel} value={venueId} onChange={(e) => setVenueId(e.target.value)}>
                {venues.map((v) => <option key={v.id} value={v.id}>{v.name}</option>)}
              </select>
            )}
            <button className={sel} onClick={() => setYm(shiftMonth(ym, -1))} aria-label="Предыдущий месяц"><ArrowLeft className="h-4 w-4" /></button>
            <span className="px-1 text-xs tabular-nums text-[var(--op-muted)]">{ym}</span>
            <button className={sel} onClick={() => setYm(shiftMonth(ym, 1))} aria-label="Следующий месяц"><ArrowRight className="h-4 w-4" /></button>
          </div>
        }
      />

      {err && <p className="rounded-lg bg-[var(--op-danger-soft)] px-3 py-2 text-xs text-[var(--op-danger-text)]">{err}</p>}
      {!rows && <p className="text-xs text-[var(--op-muted)]">Загрузка…</p>}

      {rows && rows.length === 0 && (
        <p className="text-xs text-[var(--op-muted)]">В этом месяце смен нет.</p>
      )}

      {rows && rows.length > 0 && (
        <div className="space-y-1.5">
          {rows.map((s) => (
            <div key={s.id} className="rounded-xl border border-[var(--op-line)] bg-[var(--op-surface-2)] p-3">
              <div className="flex flex-wrap items-center justify-between gap-2">
                <div className="text-sm text-[var(--op-fg)]">
                  <span className="font-semibold tabular-nums">{s.date.slice(8)}.{s.date.slice(5, 7)}</span>
                  {" · "}
                  {SHIFT_KINDS.find((k) => k.k === s.kind)?.label ?? s.kind}
                  {" · "}
                  {SHIFT_ZONES.find((z) => z.k === s.zone)?.label ?? s.zone}
                  <span className="pl-2 text-xs text-[var(--op-muted)]">{s.booked} из {s.capacity}</span>
                </div>
                <button
                  className={`${bDanger} px-2.5 py-1 text-xs`}
                  disabled={busy === s.id}
                  onClick={() => {
                    if (!confirm(`Удалить смену ${s.date}? Назначения снимутся.`)) return;
                    act("opDeleteShift", { shiftId: s.id }, s.id);
                  }}
                >
                  Удалить
                </button>
              </div>

              <div className="flex flex-wrap items-center gap-1.5 pt-2">
                {s.claims.map((c) => (
                  <span
                    key={c.userId}
                    className="inline-flex items-center gap-1 rounded-full bg-[var(--op-ghost)] px-2 py-0.5 text-xs text-[var(--op-fg)]"
                  >
                    {c.userName}
                    <button
                      className="text-[var(--op-faint)] hover:text-[var(--op-danger-text)]"
                      aria-label={`Снять ${c.userName}`}
                      disabled={busy === s.id + c.userId}
                      onClick={() => act("opUnassignShift", { shiftId: s.id, userId: c.userId }, s.id + c.userId)}
                    >
                      <CloseIcon className="h-3 w-3" />
                    </button>
                  </span>
                ))}

                {s.booked < s.capacity && venuePeople.length > 0 && (
                  <select
                    className={sel}
                    value=""
                    disabled={busy === s.id + "assign"}
                    onChange={(e) => {
                      if (!e.target.value) return;
                      act("opAssignShift", { shiftId: s.id, userId: e.target.value }, s.id + "assign");
                    }}
                  >
                    <option value="">+ назначить</option>
                    {venuePeople
                      .filter((p) => !s.claims.some((c) => c.userId === p.id))
                      .map((p) => <option key={p.id} value={p.id}>{p.name}</option>)}
                  </select>
                )}
              </div>
            </div>
          ))}
        </div>
      )}

      {!addOpen ? (
        <button className={`${bGhost} py-1.5 text-xs`} onClick={() => setAddOpen(true)}>+ Смена</button>
      ) : (
        <div className="space-y-2 rounded-xl border border-[var(--op-line)] bg-[var(--op-surface-2)] p-3">
          <div className="grid gap-2 sm:grid-cols-4">
            <DateField value={nf.date} onChange={(v) => setNf({ ...nf, date: v })} ariaLabel="Дата смены" clearable={false} />
            <select className={field} value={nf.kind} onChange={(e) => setNf({ ...nf, kind: e.target.value })}>
              {SHIFT_KINDS.map((k) => <option key={k.k} value={k.k}>{k.label}</option>)}
            </select>
            <select className={field} value={nf.zone} onChange={(e) => setNf({ ...nf, zone: e.target.value })}>
              {SHIFT_ZONES.map((z) => <option key={z.k} value={z.k}>{z.label}</option>)}
            </select>
            <input className={field} inputMode="numeric" value={nf.capacity} onChange={(e) => setNf({ ...nf, capacity: e.target.value })} placeholder="мест" />
          </div>
          <div className="flex gap-2">
            <button className={`${bPrimary} py-1.5 text-xs`} disabled={busy === "add"} onClick={add}>Создать</button>
            <button className={`${bGhost} py-1.5 text-xs`} onClick={() => setAddOpen(false)}>Отмена</button>
          </div>
        </div>
      )}
    </section>
  );
}

// ── Сотрудники заведения (слой «оператор = владелец точки») ──
//
// Роли берутся из ЗАВЕДЕНИЯ, а не из списка в коде: клиент заводит свои
// («су-шеф», «хостес»), и захардкоженный перечень их бы просто не показал.
// Зона определяет, чьи смены человек видит в графике и какое меню ему показывать.
const EMP_ZONES: { k: string; label: string }[] = [
  { k: "bar", label: "Бар" },
  { k: "hall", label: "Зал" },
  { k: "kitchen", label: "Кухня" },
  { k: "housekeeping", label: "Уборка" },
];

type EmployeeRow = {
  id: string;
  name: string;
  email: string | null;
  phone: string | null;
  is_active: boolean;
  memberships: {
    id: string;
    role: string;
    role_id: string | null;
    role_name: string;
    role_level: number | null;
    zone: string | null;
    venue_id: string;
  }[];
};

function EmployeesCard({ apiKey, tenantId, venues, roles, appUrl }: { apiKey: string; tenantId: string; venues: VenueLite[]; roles: TenantRoleOut[]; appUrl?: string }) {
  const [rows, setRows] = useState<EmployeeRow[] | null>(null);
  const [venueId, setVenueId] = useState(venues[0]?.id ?? "");
  const [err, setErr] = useState("");
  const [busy, setBusy] = useState("");
  const [addOpen, setAddOpen] = useState(false);
  // Роль по умолчанию — самая младшая несистемная: безопаснее предложить
  // минимум прав, чем случайно завести управляющего.
  const defaultRole = [...roles].filter((r) => !r.is_system).sort((a, b) => b.level - a.level)[0]?.id ?? "";
  const [nf, setNf] = useState({ name: "", email: "", phone: "", role: "", zone: "" });
  const [tempPwd, setTempPwd] = useState<{ name: string; pwd: string; restored?: boolean; networkLinked?: string | null } | null>(null);
  // Массовое заведение списком (Марат 19.09): «имя; телефон; должность» построчно.
  const [bulkOpen, setBulkOpen] = useState(false);
  const [bulkText, setBulkText] = useState("");
  const [bulkResult, setBulkResult] = useState<{ name: string; login: string | null; password: string | null; role: string | null; networkLinked: string | null; error: string | null }[] | null>(null);
  const bulkRows = bulkText
    .split(/\r?\n/)
    .map((l) => l.trim())
    .filter(Boolean)
    .map((l) => {
      const parts = l.split(/[;\t]|,(?=\s*[+\d])/).map((p) => p.trim());
      const [name = "", contact = "", role = "", zone = ""] = parts;
      const isEmail = contact.includes("@");
      return { name, phone: isEmail ? null : contact || null, email: isEmail ? contact : null, role: role || null, zone: zone || null };
    });
  async function addBulk() {
    setBusy("bulk"); setErr(""); setBulkResult(null);
    const r = await opApi<{ rows: NonNullable<typeof bulkResult> }>(apiKey, "opCreateEmployeesBulk", { tenant_id: tenantId, venue_id: venueId, rows: bulkRows });
    setBusy("");
    if (!r.ok) { setErr(r.error); return; }
    setBulkResult(r.data.rows);
    setBulkText("");
    load();
  }
  function copyBulk() {
    if (!bulkResult) return;
    const text = bulkResult
      .filter((x) => !x.error)
      .map((x) => (x.networkLinked ? `${x.name} — уже работает в сети (${x.networkLinked}), вход под своим паролем` : `${x.name} — логин ${x.login ?? "—"}, пароль ${x.password ?? "—"}`))
      .join("\n");
    // Куда входить — сразу в тексте (аудит 28.09): 20 логинов без адреса
    // превращались в 20 вопросов «а куда это вводить».
    void navigator.clipboard?.writeText(appUrl ? `Вход в KLAIT с телефона: ${appUrl}\n\n${text}` : text);
  }

  const load = useCallback(async () => {
    const r = await opApi<{ employees: EmployeeRow[] }>(apiKey, "opListEmployees", { tenant_id: tenantId });
    if (r.ok) setRows(r.data.employees); else setErr(r.error);
  }, [apiKey, tenantId]);

  useEffect(() => { load(); }, [load]);

  async function patch(userId: string, data: Record<string, unknown>) {
    setBusy(userId); setErr("");
    const r = await opApi(apiKey, "opUpdateEmployee", { tenant_id: tenantId, venue_id: venueId, userId, ...data });
    setBusy("");
    if (r.ok) load(); else setErr(r.error);
  }

  async function fire(e: EmployeeRow) {
    if (!confirm(`Уволить «${e.name}»? Доступ закроется, данные останутся.`)) return;
    setBusy(e.id); setErr("");
    const r = await opApi(apiKey, "opRemoveEmployee", { tenant_id: tenantId, userId: e.id });
    setBusy("");
    if (r.ok) load(); else setErr(r.error);
  }

  // Сброс пароля из консоли (Марат 18.09): новый пароль постоянный, показывается
  // один раз; по сети доезжает до всех учёток человека.
  async function resetPwd(e: EmployeeRow) {
    if (!confirm(`Выдать «${e.name}» новый пароль? Старый перестанет работать, PIN сбросится.`)) return;
    setBusy(e.id); setErr(""); setTempPwd(null);
    const r = await opApi<{ employee: { name: string }; tempPassword?: string }>(apiKey, "opResetEmployeePassword", { tenant_id: tenantId, userId: e.id });
    setBusy("");
    if (!r.ok) { setErr(r.error); return; }
    if (r.data.tempPassword) setTempPwd({ name: r.data.employee.name, pwd: r.data.tempPassword });
  }

  async function add() {
    setBusy("add"); setErr(""); setTempPwd(null);
    const r = await opApi<{ employee: { name: string }; tempPassword?: string; restored?: boolean; networkLinked?: { restaurant: string } }>(
      apiKey, "opCreateEmployee",
      { tenant_id: tenantId, venue_id: venueId, name: nf.name.trim(), email: nf.email.trim() || null, phone: nf.phone.trim() || null, role_id: nf.role, zone: nf.zone || null },
    );
    setBusy("");
    if (!r.ok) { setErr(r.error); return; }
    // Временный пароль показывается ОДИН раз — передать сотруднику.
    if (r.data.tempPassword) {
      setTempPwd({
        name: r.data.employee.name,
        pwd: r.data.tempPassword,
        restored: r.data.restored === true,
      });
    } else if (r.data.networkLinked) {
      setTempPwd({ name: r.data.employee.name, pwd: "", networkLinked: r.data.networkLinked.restaurant });
    }
    setNf({ name: "", email: "", phone: "", role: "", zone: "" });
    setAddOpen(false);
    load();
  }

  const sel = "rounded-lg border border-[var(--op-line-2)] bg-[var(--op-surface)] px-2 py-1 text-xs text-[var(--op-fg)] outline-none focus:border-[var(--op-accent)]";

  return (
    <section className={`${card} space-y-3`}>
      <CardHead
        title="Сотрудники"
        right={
          venues.length > 1 ? (
            <select className={sel} value={venueId} onChange={(e) => setVenueId(e.target.value)}>
              {venues.map((v) => <option key={v.id} value={v.id}>{v.name}</option>)}
            </select>
          ) : null
        }
      />

      {!rows && <p className="text-xs text-[var(--op-muted)]">Загрузка…</p>}

      {rows && (
        <ul className="space-y-1">
          {rows.map((e) => {
            const m = e.memberships[0];
            return (
              <li key={e.id} className="flex flex-wrap items-center gap-2 rounded-lg bg-[var(--op-surface-2)] px-2.5 py-2 text-xs">
                <span className={`flex-1 truncate ${e.is_active ? "text-[var(--op-fg)]" : "text-[var(--op-dim)] line-through"}`}>
                  {e.name}
                  <span className="ml-1.5 text-[var(--op-faint)]">{e.email || e.phone}</span>
                </span>
                <select className={sel} disabled={busy === e.id} value={m?.role_id ?? ""} onChange={(ev) => patch(e.id, { role_id: ev.target.value })}>
                  {roles.map((r) => <option key={r.id} value={r.id}>{r.name}</option>)}
                </select>
                <select className={sel} disabled={busy === e.id} value={m?.zone ?? ""} onChange={(ev) => patch(e.id, { zone: ev.target.value || null })}>
                  <option value="">без зоны</option>
                  {EMP_ZONES.map((z) => <option key={z.k} value={z.k}>{z.label}</option>)}
                </select>
                <button
                  disabled={busy === e.id}
                  onClick={() => patch(e.id, { is_active: !e.is_active })}
                  className={`${bGhost} px-2 py-1 text-xs`}
                  title={e.is_active ? "Временно закрыть доступ" : "Вернуть доступ"}
                >
                  {e.is_active ? "Отключить" : "Включить"}
                </button>
                <button disabled={busy === e.id} onClick={() => resetPwd(e)} className={`${bGhost} px-2 py-1 text-xs`} title="Выдать новый пароль">пароль</button>
                <button disabled={busy === e.id} onClick={() => fire(e)} className={`${bGhost} px-2 py-1 text-xs`} title="Уволить"><CloseIcon className="h-3.5 w-3.5" /></button>
              </li>
            );
          })}
        </ul>
      )}

      {tempPwd && (
        <div className="rounded-lg bg-[var(--op-accent-soft)] p-2.5 text-xs text-[var(--op-accent)]">
          {tempPwd.networkLinked ? (
            <>«{tempPwd.name}» уже работает в сети ({tempPwd.networkLinked}) — учётка привязана к нему, вход под его обычным паролем, новый не нужен.</>
          ) : (
            <>Пароль для «{tempPwd.name}»: <b className="font-mono">{tempPwd.pwd}</b>
            <br />Показан один раз — передайте сотруднику. Пароль постоянный: менять при входе не нужно, сменить можно в профиле.</>
          )}
          {/* Возврат ранее уволенного. Раньше это происходило МОЛЧА: заводишь
              человека с новой почтой, а телефон совпал с уволенным — и вместо
              нового сотрудника воскресает старый, со своей аватаркой, сменами и
              списаниями. Со стороны выглядит как чужой профиль под новым
              логином, и объяснить это невозможно, пока не знаешь про механизм. */}
          {tempPwd.restored && (
            <>
              <br />
              <b>Это ВОЗВРАТ ранее уволенного сотрудника</b> — совпали почта или
              телефон. Вернулся тот же человек со своей историей: аватар, смены,
              списания и результаты аттестации остались его. Если это другой
              человек — заведите его с другой почтой и телефоном.
            </>
          )}
        </div>
      )}
      {err && <p className="text-xs text-[var(--op-danger-text)]">{err}</p>}

      <div className="flex flex-wrap gap-2">
        <button onClick={() => { setAddOpen((o) => !o); setBulkOpen(false); }} className={bGhost}>
          {addOpen ? "Отмена" : "+ Сотрудник"}
        </button>
        <button onClick={() => { setBulkOpen((o) => !o); setAddOpen(false); }} className={bGhost} title="Вставить список от клиента: имя; телефон; должность">
          {bulkOpen ? "Отмена" : "+ Список сотрудников"}
        </button>
      </div>

      {bulkOpen && (
        <div className="space-y-2 rounded-xl bg-[var(--op-surface-2)] p-3">
          <p className="text-xs text-[var(--op-muted)]">
            По строке на человека: <b>Имя Фамилия; телефон; должность; зона</b> (зона — бар, зал, кухня или хозчасть; не указана — берётся из роли). Телефон — это логин, пароль выдаст система. Должность подбирается по ролям
            заведения, не нашлась — самая младшая роль. Тот, кто уже работает в другом ресторане сети с этим телефоном, входит под своим паролем.
          </p>
          <textarea className={`${field} min-h-[120px] w-full font-mono text-xs`} value={bulkText} onChange={(e) => setBulkText(e.target.value)} placeholder={"Иван Иванов; +79001234567; официант\nМария Петрова; +79007654321; бармен"} />
          <div className="flex items-center gap-2 text-xs text-[var(--op-faint)]">
            <span>строк: {bulkRows.length}</span>
            <button disabled={busy === "bulk" || bulkRows.length === 0} onClick={addBulk} className={`${bGhost} ml-auto`}>
              {busy === "bulk" ? "Заводим…" : `Завести ${bulkRows.length}`}
            </button>
          </div>
        </div>
      )}

      {bulkResult && (
        <div className="space-y-2 rounded-lg bg-[var(--op-accent-soft)] p-2.5 text-xs">
          <div className="flex items-center gap-2">
            <b>Заведено {bulkResult.filter((x) => !x.error).length} из {bulkResult.length}</b> — логины и пароли показаны один раз.
            <button className={`${bGhost} ml-auto py-0.5 text-[11px]`} onClick={copyBulk}>Скопировать для отправки</button>
            <button className={`${bGhost} py-0.5 text-[11px]`} onClick={() => setBulkResult(null)}>Скрыть</button>
          </div>
          <table className="w-full text-[12px]">
            <tbody>
              {bulkResult.map((x, i) => (
                <tr key={i} className="border-t border-[var(--op-line)]">
                  <td className="py-1 pr-2 font-medium">{x.name}</td>
                  <td className="py-1 pr-2 text-[var(--op-muted)]">{x.role ?? ""}</td>
                  <td className="py-1 pr-2 font-mono">{x.login ?? ""}</td>
                  <td className="py-1 pr-2 font-mono">{x.password ?? ""}</td>
                  <td className="py-1 text-[var(--op-muted)]">{x.error ? <span className="text-[var(--op-danger-text)]">{x.error}</span> : x.networkLinked ? `уже в сети (${x.networkLinked}), вход под своим паролем` : ""}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {addOpen && (
        <div className="space-y-2 rounded-xl bg-[var(--op-surface-2)] p-3">
          <input className={field} value={nf.name} placeholder="Имя и фамилия" onChange={(e) => setNf({ ...nf, name: e.target.value })} />
          <div className="grid gap-2 sm:grid-cols-2">
            <input className={field} value={nf.email} placeholder="email (он же логин)" onChange={(e) => setNf({ ...nf, email: e.target.value })} />
            <input className={field} value={nf.phone} placeholder="телефон +7…" onChange={(e) => setNf({ ...nf, phone: e.target.value })} />
          </div>
          <div className="grid gap-2 sm:grid-cols-2">
            <select className={field} value={nf.role || defaultRole} onChange={(e) => setNf({ ...nf, role: e.target.value })}>
              {roles.map((r) => <option key={r.id} value={r.id}>{r.name} · старшинство {r.level}</option>)}
            </select>
            <select className={field} value={nf.zone} onChange={(e) => setNf({ ...nf, zone: e.target.value })}>
              <option value="">без зоны</option>
              {EMP_ZONES.map((z) => <option key={z.k} value={z.k}>{z.label}</option>)}
            </select>
          </div>
          <p className="text-[11px] leading-snug text-[var(--op-dim)]">
            Нужен email или телефон — это логин. Пароль сгенерируется временный и
            покажется один раз. Зона определяет, чьи смены человек видит и какое меню
            ему показывать.
          </p>
          <button disabled={busy === "add" || !nf.name.trim() || (!nf.email.trim() && !nf.phone.trim())} onClick={add} className={bPrimary}>
            {busy === "add" ? "Завожу…" : "Завести сотрудника"}
          </button>
        </div>
      )}
    </section>
  );
}

function FlagsCard({ apiKey, tenantId, detail }: { apiKey: string; tenantId: string; detail: TenantDetail }) {
  const [flags, setFlags] = useState<Record<string, boolean>>(detail.feature_flags);
  const [busy, setBusy] = useState<string | null>(null);
  const [saved, setSaved] = useState(false);

  async function toggle(mkey: string) {
    const next = !flags[mkey];
    setBusy(mkey);
    const r = await opApi<{ feature_flags: Record<string, boolean> }>(apiKey, "opSetTenantFlags", { tenant_id: tenantId, flags: { [mkey]: next } });
    setBusy(null);
    if (r.ok) {
      setFlags(r.data.feature_flags);
      setSaved(true);
      setTimeout(() => setSaved(false), 1500);
    }
  }

  return (
    <section className={`${card} space-y-3`}>
      <CardHead title="Модули" right={<Saved show={saved} />} />
      <div className="divide-y divide-[var(--op-line)]">
        {detail.modules.map((m) => (
          <div key={m.key} className="flex items-center justify-between py-2.5 first:pt-0 last:pb-0">
            <span className="text-sm">{m.label}</span>
            <Switch ariaLabel={m.label} checked={!!flags[m.key]} disabled={busy === m.key} onChange={() => toggle(m.key)} />
          </div>
        ))}
      </div>
    </section>
  );
}

/**
 * Кто считает зарплату у точек клиента — касса iiko или KLAIT (06.10, демо).
 * Оператор ставит правило ресторана на онбординге, с сегодняшнего дня;
 * исключения по должностям и людям клиент ставит сам в «Деньги → Настройка
 * зарплат». Без кассы вопроса нет — там всегда KLAIT.
 */
function PaySourceOpCard({ apiKey, tenantId }: { apiKey: string; tenantId: string }) {
  type Row = { venueId: string; venueName: string; posConnected: boolean; linkedPeople: number; rule: "iiko" | "klait" | null; exceptions: number };
  const [rows, setRows] = useState<Row[] | null>(null);
  const [busy, setBusy] = useState<string | null>(null);
  const [err, setErr] = useState("");
  const [saved, setSaved] = useState(false);

  const load = useCallback(async () => {
    const r = await opApi<{ venues: Row[] }>(apiKey, "opGetPaySource", { tenant_id: tenantId });
    if (r.ok) setRows(r.data.venues);
    else setErr(r.error);
  }, [apiKey, tenantId]);
  useEffect(() => {
    load();
  }, [load]);

  async function set(venueId: string, venue: "iiko" | "klait") {
    setBusy(venueId);
    setErr("");
    const r = await opApi(apiKey, "opSetVenuePaySource", { tenant_id: tenantId, venue_id: venueId, venue });
    setBusy(null);
    if (!r.ok) {
      setErr(r.error);
      return;
    }
    setSaved(true);
    setTimeout(() => setSaved(false), 1500);
    await load();
  }

  return (
    <section className={`${card} space-y-3`}>
      <CardHead title="Кто считает зарплату" right={<Saved show={saved} />} />
      <p className="text-[13px] text-[var(--op-muted)]">
        Касса iiko — сумма за смену из расчёта кассы. KLAIT — явки и часы из кассы, деньги по ставкам KLAIT. Кого нет в iiko, того всегда считает KLAIT.
        Действует с сегодняшнего дня; исключения по должностям клиент ставит сам.
      </p>
      {!rows && !err && <p className="text-[13px] text-[var(--op-muted)]">Загрузка…</p>}
      {rows && (
        <ul className="divide-y divide-[var(--op-line)]">
          {rows.map((v) => (
            <li key={v.venueId} className="flex flex-wrap items-center justify-between gap-2 py-2.5 first:pt-0 last:pb-0 text-[13px]">
              <span className="min-w-0 flex-1">
                <b className="font-semibold">{v.venueName}</b>
                <span className="ml-1.5 text-[var(--op-faint)]">
                  {v.posConnected ? `iiko · связано ${v.linkedPeople} чел.` : "кассы нет"}
                  {v.exceptions > 0 ? ` · исключений: ${v.exceptions}` : ""}
                </span>
              </span>
              {v.posConnected ? (
                <PillRow
                  items={[
                    { key: "iiko" as const, title: "iiko" },
                    { key: "klait" as const, title: "KLAIT" },
                  ]}
                  value={v.rule ?? "iiko"}
                  onChange={(k) => busy === null && k !== (v.rule ?? "iiko") && void set(v.venueId, k)}
                  size="s"
                  ariaLabel={`Кто считает зарплату: ${v.venueName}`}
                />
              ) : (
                <span className="text-[var(--op-muted)]">KLAIT</span>
              )}
            </li>
          ))}
        </ul>
      )}
      {err && <p className="text-[13px] text-[var(--op-danger-text)]">{err}</p>}
    </section>
  );
}

/**
 * Telegram у клиента: чей бот, когда отчёт, кому и проверка связи.
 *
 * Раньше карточка знала только «токен задан / токена нет», и это было неправдой
 * с появлением общего бота (07.09): общий токен подставляется при чтении, и
 * заведение без своего бота выглядело как заведение с ботом. Теперь бот выбран
 * явно, а расписание и напоминания — настройки заведения, а не константы
 * воркера.
 */
function TelegramCard({ apiKey, tenantId, detail }: { apiKey: string; tenantId: string; detail: TenantDetail }) {
  const [cfg, setCfg] = useState<TelegramState>(detail.telegram);
  const [tokenInput, setTokenInput] = useState("");
  // Какой режим ПОКАЗЫВАЕМ: у заведения со своим ботом — свой, иначе общий.
  // Отдельно от cfg.own_token, потому что «Свой бот» надо выбрать до того, как
  // появится токен, — иначе поле ввода негде показать.
  const [own, setOwn] = useState(detail.telegram.own_token);
  const [recipients, setRecipients] = useState<Recipient[]>(detail.telegram.recipients);
  const [testTo, setTestTo] = useState("");
  const [testNote, setTestNote] = useState("");
  const [health, setHealth] = useState<BotHealth | null>(null);
  const [menuNote, setMenuNote] = useState("");
  const [busy, setBusy] = useState(false);
  const [saved, setSaved] = useState(false);
  const [err, setErr] = useState("");

  function flash() {
    setSaved(true);
    setTimeout(() => setSaved(false), 1500);
  }

  // Одно сохранение на все настройки: действие принимает поля по отдельности и
  // не трогает непереданные, поэтому тумблер шлёт только себя.
  async function save(patch: Record<string, unknown>) {
    setBusy(true);
    setErr("");
    setTestNote("");
    const r = await opApi<TelegramState>(apiKey, "opSetTenantTelegram", { tenant_id: tenantId, ...patch });
    setBusy(false);
    if (!r.ok) {
      setErr(r.error);
      return false;
    }
    setCfg(r.data);
    setOwn(r.data.own_token);
    // Список получателей перетираем только когда сами его и сохраняли: иначе
    // смена времени отчёта стирала бы недописанного получателя.
    if (patch.recipients !== undefined) setRecipients(r.data.recipients);
    flash();
    return true;
  }

  async function checkHealth() {
    setBusy(true);
    setErr("");
    setMenuNote("");
    const r = await opApi<BotHealth>(apiKey, "opTelegramHealth", { tenant_id: tenantId });
    setBusy(false);
    if (!r.ok) return setErr(r.error);
    setHealth(r.data);
  }

  async function setupMenu() {
    setBusy(true);
    setErr("");
    setMenuNote("");
    const r = await opApi<{ commands: number; shared: boolean }>(apiKey, "opTelegramSetupMenu", {
      tenant_id: tenantId,
    });
    setBusy(false);
    if (!r.ok) return setErr(r.error);
    setMenuNote(
      `Меню бота обновлено: ${r.data.commands} команды.` +
        (r.data.shared ? " Это общий бот KLAIT — меню изменилось у всех клиентов сразу." : ""),
    );
  }

  async function test() {
    setBusy(true);
    setErr("");
    setTestNote("");
    const r = await opApi<{ sent: number; failed: { chat_id: string; error: string }[] }>(
      apiKey,
      "opTelegramTest",
      { tenant_id: tenantId, ...(testTo.trim() ? { chat_id: testTo.trim() } : {}) },
    );
    setBusy(false);
    if (!r.ok) return setErr(r.error);
    setTestNote(
      r.data.failed.length === 0
        ? `Отправлено: ${r.data.sent}`
        : `Отправлено: ${r.data.sent}. Не дошло — ${r.data.failed.map((f) => `${f.chat_id}: ${f.error}`).join("; ")}`,
    );
  }

  const status = !cfg.has_token
    ? <span className="text-[var(--op-muted)]">бот не подключён</span>
    : cfg.own_token
      ? <span className="text-[var(--op-accent)]">свой бот заведения</span>
      : <span className="text-[var(--op-accent)]">общий бот KLAIT</span>;

  return (
    <section className={`${card} space-y-3`}>
      <CardHead title="Telegram-бот" right={<span className="text-xs">{status}</span>} />

      <div className="flex gap-2">
        <button
          onClick={() => (cfg.own_token ? save({ bot_token: null }) : setOwn(false))}
          disabled={busy}
          className={!own ? bPrimary : bGhost}
        >
          Общий бот KLAIT
        </button>
        <button onClick={() => setOwn(true)} disabled={busy} className={own ? bPrimary : bGhost}>
          Свой бот заведения
        </button>
      </div>

      {!own && (
        <p className="text-[11px] leading-snug text-[var(--op-dim)]">
          {cfg.shared_ready
            ? "Работает общий бот KLAIT. Клиенту вводить нечего: сотрудники привязываются в приложении, Профиль → Telegram."
            : "⚠️ В окружении сервера нет TELEGRAM_BOT_TOKEN — общего бота нет, доставки не будет."}
        </p>
      )}

      {own && (
        <div className="space-y-2">
          <div className="flex gap-2">
            <input className={field} type="password" value={tokenInput} placeholder={cfg.own_token ? "новый токен, чтобы заменить" : "токен бота из @BotFather (123456:AA…)"} onChange={(e) => setTokenInput(e.target.value)} autoComplete="off" />
            <button disabled={busy || !tokenInput.trim()} onClick={async () => { if (await save({ bot_token: tokenInput.trim() })) setTokenInput(""); }} className={`${bPrimary} shrink-0`}>Задать</button>
          </div>
          <p className="text-[11px] leading-snug text-[var(--op-dim)]">
            Свой бот — платная опция; он имеет приоритет над общим. Убрать — кнопка «Общий бот KLAIT».
          </p>
        </div>
      )}

      <div className="space-y-2.5 border-t border-[var(--op-line)] pt-3">
        <div className="flex items-center justify-between gap-3">
          <span className="text-sm">Вечерний отчёт руководителям</span>
          <Switch ariaLabel="Вечерний отчёт" checked={cfg.reports_enabled} disabled={busy} onChange={() => save({ reports_enabled: !cfg.reports_enabled })} />
        </div>
        <div className="flex items-center justify-between gap-3">
          <span className="text-sm text-[var(--op-muted)]">Время отчёта</span>
          <select
            className={`${field} w-28`}
            value={cfg.report_time}
            disabled={busy || !cfg.reports_enabled}
            onChange={(e) => save({ report_time: e.target.value })}
          >
            {/* Шаг 30 минут — воркер тикает раз в полчаса; 22:45 не наступило бы никогда. */}
            {Array.from({ length: 48 }, (_, i) => `${String(Math.floor(i / 2)).padStart(2, "0")}:${i % 2 ? "30" : "00"}`).map((t) => (
              <option key={t} value={t}>{t}</option>
            ))}
          </select>
        </div>
        {cfg.reports_enabled && (
          <p className="text-xs text-[var(--op-faint)]">
            По часам каждой точки. Точка, работающая за полночь, получает отчёт после закрытия — о прошедшей ночи.
          </p>
        )}
        <div className="flex items-center justify-between gap-3">
          <span className="text-sm">
            PDF за неделю и месяц
            <span className="block text-[11px] text-[var(--op-dim)]">Уходит в понедельник и первого числа вместе с вечерней сводкой.</span>
          </span>
          <Switch ariaLabel="PDF за период" checked={cfg.period_reports_enabled} disabled={busy || !cfg.reports_enabled} onChange={() => save({ period_reports_enabled: !cfg.period_reports_enabled })} />
        </div>
        <div className="flex items-center justify-between gap-3">
          <span className="text-sm">
            Напоминания сотрудникам
            <span className="block text-[11px] text-[var(--op-dim)]">Кому отчёт не полагается — короткий список несделанного. Всё закрыто — молчим.</span>
          </span>
          <Switch ariaLabel="Напоминания сотрудникам" checked={cfg.reminders_enabled} disabled={busy} onChange={() => save({ reminders_enabled: !cfg.reminders_enabled })} />
        </div>
      </div>

      <div className="space-y-2 border-t border-[var(--op-line)] pt-3">
        <span className={lbl}>Получатели отчётов</span>
        {recipients.map((r, i) => (
          <div key={i} className="flex gap-2">
            <input className={field} value={r.chat_id} placeholder="chat_id" onChange={(e) => setRecipients((s) => s.map((x, j) => (j === i ? { ...x, chat_id: e.target.value } : x)))} />
            <input className={field} value={r.role} placeholder="роль (напр. manager)" onChange={(e) => setRecipients((s) => s.map((x, j) => (j === i ? { ...x, role: e.target.value } : x)))} />
            <button onClick={() => setRecipients((s) => s.filter((_, j) => j !== i))} className={`${bGhost} shrink-0 px-3`} aria-label="Удалить получателя"><CloseIcon className="h-4 w-4" /></button>
          </div>
        ))}
        <div className="flex gap-2">
          <button onClick={() => setRecipients((s) => [...s, { chat_id: "", role: "" }])} className={bGhost}>+ Получатель</button>
          <button disabled={busy} onClick={() => save({ recipients: recipients.filter((r) => r.chat_id.trim()) })} className={bPrimary}>Сохранить</button>
        </div>
      </div>

      {/* Состояние бота (09.09.2026).
          Главное здесь — строка про вебхук. `setWebhook` зовётся руками один
          раз, в коде её нет, и до этой панели «позвали или нет» ниоткуда не
          было видно: при незаданном вебхуке привязка сотрудника молча не
          доходит — код «Старт» копится в очереди Telegram, а забрать его
          некому. */}
      <div className="space-y-2 border-t border-[var(--op-line)] pt-3">
        <div className="flex items-center justify-between gap-2">
          <span className={lbl}>Состояние бота</span>
          <div className="flex gap-2">
            <button disabled={busy || !cfg.has_token} onClick={checkHealth} className={bGhost}>
              Проверить
            </button>
            <button disabled={busy || !cfg.has_token} onClick={setupMenu} className={bGhost}>
              Обновить меню команд
            </button>
          </div>
        </div>
        {health && (
          <div className="space-y-1 text-[12px]">
            <p className={health.reachable ? "text-[var(--op-accent)]" : "text-[var(--op-danger-text)]"}>
              {health.reachable
                ? `Telegram отвечает${health.username ? ` · @${health.username}` : ""}`
                : "⚠️ Telegram не отвечает — проверьте прокси и токен"}
            </p>
            <p className={health.webhook_url ? "text-[var(--op-muted)]" : "text-[var(--op-danger-text)]"}>
              {health.webhook_url
                ? `Вебхук задан: ${health.webhook_url}`
                : "⚠️ Вебхук НЕ задан — сотрудники не смогут привязаться, а кнопки под задачами не сработают. Нужен разовый вызов setWebhook."}
            </p>
            {health.pending > 0 && (
              <p className="text-[var(--op-danger-text)]">
                В очереди Telegram висит необработанных обновлений: {health.pending}
              </p>
            )}
            {health.last_error && (
              <p className="text-[var(--op-muted)]">
                Последняя ошибка доставки: {health.last_error}
                {health.last_error_at
                  ? ` (${new Date(health.last_error_at).toLocaleString("ru-RU")})`
                  : ""}
              </p>
            )}
            {/* Скачивание файлов — свой путь у Telegram (`/file/…`) и своя беда:
                команды ходят, отправка работает, а фото-подтверждения нет. */}
            <p className={health.files.ok ? "text-[var(--op-muted)]" : "text-[var(--op-danger-text)]"}>
              {health.files.ok
                ? `Фото принимаются: ${health.files.detail}`
                : `⚠️ Фото из бота НЕ принимаются — ${health.files.detail}. Путь /file/ должен проксироваться на api.telegram.org так же, как /bot<токен>/.`}
            </p>
            {/* Кому дойдёт. Бот не умеет писать по @нику: нужен chat_id, а он
                появляется, только когда человек сам нажал «Старт» у бота. */}
            <p
              className={
                health.recipients.linked > 0 ? "text-[var(--op-muted)]" : "text-[var(--op-danger-text)]"
              }
            >
              Привязали Telegram: {health.recipients.linked} из {health.recipients.total}
              {health.recipients.unlinked.length > 0 && (
                <>
                  {" "}
                  · не привязали: {health.recipients.unlinked.join(", ")}
                  {health.recipients.unlinked_more > 0 ? ` и ещё ${health.recipients.unlinked_more}` : ""}
                </>
              )}
            </p>
            {health.venues.map((v) => (
              <p
                key={v.name}
                className={v.telegram_on ? "text-[var(--op-muted)]" : "text-[var(--op-danger-text)]"}
              >
                {v.name}:{" "}
                {v.telegram_on
                  ? `Telegram включён${v.explicit ? "" : " (по умолчанию)"}`
                  : "⚠️ Telegram ВЫКЛЮЧЕН в настройках точки — уведомления наружу не уходят. Задачи → Уведомления."}
              </p>
            ))}
          </div>
        )}
        {menuNote && <p className="text-[12px] text-[var(--op-muted)]">{menuNote}</p>}
      </div>

      <div className="space-y-2 border-t border-[var(--op-line)] pt-3">
        <span className={lbl}>Проверка связи</span>
        <div className="flex gap-2">
          <input className={field} value={testTo} placeholder="chat_id — пусто значит всем получателям" onChange={(e) => setTestTo(e.target.value)} />
          <button disabled={busy || !cfg.has_token} onClick={test} className={`${bPrimary} shrink-0`}>Отправить пробное</button>
        </div>
        {/* Ответ Telegram показываем как есть: «чат не найден» и «бот заблокирован»
            чинятся по-разному. */}
        {testNote && <p className="text-[12px] text-[var(--op-muted)]">{testNote}</p>}
      </div>

      {saved && <Saved show={saved} />}
      {err && <p className="text-sm text-[var(--op-danger-text)]">{err}</p>}
    </section>
  );
}

// Роли заведения: один блок вместо двух. Слева список ролей, справа —
// переименование и тумблеры прав выбранной (решение Марата 29.07: «выбрал
// роль — справа тумблеры»). Добавление — кнопками из каталога заготовок либо
// своя роль по названию. Старшинство назначается автоматически (заготовка
// знает своё место, своя роль встаёт под менеджера зоны); двигать его можно
// через опер-API, в интерфейс не выносим — лишняя ручка.
//
// По решению Марата роли настраивает оператор; клиенту редактор появится
// позже, в десктопной версии.
function RolesCard({
  apiKey,
  tenantId,
  capabilities,
  onChanged,
}: {
  apiKey: string;
  tenantId: string;
  // Справочник прав с подписями — из карточки клиента (opGetTenant).
  capabilities: Capability[];
  // Перечитать карточку клиента: состав ролей виден и в списке сотрудников.
  onChanged: () => void;
}) {
  const [rows, setRows] = useState<(TenantRoleOut & { members: number })[]>([]);
  const [templates, setTemplates] = useState<RoleTemplateOut[]>([]);
  const [tplOpen, setTplOpen] = useState(false);
  const [selected, setSelected] = useState<string | null>(null);
  const [nameDraft, setNameDraft] = useState("");
  const [capsDraft, setCapsDraft] = useState<Set<string>>(new Set());
  const [dirty, setDirty] = useState(false);
  const [busy, setBusy] = useState(false);
  const [saved, setSaved] = useState(false);
  const [err, setErr] = useState("");
  const [removing, setRemoving] = useState(false);
  const [moveTo, setMoveTo] = useState("");
  const [newName, setNewName] = useState("");

  const load = useCallback(async () => {
    const r = await opApi<{ roles: (TenantRoleOut & { members: number })[] }>(
      apiKey, "opListRoles", { tenant_id: tenantId },
    );
    if (r.ok) setRows(r.data.roles);
    else setErr(r.error);
    const tr = await opApi<{ templates: RoleTemplateOut[] }>(
      apiKey, "opListRoleTemplates", { tenant_id: tenantId },
    );
    if (tr.ok) setTemplates(tr.data.templates);
  }, [apiKey, tenantId]);
  useEffect(() => { load(); }, [load]);

  const current = rows.find((r) => r.id === selected) ?? null;

  // Выбор роли наполняет черновик справа. Несохранённые тумблеры при смене
  // роли пропадают — поэтому кнопка «Сохранить» подсвечивается через dirty.
  function select(r: TenantRoleOut & { members: number }) {
    setSelected(r.id);
    setNameDraft(r.name);
    setCapsDraft(new Set(r.capabilities));
    setDirty(false);
    setRemoving(false);
    setErr("");
  }

  async function run(action: string, payload: Record<string, unknown>): Promise<boolean> {
    setBusy(true);
    setErr("");
    const r = await opApi(apiKey, action, { tenant_id: tenantId, ...payload });
    setBusy(false);
    if (!r.ok) { setErr(r.error); return false; }
    await load();
    onChanged();
    return true;
  }

  async function save() {
    if (!current) return;
    const okd = await run("opUpdateRole", {
      role_id: current.id,
      name: nameDraft.trim(),
      capabilities: [...capsDraft],
    });
    if (okd) {
      setDirty(false);
      setSaved(true);
      setTimeout(() => setSaved(false), 1500);
    }
  }

  return (
    <section className={`${card} space-y-4`}>
      <CardHead title="Роли заведения" right={<Saved show={saved} />} />

      {/* Добавление: заготовки одной кнопкой + своя роль по названию. */}
      <div className="flex flex-wrap items-center gap-2">
        <button className={bGhost} onClick={() => setTplOpen((o) => !o)}>
          {tplOpen ? "Скрыть заготовки" : "+ Роль из заготовок"}
        </button>
        <input
          className={`${field} max-w-[200px]`}
          value={newName}
          placeholder="Или своя роль…"
          onChange={(e) => setNewName(e.target.value)}
        />
        <button
          className={bGhost}
          disabled={busy || !newName.trim()}
          onClick={async () => {
            // Своя роль встаёт сразу под менеджером зоны (25) — дальше её
            // права всё равно настраиваются тумблерами справа.
            if (await run("opCreateRole", { name: newName.trim(), level: 25, capabilities: [] })) {
              setNewName("");
            }
          }}
        >
          Завести
        </button>
      </div>
      {tplOpen && (
        <div className="flex flex-wrap gap-2">
          {templates.map((t) => (
            <button
              key={t.key}
              className={`${bGhost} ${t.added ? "opacity-40" : ""}`}
              disabled={busy || t.added}
              title={`${t.hint} · прав ${t.capabilities.length}`}
              onClick={() => run("opAddRoleFromTemplate", { template: t.key })}
            >
              <>{t.name}{t.added && <IcCheck className="h-3.5 w-3.5" />}</>
            </button>
          ))}
        </div>
      )}

      {err && <div className="rounded-xl bg-[var(--op-danger-soft)] px-3 py-2 text-sm text-[var(--op-danger-text)]">{err}</div>}

      <div className="grid gap-4 md:grid-cols-[minmax(180px,240px)_1fr]">
        {/* Слева — список ролей. Клик выбирает роль для правки. */}
        <div className="divide-y divide-[var(--op-line)] rounded-xl bg-[var(--op-surface-2)]">
          {rows.map((r) => (
            <button
              key={r.id}
              className={`block w-full px-3 py-2.5 text-left transition-colors ${selected === r.id ? "bg-[var(--op-accent-soft)]" : "hover:bg-[var(--op-ghost)]"}`}
              onClick={() => select(r)}
            >
              <span className="text-sm text-[var(--op-fg)]">{r.name}</span>
              <span className="mt-0.5 block text-xs text-[var(--op-faint)]">
                {r.is_system ? "полный доступ · системная" : `прав ${r.capabilities.length} · людей ${r.members}`}
              </span>
            </button>
          ))}
        </div>

        {/* Справа — переименование и тумблеры прав выбранной роли. */}
        {!current ? (
          <div className="grid place-items-center rounded-xl border border-dashed border-[var(--op-line)] p-6 text-sm text-[var(--op-faint)]">
            Выберите роль слева — здесь появятся её права
          </div>
        ) : current.is_system ? (
          <div className="grid place-items-center rounded-xl border border-dashed border-[var(--op-line)] p-6 text-center text-sm text-[var(--op-faint)]">
            Владелец — системная роль: полный доступ всегда.<br />Переименовать или урезать её нельзя.
          </div>
        ) : (
          <div className="space-y-3">
            <div className="flex flex-wrap items-center gap-2">
              <input
                className={`${field} max-w-[240px]`}
                value={nameDraft}
                onChange={(e) => { setNameDraft(e.target.value); setDirty(true); }}
                placeholder="Название роли"
              />
              <button disabled={busy || !dirty} className={bPrimary} onClick={save}>
                {busy ? "Сохраняю…" : "Сохранить"}
              </button>
              {!removing ? (
                <button className={bDanger} onClick={() => { setRemoving(true); setMoveTo(""); }}>Удалить</button>
              ) : (
                <span className="flex items-center gap-2">
                  {current.members > 0 && (
                    <select className={`${field} max-w-[180px]`} value={moveTo} onChange={(e) => setMoveTo(e.target.value)}>
                      <option value="">перевести {current.members} чел. на…</option>
                      {rows.filter((x) => x.id !== current.id).map((x) => (
                        <option key={x.id} value={x.id}>{x.name}</option>
                      ))}
                    </select>
                  )}
                  <button
                    disabled={busy || (current.members > 0 && !moveTo)}
                    className={bDangerSolid}
                    onClick={async () => {
                      const payload: Record<string, unknown> = { role_id: current.id };
                      if (current.members > 0) payload.reassign_to_role_id = moveTo;
                      if (await run("opDeleteRole", payload)) { setSelected(null); setRemoving(false); }
                    }}
                  >
                    Точно удалить
                  </button>
                  <button className={bGhost} onClick={() => setRemoving(false)}>Отмена</button>
                </span>
              )}
            </div>

            <div className="divide-y divide-[var(--op-line)] rounded-xl bg-[var(--op-surface-2)] px-3">
              {capabilities.map((c) => (
                <div key={c.key} className="flex items-center justify-between gap-3 py-2.5">
                  <span className="text-sm text-[var(--op-fg)]">
                    {c.label}
                    <span className="mt-0.5 block text-xs text-[var(--op-faint)]">{c.hint}</span>
                  </span>
                  <Switch
                    ariaLabel={c.label}
                    checked={capsDraft.has(c.key)}
                    onChange={() => {
                      setCapsDraft((prev) => {
                        const next = new Set(prev);
                        if (next.has(c.key)) next.delete(c.key);
                        else next.add(c.key);
                        return next;
                      });
                      setDirty(true);
                    }}
                  />
                </div>
              ))}
            </div>
            <p className="text-xs text-[var(--op-faint)]">
              Тумблеры вступают в силу после «Сохранить». Действуют сразу на всех
              людей с этой ролью.
            </p>
          </div>
        )}
      </div>
    </section>
  );
}


// ── Данные точки (D-b2): выбор точки + переименование/бланк + загрузки + категории + каталог ──
function VenueData({ apiKey, tenantId, venues }: { apiKey: string; tenantId: string; venues: VenueLite[] }) {
  const [venueId, setVenueId] = useState(venues[0]?.id ?? "");
  const [summary, setSummary] = useState<VenueSummary | null>(null);
  const [err, setErr] = useState("");
  const [reloadKey, setReloadKey] = useState(0);

  const load = useCallback(async () => {
    if (!venueId) return;
    const r = await opApi<VenueSummary>(apiKey, "opListVenueData", { tenant_id: tenantId, venue_id: venueId });
    if (r.ok) setSummary(r.data);
    else setErr(r.error);
  }, [apiKey, tenantId, venueId]);

  useEffect(() => {
    load();
  }, [load, reloadKey]);
  const refresh = () => setReloadKey((k) => k + 1);

  // Есть ли у точки касса iiko (23.09): в консоли нет отдельного «статус
  // подключения» — резонный сигнал под рукой ровно тот, что уже нужен карте
  // категорий: категории кассы появляются, только если у точки правда есть
  // зеркало iiko (posProduct непустой). До первой синхронизации каталога
  // новая точка это распознать не сможет — тогда ручная загрузка ниже видна
  // как обычно, и это верно: синхронизации ещё не было, каталога взять неоткуда.
  const [iikoCategories, setIikoCategories] = useState<SeenCategory[] | null>(null);
  const loadIikoMap = useCallback(async () => {
    if (!venueId) return { ok: false as const, error: "Точка не выбрана" };
    const r = await opApi<{ categories: SeenCategory[]; map: CategoryMap }>(apiKey, "opIikoCategoryMap", {
      tenant_id: tenantId,
      venue_id: venueId,
    });
    if (r.ok) setIikoCategories(r.data.categories);
    return r;
  }, [apiKey, tenantId, venueId]);
  useEffect(() => {
    void loadIikoMap();
  }, [loadIikoMap, reloadKey]);
  // Признак подключения — с сервера (opListVenueData); список категорий
  // кассы остаётся запасным сигналом для старого ответа без поля.
  const iikoConnected = summary?.iiko_connected ?? (iikoCategories?.length ?? 0) > 0;

  if (venues.length === 0) return <section className={`${card} text-sm text-[var(--op-muted)]`}>У заведения нет точек.</section>;
  const venue = venues.find((v) => v.id === venueId) ?? null;

  return (
    <section className={`${card} space-y-5`}>
      <div className="flex flex-wrap items-center justify-between gap-3">
        <CardHead title="Данные точки" />
        {venues.length > 1 ? (
          <select className={`${field} w-auto`} value={venueId} onChange={(e) => setVenueId(e.target.value)}>
            {venues.map((v) => <option key={v.id} value={v.id}>{v.name}</option>)}
          </select>
        ) : (
          <Pill>{venues[0].name}</Pill>
        )}
      </div>

      {summary && (
        <div className="grid grid-cols-3 gap-3">
          <MiniStat label="Каталог" value={summary.catalog_count} />
          <MiniStat label="Меню" value={summary.recipe_count} />
          <MiniStat label="Категорий" value={summary.categories.length} />
        </div>
      )}
      {err && <p className="text-sm text-[var(--op-danger-text)]">{err}</p>}

      {summary && venue && <VenueAdmin key={`va-${venueId}`} apiKey={apiKey} tenantId={tenantId} venue={venue} summary={summary} onDone={refresh} />}
      <IikoCatalogCard
        key={`iiko-${venueId}`}
        apiKey={apiKey}
        tenantId={tenantId}
        venueId={venueId}
        klaitCategories={(summary?.categories ?? []).map((c) => c.name)}
        categories={iikoCategories}
        loadCategories={loadIikoMap}
        onSynced={refresh}
      />
      <Uploads apiKey={apiKey} tenantId={tenantId} venueId={venueId} onDone={refresh} iikoConnected={iikoConnected} />
      <CategoriesEditor apiKey={apiKey} tenantId={tenantId} venueId={venueId} categories={summary?.categories ?? []} onDone={refresh} iikoConnected={iikoConnected} />
      <CatalogEditor key={`cat-${venueId}`} apiKey={apiKey} tenantId={tenantId} venueId={venueId} categories={summary?.categories ?? []} reloadKey={reloadKey} />
      <MenuEditor key={`menu-${venueId}`} apiKey={apiKey} tenantId={tenantId} venueId={venueId} reloadKey={reloadKey} />
    </section>
  );
}

// «Обновить каталог из iiko» (23.09, D-b2): точка синхронизирует свою ОБЛАСТЬ
// (что реально продаётся и хранится именно здесь, не вся сеть) и заодно правит
// карту «категория кассы → категория KLAIT» — тот же экран, что видит владелец
// с десктопа (IikoCategoryMapEditor), только транспорт свой (opApi ключом
// администратора вместо сессии сотрудника).
type OpCatalogSyncResult = {
  scopeDishes: number;
  scopeItems: number;
  fallbackWholeNetwork: boolean;
  itemsCreated: number;
  itemsUpdated: number;
  itemsDeactivated: number;
  recipesCreated: number;
  recipesUpdated: number;
  recipesDeactivated: number;
  categoriesAdded: number;
  skipped: { name: string; reason: string }[];
  skippedTotal: number;
  warnings: string[];
  relinked: { invoices: number; priced: number; writeoffs: number; sales: number };
};

function catalogSyncSummary(r: OpCatalogSyncResult): string {
  const parts = [
    `Позиции: новых ${r.itemsCreated}, обновлено ${r.itemsUpdated}, снято с оборота ${r.itemsDeactivated}.`,
    `Меню: новых ${r.recipesCreated}, обновлено ${r.recipesUpdated}, снято с оборота ${r.recipesDeactivated}.`,
    `Категорий добавлено: ${r.categoriesAdded}.`,
  ];
  if (r.warnings.length > 0) parts.push(r.warnings.join(" "));
  if (r.skippedTotal > 0) {
    parts.push(
      `Пропущено ${r.skippedTotal}: ${r.skipped.slice(0, 3).map((s) => `${s.name} — ${s.reason}`).join("; ")}${r.skippedTotal > 3 ? "…" : ""}.`,
    );
  }
  if (r.relinked.invoices || r.relinked.writeoffs || r.relinked.sales) {
    parts.push(`Пересвязано по новым позициям: накладных ${r.relinked.invoices}, списаний ${r.relinked.writeoffs}, строк продаж ${r.relinked.sales}.`);
  }
  return parts.join(" ");
}

function IikoCatalogCard({
  apiKey,
  tenantId,
  venueId,
  klaitCategories,
  categories,
  loadCategories,
  onSynced,
}: {
  apiKey: string;
  tenantId: string;
  venueId: string;
  klaitCategories: string[];
  /** Категории кассы этой точки — `null`, пока не читали. Пусто = либо касса не подключена, либо ещё не синхронизировали каталог. */
  categories: SeenCategory[] | null;
  loadCategories: () => Promise<ApiResult<{ categories: SeenCategory[]; map: CategoryMap }>>;
  onSynced: () => void;
}) {
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");
  const [result, setResult] = useState<OpCatalogSyncResult | null>(null);

  async function sync() {
    setBusy(true);
    setErr("");
    setResult(null);
    const r = await opApi<OpCatalogSyncResult>(apiKey, "opIikoCatalogSync", { tenant_id: tenantId, venue_id: venueId });
    setBusy(false);
    if (!r.ok) {
      setErr(r.error);
      return;
    }
    setResult(r.data);
    onSynced();
  }

  return (
    <div className="space-y-3 border-t border-[var(--op-line)] pt-4">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <span className={lbl}>Каталог из iiko</span>
        <button className={bPrimary} disabled={busy || !venueId} onClick={() => void sync()}>
          {busy ? "Синхронизирую…" : "Обновить каталог из iiko"}
        </button>
      </div>
      <p className="text-[11px] leading-snug text-[var(--op-dim)]">
        Тянет склад, меню и категории из кассы за эту точку (область — что реально продаётся и
        хранится именно здесь, а не вся сеть). Без подключённой кассы вернёт ошибку — подключает
        её владелец сам, в «Настройки → POS».
      </p>
      {err && <p className="text-xs text-[var(--op-danger-text)]">{err}</p>}
      {result && <p className="text-xs text-[var(--op-muted)]">{catalogSyncSummary(result)}</p>}
      {categories !== null && categories.length > 0 && (
        <IikoCategoryMapEditor
          canEdit
          klaitCategories={klaitCategories}
          load={loadCategories}
          save={(patch) => opApi(apiKey, "opIikoCategoryMapSet", { tenant_id: tenantId, venue_id: venueId, patch })}
        />
      )}
      {categories !== null && categories.length === 0 && (
        <p className="text-xs text-[var(--op-muted)]">
          Категорий из кассы пока нет — если у точки подключён iiko, они появятся после первой синхронизации выше.
        </p>
      )}
    </div>
  );
}

function MiniStat({ label, value }: { label: string; value: number }) {
  return (
    <div className="rounded-xl bg-[var(--op-surface-2)] p-3">
      <div className="text-[11px] uppercase tracking-[0.12em] text-[var(--op-faint)]">{label}</div>
      <div className="mt-1 text-xl font-bold tabular-nums">{value}</div>
    </div>
  );
}

function Uploads({
  apiKey,
  tenantId,
  venueId,
  onDone,
  iikoConnected,
}: {
  apiKey: string;
  tenantId: string;
  venueId: string;
  onDone: () => void;
  /** Точка на iiko (23.09): каталог и меню приходят синхронизацией, ручная загрузка этих двух файлов только мешала бы. */
  iikoConnected: boolean;
}) {
  const [busy, setBusy] = useState("");
  const [msg, setMsg] = useState("");

  async function upload(action: string, file: File | null) {
    if (!file || !venueId) return;
    setBusy(action);
    setMsg("");
    try {
      const fileB64 = await readFileB64(file);
      // Ответ разных импортов отличается: у весов есть список пропущенных строк,
      // и Record<string, number> его бы не пропустил.
      const r = await opApi<Record<string, unknown>>(apiKey, action, { tenant_id: tenantId, venue_id: venueId, file: fileB64 });
      if (r.ok) {
        const d = r.data;
        setMsg(
          action === "opImportMenu"
            ? `Меню: импортировано ${d.imported} рецептов`
            // Пропущенные штрихкоды называем вслух: дубли в выгрузке — обычное
            // дело, импорт из-за них не падает, но молчать о потере нельзя,
            // иначе сканер «не видит» позицию и это выглядит поломкой.
            : action === "opImportBottleProfiles"
              // Пропущенные строки называем поимённо: «обновлено 40 из 49» без
              // причины отправляет человека искать несуществующую ошибку.
              ? `Веса: обновлено ${d.updated} из ${d.total}` +
                (Array.isArray(d.skipped) && d.skipped.length > 0
                  ? ` · пропущено ${d.skipped.length}: ` +
                    d.skipped.slice(0, 3).map((x: { row: number; reason: string }) => `строка ${x.row} — ${x.reason}`).join("; ") +
                    (d.skipped.length > 3 ? " …" : "")
                  : "")
              : `Каталог: новых ${d.imported}, обновлено ${d.updated}, всего ${d.total}` +
                (d.barcodeSkipped ? ` · штрихкодов пропущено (дубли): ${d.barcodeSkipped}` : ""),
        );
        onDone();
      } else setMsg("Ошибка: " + r.error);
    } catch {
      setMsg("Ошибка чтения файла");
    } finally {
      setBusy("");
    }
  }

  return (
    <div className="space-y-2 border-t border-[var(--op-line)] pt-4">
      <span className={lbl}>Загрузка данных (файлы Excel)</span>
      {/* Прежние подписи («Каталог (iiko-бланк)», «Меню (ТТК)») не читались как
          то, что человек ищет: «где загрузить позиции для инвентаризации».
          Называем действием и добавляем строку-пояснение под каждой кнопкой. */}
      {/* Точка на iiko (23.09): каталог и техкарты приходят синхронизацией
          («Каталог из iiko» выше) — ручная загрузка этих двух файлов здесь
          только собьёт то, что уже пришло из кассы. Веса бутылок остаются
          ВСЕГДА: это наш профиль тары, его iiko не знает вовсе. */}
      <div className="grid gap-2 sm:grid-cols-2">
        {!iikoConnected && (
          <div className="space-y-1">
            <label className={`${bGhost} w-full cursor-pointer`}>
              {busy === "opImportInventoryCatalog" ? "Загружаю…" : "↑ Позиции для инвентаризации"}
              <input type="file" accept=".xlsx" className="hidden" disabled={!!busy} onChange={(e) => { const f = e.target.files?.[0] ?? null; e.target.value = ""; upload("opImportInventoryCatalog", f); }} />
            </label>
            <p className="text-[11px] leading-snug text-[var(--op-dim)]">
              Бланк из iiko. Категории проставятся сами, повторная загрузка не затирает
              штрихкоды и вашу разметку.
            </p>
          </div>
        )}
        <div className="space-y-1">
          {/* Веса тары — отдельным файлом от каталога: iiko-бланк описывает
              товар, а вес пустой бутылки клиент измеряет сам один раз и
              пользуется годами. Без них позиция не считается по весу вовсе. */}
          <label className={`${bGhost} w-full cursor-pointer`}>
            {busy === "opImportBottleProfiles" ? "Загружаю…" : "↑ Веса бутылок (для взвешивания)"}
            <input type="file" accept=".xlsx" className="hidden" disabled={!!busy} onChange={(e) => { const f = e.target.files?.[0] ?? null; e.target.value = ""; upload("opImportBottleProfiles", f); }} />
          </label>
          <p className="text-[11px] leading-snug text-[var(--op-dim)]">
            Объём, вес пустой и полной, плотность. Шаблон —
            <code className="px-1">reference/template-bottle-profiles.xlsx</code>.
            Без них взвешивание не считает остаток.
          </p>
        </div>
        {!iikoConnected && (
          <div className="space-y-1">
            <label className={`${bGhost} w-full cursor-pointer`}>
              {busy === "opImportMenu" ? "Загружаю…" : "↑ Меню и техкарты"}
              <input type="file" accept=".xlsx" className="hidden" disabled={!!busy} onChange={(e) => { const f = e.target.files?.[0] ?? null; e.target.value = ""; upload("opImportMenu", f); }} />
            </label>
            <p className="text-[11px] leading-snug text-[var(--op-dim)]">
              Листы Recipes, Ingredients, Steps. Появится у сотрудников во вкладке ТТК.
            </p>
          </div>
        )}
      </div>
      {iikoConnected && (
        <p className="text-[11px] leading-snug text-[var(--op-dim)]">
          Каталог, меню и категории приходят из iiko автоматически.
        </p>
      )}
      {msg && <p className="text-xs text-[var(--op-muted)]">{msg}</p>}
    </div>
  );
}

function CategoriesEditor({
  apiKey,
  tenantId,
  venueId,
  categories,
  onDone,
  iikoConnected,
}: {
  apiKey: string;
  tenantId: string;
  venueId: string;
  categories: Category[];
  onDone: () => void;
  /** Точка на iiko (23.09): категории заводит карта «iiko → KLAIT» выше, ручное создание/удаление здесь только разошлось бы с ней. Переименование и режим замера — по-прежнему наши, оставлены. */
  iikoConnected: boolean;
}) {
  const [name, setName] = useState("");
  const [mode, setMode] = useState<"count" | "formula">("count");
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");

  async function add() {
    if (!name.trim()) return;
    setBusy(true);
    setErr("");
    const r = await opApi(apiKey, "opCreateCategory", { tenant_id: tenantId, venue_id: venueId, name: name.trim(), mode });
    setBusy(false);
    if (r.ok) { setName(""); onDone(); } else setErr(r.error);
  }

  // Правка (переименование + режим замера) и удаление пустой категории (20.09).
  // Раньше это умел только владелец с десктопа — на выезде к клиенту
  // переименовать «Вино» в «Вина» или включить взвешивание было неоткуда,
  // чинили руками в БД. Строка раскрывается в инлайн-форму, как позиция
  // каталога ниже.
  const [editName, setEditName] = useState<string | null>(null);
  const [renameDraft, setRenameDraft] = useState("");
  const [modeDraft, setModeDraft] = useState<"count" | "formula">("count");
  const [rowBusy, setRowBusy] = useState(false);
  const [rowErr, setRowErr] = useState("");
  const [movedMsg, setMovedMsg] = useState("");
  const [delName, setDelName] = useState<string | null>(null);
  const [delBusy, setDelBusy] = useState(false);
  const [delErr, setDelErr] = useState("");

  function startEdit(c: Category) {
    setEditName(c.name);
    setRenameDraft(c.name);
    setModeDraft(c.mode);
    setRowErr("");
    setMovedMsg("");
    setDelName(null);
  }

  async function saveEdit(original: string) {
    const newName = renameDraft.trim();
    if (!newName) { setRowErr("Название не может быть пустым"); return; }
    setRowBusy(true);
    setRowErr("");
    // new_name шлём всегда, даже если строка не изменилась: сервер сам не
    // считает это переименованием (moved останется 0) и не ругнётся на
    // столкновение имени с самим собой — так проще, чем отслеживать «правил
    // ли человек название» отдельным флагом.
    const r = await opApi<{ name: string; mode: string; moved: number }>(apiKey, "opUpdateCategory", {
      tenant_id: tenantId, venue_id: venueId, name: original, new_name: newName, mode: modeDraft,
    });
    setRowBusy(false);
    if (r.ok) {
      setEditName(null);
      setMovedMsg(r.data.moved > 0 ? `«${original}» → «${r.data.name}»: перенесено ${r.data.moved} позиций` : "");
      onDone();
    } else setRowErr(r.error);
  }

  async function doDelete(catName: string) {
    setDelBusy(true);
    setDelErr("");
    const r = await opApi(apiKey, "opDeleteCategory", { tenant_id: tenantId, venue_id: venueId, name: catName });
    setDelBusy(false);
    // Ошибку «в категории N позиций» показываем текстом как есть — сервер уже
    // объяснил, что делать (перенести позиции), выдумывать формулировку не надо.
    if (r.ok) { setDelName(null); onDone(); } else setDelErr(r.error);
  }

  return (
    <div className="space-y-2 border-t border-[var(--op-line)] pt-4">
      <span className={lbl}>Категории учёта</span>
      {movedMsg && <p className="text-xs text-[var(--op-muted)]">{movedMsg}</p>}
      <ul className="space-y-1">
        {categories.map((c) => (
          <li key={c.name} className="rounded-lg bg-[var(--op-surface-2)] px-2.5 py-1.5 text-xs">
            {editName === c.name ? (
              <div className="space-y-2 py-1">
                <div className="flex flex-wrap gap-2">
                  <input className={`${field} max-w-[220px]`} value={renameDraft} placeholder="Название категории" onChange={(e) => setRenameDraft(e.target.value)} />
                  <select className={`${field} w-auto`} value={modeDraft} onChange={(e) => setModeDraft(e.target.value as "count" | "formula")}>
                    <option value="count">кол-во</option>
                    <option value="formula">взвешивание</option>
                  </select>
                </div>
                {rowErr && <p className="text-xs text-[var(--op-danger-text)]">{rowErr}</p>}
                <div className="flex gap-2">
                  <button disabled={rowBusy || !renameDraft.trim()} className={bPrimary} onClick={() => saveEdit(c.name)}>
                    {rowBusy ? "Сохраняю…" : "Сохранить"}
                  </button>
                  <button className={bGhost} onClick={() => setEditName(null)}>Отмена</button>
                </div>
              </div>
            ) : (
              <div className="flex flex-wrap items-center gap-2">
                <span className="min-w-0 flex-1 truncate text-[var(--op-fg)]">
                  {c.name} <span className="text-[var(--op-dim)]">{c.mode === "formula" ? "взвеш." : "кол-во"}</span>
                </span>
                <button className="shrink-0 text-xs text-[var(--op-accent)]" onClick={() => startEdit(c)}>переименовать</button>
                {/* Удаление — не на точке с iiko (23.09): категорию завела карта
                    «iiko → KLAIT», и снять её отсюда значило бы разойтись с
                    картой до следующей синхронизации. */}
                {!iikoConnected && (delName === c.name ? (
                  <span className="flex shrink-0 items-center gap-1.5">
                    <button disabled={delBusy} className={`${bDangerSolid} px-3 py-1 text-xs`} onClick={() => doDelete(c.name)}>
                      {delBusy ? "…" : "Точно удалить"}
                    </button>
                    <button className={`${bGhost} px-2.5 py-1 text-xs`} onClick={() => { setDelName(null); setDelErr(""); }}>Отмена</button>
                  </span>
                ) : (
                  <button className={`${bDanger} px-2.5 py-1 text-xs shrink-0`} onClick={() => { setDelName(c.name); setDelErr(""); }}>Удалить</button>
                ))}
              </div>
            )}
            {delName === c.name && delErr && <p className="mt-1 text-xs text-[var(--op-danger-text)]">{delErr}</p>}
          </li>
        ))}
        {categories.length === 0 && <li className="text-xs text-[var(--op-muted)]">пока нет</li>}
      </ul>
      {iikoConnected ? (
        <p className="text-[11px] leading-snug text-[var(--op-dim)]">
          Каталог, меню и категории приходят из iiko автоматически.
        </p>
      ) : (
        <div className="flex gap-2">
          <input className={field} value={name} placeholder="Новая категория" onChange={(e) => setName(e.target.value)} />
          <select className={`${field} w-auto`} value={mode} onChange={(e) => setMode(e.target.value as "count" | "formula")}>
            <option value="count">кол-во</option>
            <option value="formula">взвешивание</option>
          </select>
          <button disabled={busy || !name.trim()} onClick={add} className={`${bPrimary} shrink-0`}>Добавить</button>
        </div>
      )}
      {err && <p className="text-xs text-[var(--op-danger-text)]">{err}</p>}
    </div>
  );
}

// Меню: подписи и цены (решение Марата 30.07). В перспективе это правит
// менеджер/владелец в десктопе; до его появления — оператор здесь и менеджер
// с телефона. Три поля позиции — цена, описание, аллергены; состав приходит
// импортом меню и здесь не правится.
type MenuRow = {
  id: string;
  name: string;
  category: string | null;
  zone: string | null;
  price: number | null;
  description: string | null;
  allergens: string[];
  sommelier: string | null;
};

function MenuEditor({ apiKey, tenantId, venueId, reloadKey }: { apiKey: string; tenantId: string; venueId: string; reloadKey: number }) {
  const [rows, setRows] = useState<MenuRow[] | null>(null);
  const [q, setQ] = useState("");
  const [open, setOpen] = useState(false);
  const [expanded, setExpanded] = useState<string | null>(null);
  const [draft, setDraft] = useState({ price: "", description: "", allergens: "" });
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");

  const load = useCallback(async () => {
    const r = await opApi<{ recipes: MenuRow[] }>(apiKey, "opListMenu", { tenant_id: tenantId, venue_id: venueId });
    if (r.ok) setRows(r.data.recipes);
  }, [apiKey, tenantId, venueId]);

  useEffect(() => {
    if (open) load();
  }, [open, load, reloadKey]);

  function expand(r: MenuRow) {
    setExpanded(r.id);
    setErr("");
    setDraft({
      price: r.price != null ? String(r.price) : "",
      description: r.description ?? "",
      allergens: r.allergens.join(", "),
    });
  }

  async function save(r: MenuRow) {
    setBusy(true);
    setErr("");
    const priceNum = draft.price.trim() === "" ? null : Number(draft.price.replace(",", "."));
    if (priceNum !== null && (!Number.isFinite(priceNum) || priceNum < 0)) {
      setBusy(false);
      setErr("Цена — число не меньше нуля");
      return;
    }
    const res = await opApi(apiKey, "opUpdateMenuItem", {
      tenant_id: tenantId,
      venue_id: venueId,
      recipe_id: r.id,
      price: priceNum,
      description: draft.description,
      allergens: draft.allergens.split(/[,\n]/).map((a) => a.trim()).filter(Boolean),
    });
    setBusy(false);
    if (res.ok) {
      setExpanded(null);
      load();
    } else setErr(res.error);
  }

  const filtered = (rows ?? []).filter((r) => !q || r.name.toLowerCase().includes(q.toLowerCase()));

  return (
    <div className="space-y-2 border-t border-[var(--op-line)] pt-4">
      <button onClick={() => setOpen((o) => !o)} className="text-sm font-medium text-[var(--op-muted)] transition-colors hover:text-[var(--op-fg)]">
        {open ? "▾" : "▸"} Меню: подписи и цены{rows ? ` · ${rows.length}` : ""}
      </button>
      {open && (
        <>
          <p className="text-xs text-[var(--op-faint)]">
            Цена, описание и аллергены видны официанту в карточке позиции. Пустые
            поля показываются как «пока не заполнено» — заполняйте по мере готовности.
          </p>
          <input className={field} value={q} placeholder="Поиск по названию" onChange={(e) => setQ(e.target.value)} />
          {err && <p className="text-xs text-[var(--op-danger-text)]">{err}</p>}
          <ul className="max-h-96 divide-y divide-[var(--op-line)] overflow-y-auto rounded-xl bg-[var(--op-surface-2)] px-3">
            {filtered.map((r) => (
              <li key={r.id} className="py-2">
                {expanded === r.id ? (
                  <div className="space-y-2">
                    <div className="text-sm font-medium text-[var(--op-fg)]">{r.name}</div>
                    <div className="flex flex-wrap gap-2">
                      <input className={`${field} max-w-[130px]`} value={draft.price} placeholder="Цена, ₽"
                        onChange={(e) => setDraft({ ...draft, price: e.target.value })} />
                      <input className={`${field} flex-1 min-w-[200px]`} value={draft.allergens} placeholder="Аллергены через запятую"
                        onChange={(e) => setDraft({ ...draft, allergens: e.target.value })} />
                    </div>
                    <textarea className={`${field} min-h-[70px]`} value={draft.description} placeholder="Описание для гостя"
                      onChange={(e) => setDraft({ ...draft, description: e.target.value })} />
                    <div className="flex gap-2">
                      <button disabled={busy} className={bPrimary} onClick={() => save(r)}>Сохранить</button>
                      <button className={bGhost} onClick={() => setExpanded(null)}>Отмена</button>
                    </div>
                  </div>
                ) : (
                  <button className="flex w-full items-center justify-between gap-3 text-left" onClick={() => expand(r)}>
                    <span className="text-sm text-[var(--op-fg)]">
                      {r.name}
                      <span className="mt-0.5 block text-xs text-[var(--op-faint)]">
                        {r.category ?? "без категории"}
                        {" · "}{r.price != null ? `${r.price.toLocaleString("ru-RU")} ₽` : "цены нет"}
                        {" · "}{r.description ? "есть описание" : "описания нет"}
                        {" · "}{r.allergens.length ? "аллергены указаны" : "аллергенов нет"}
                      </span>
                    </span>
                    <span className="shrink-0 text-xs text-[var(--op-accent)]">править</span>
                  </button>
                )}
              </li>
            ))}
          </ul>
        </>
      )}
    </div>
  );
}

// Подписи источника плотности — те же слова, что в правке позиции на десктопе
// (ItemEditModal): у ВСЕХ позиций в колонке density стоит дефолт 0.95, поэтому
// число показывается фактом только вместе с источником; без источника честно
// говорим «не подтверждена».
const DENSITY_SRC_LABEL: Record<string, string> = {
  measured: "выведена из веса бутылки",
  manual: "введена вручную",
  reference: "из справочника продуктов",
};

const fmtNum3 = (n: number) => n.toLocaleString("ru-RU", { maximumFractionDigits: 3 });

// Строка профиля бутылки в списке: объём · тара · плотность с источником.
function bottleMeta(i: CatalogItem): string {
  return [
    i.volume_ml != null ? `${fmtNum3(i.volume_ml)} мл` : "объём не задан",
    i.empty_g != null ? `тара ${fmtNum3(i.empty_g)} г` : "вес пустой не задан",
    i.density_source
      ? `плотность ${fmtNum3(i.density)} (${DENSITY_SRC_LABEL[i.density_source] ?? i.density_source})`
      : `плотность ${fmtNum3(i.density)} · не подтверждена`,
  ].join(" · ");
}

// Ярлык зоны — тот же словарь, что у смен (SHIFT_ZONES), чтобы название зоны не
// разъезжалось между разделами консоли. Дефолт "bar" — как у сервера (parseZone):
// импорт каталога и создание позиции без явной зоны кладут её в бар.
function zoneLabel(zone: string | null | undefined): string {
  return SHIFT_ZONES.find((z) => z.k === (zone ?? "bar"))?.label ?? (zone ?? "Бар");
}

// Черновик правки позиции (opUpdateItem). Поля зависят от режима категории —
// правило то же, что у владельца на десктопе, проверяет общее ядро updateItemCore.
// zoneTouched/barcodeTouched — список каталога (opListCatalog) не отдаёт зону и
// штрихкод (их нет в select), поэтому предзаполнить их достоверно нечем: шлём
// на сервер, только если оператор реально тронул поле в этой сессии правки,
// иначе правка одного имени молча откатила бы зону на «бар» или стёрла бы
// штрихкод, которых список не показал.
type ItemDraft = {
  name: string;
  unit: string;
  volume_ml: string;
  empty_g: string;
  full_g: string;
  density: string;
  zone: string;
  barcode: string;
  zoneTouched: boolean;
  barcodeTouched: boolean;
};

function CatalogEditor({ apiKey, tenantId, venueId, categories, reloadKey }: { apiKey: string; tenantId: string; venueId: string; categories: Category[]; reloadKey: number }) {
  const [items, setItems] = useState<CatalogItem[] | null>(null);
  const [q, setQ] = useState("");
  const [zoneFilter, setZoneFilter] = useState("");
  const [catFilter, setCatFilter] = useState("");
  const [open, setOpen] = useState(false);

  const load = useCallback(async () => {
    const r = await opApi<{ items: CatalogItem[] }>(apiKey, "opListCatalog", { tenant_id: tenantId, venue_id: venueId });
    if (r.ok) setItems(r.data.items);
  }, [apiKey, tenantId, venueId]);

  useEffect(() => {
    if (open) load();
  }, [open, load, reloadKey]);

  async function setCat(item: CatalogItem, category: string) {
    const r = await opApi(apiKey, "opSetItemCategory", { tenant_id: tenantId, venue_id: venueId, item_id: item.id, category });
    if (r.ok) setItems((s) => s?.map((x) => (x.id === item.id ? { ...x, category } : x)) ?? null);
  }

  // Включить/выключить позицию из оборота — своим тумблером в строке, не через
  // общую форму правки: это единственное поле, которое меняют одним кликом
  // без открытия остальных.
  const [activeBusyId, setActiveBusyId] = useState<string | null>(null);
  async function toggleActive(item: CatalogItem) {
    setActiveBusyId(item.id);
    const r = await opApi<{ is_active: boolean }>(apiKey, "opSetItemActive", {
      tenant_id: tenantId, venue_id: venueId, item_id: item.id, active: !item.is_active,
    });
    setActiveBusyId(null);
    if (r.ok) setItems((s) => s?.map((x) => (x.id === item.id ? { ...x, is_active: r.data.is_active } : x)) ?? null);
  }

  // Массовая смена зоны (20.09): импорт каталога кладёт всё в бар, а зона решает,
  // на какой склад в iiko уйдёт документ инвентаризации — перекладывать сотни
  // строк по одной руками никто не станет.
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());
  const [bulkZone, setBulkZone] = useState("bar");
  const [bulkConfirm, setBulkConfirm] = useState(false);
  const [bulkBusy, setBulkBusy] = useState(false);
  const [bulkMsg, setBulkMsg] = useState("");
  const [bulkErr, setBulkErr] = useState("");

  function toggleSelect(id: string) {
    setSelectedIds((s) => {
      const next = new Set(s);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });
  }

  async function doBulkZone() {
    setBulkBusy(true);
    setBulkErr("");
    setBulkMsg("");
    const r = await opApi<{ moved: number; zone: string }>(apiKey, "opSetItemsZone", {
      tenant_id: tenantId, venue_id: venueId, item_ids: [...selectedIds], zone: bulkZone,
    });
    setBulkBusy(false);
    if (r.ok) {
      setBulkMsg(`Перенесено ${r.data.moved} позиций в зону «${zoneLabel(r.data.zone)}»`);
      // Обновляем зону выбранных позиций локально — сервер уже подтвердил
      // перенос, ждать перезагрузки списка незачем.
      setItems((s) => s?.map((x) => (selectedIds.has(x.id) ? { ...x, zone: r.data.zone } : x)) ?? null);
      setSelectedIds(new Set());
      setBulkConfirm(false);
    } else setBulkErr(r.error);
  }

  // Режим замера позиции — по её категории; без категории в настройках точки
  // сервер считает позицию формульной (categoryMode), здесь то же правило.
  const modeOf = (i: CatalogItem): "formula" | "count" =>
    categories.find((c) => c.name === i.category)?.mode ?? "formula";

  // Добавление одной позиции. Набор полей зависит от режима категории:
  // «количество» — нужна единица; «взвешивание» — профиль бутылки (объём в мл;
  // вес пустой и плотность нужны формуле замера открытых бутылок:
  // остаток = (вес − вес пустой) / плотность). Правило то же, что и на
  // телефоне, проверяет его общее ядро createItemCore. Зона и штрихкод — не
  // зависят от режима: зона нужна сразу (иначе позиция ляжет в дефолтный бар),
  // штрихкод необязателен.
  const [addOpen, setAddOpen] = useState(false);
  const [nf, setNf] = useState({ name: "", category: "", unit: "шт", zone: "bar", barcode: "", volume_ml: "", empty_g: "", full_g: "", density: "" });
  const [addBusy, setAddBusy] = useState(false);
  const [addErr, setAddErr] = useState("");
  const nfCat = categories.find((c) => c.name === nf.category);
  const nfFormula = nfCat?.mode === "formula";

  async function addItem() {
    setAddBusy(true); setAddErr("");
    const payload: Record<string, unknown> = {
      tenant_id: tenantId, venue_id: venueId,
      name: nf.name.trim(), category: nf.category, zone: nf.zone,
    };
    if (nfFormula) {
      payload.volume_ml = Number(nf.volume_ml.replace(",", "."));
      // Пустые поля не шлём: сервер прочитал бы "" как «стереть», а при
      // создании это просто «не знаю» — бармен задаст при первом взвешивании.
      if (nf.empty_g.trim()) payload.empty_g = nf.empty_g;
      if (nf.full_g.trim()) payload.full_g = nf.full_g;
      if (nf.density.trim()) payload.density = nf.density;
    } else payload.unit = nf.unit.trim();
    const r = await opApi<{ id: string }>(apiKey, "opCreateItem", payload);
    if (!r.ok) { setAddBusy(false); setAddErr(r.error); return; }
    // opCreateItem штрихкод не принимает вовсе (createItemCore его не знает) —
    // тем же действием, что и правка (opUpdateItem), ставим его вторым вызовом
    // сразу после создания, пока есть свежий id.
    const barcode = nf.barcode.trim();
    if (barcode) {
      const br = await opApi(apiKey, "opUpdateItem", { tenant_id: tenantId, venue_id: venueId, item_id: r.data.id, barcode });
      if (!br.ok) {
        setAddBusy(false);
        setAddErr(`Позиция создана, но штрихкод не сохранён: ${br.error}`);
        load();
        return;
      }
    }
    setAddBusy(false);
    setNf({ name: "", category: nf.category, unit: nf.unit, zone: nf.zone, barcode: "", volume_ml: "", empty_g: "", full_g: "", density: "" });
    setAddOpen(false);
    load();
  }

  // Правка позиции — раскрытой строкой (как в «Меню: подписи и цены»).
  const [editId, setEditId] = useState<string | null>(null);
  const [draft, setDraft] = useState<ItemDraft>({ name: "", unit: "", volume_ml: "", empty_g: "", full_g: "", density: "", zone: "bar", barcode: "", zoneTouched: false, barcodeTouched: false });
  const [editBusy, setEditBusy] = useState(false);
  const [editErr, setEditErr] = useState("");

  function startEdit(i: CatalogItem) {
    setEditId(i.id);
    setEditErr("");
    setDraft({
      name: i.name,
      unit: i.unit ?? "шт",
      volume_ml: i.volume_ml != null ? String(i.volume_ml) : "",
      empty_g: i.empty_g != null ? String(i.empty_g) : "",
      full_g: i.full_g != null ? String(i.full_g) : "",
      // Плотность подставляем только при известном источнике — дефолт 0.95
      // без источника выглядел бы заполненным полем (заглушка как факт).
      density: i.density_source ? String(i.density) : "",
      // Список каталога зону/штрихкод не отдаёт (см. комментарий у ItemDraft) —
      // показываем дефолт и шлём его дальше, только если оператор сам тронул поле.
      zone: i.zone ?? "bar",
      barcode: i.barcode ?? "",
      zoneTouched: false,
      barcodeTouched: false,
    });
  }

  async function saveEdit(i: CatalogItem) {
    setEditBusy(true); setEditErr("");
    const payload: Record<string, unknown> = {
      tenant_id: tenantId, venue_id: venueId, item_id: i.id, name: draft.name.trim(),
    };
    if (modeOf(i) === "count") payload.unit = draft.unit.trim();
    else {
      // Пустая строка здесь — осознанное «стереть» (сервер прочитает как NULL).
      payload.volume_ml = draft.volume_ml;
      payload.empty_g = draft.empty_g;
      payload.full_g = draft.full_g;
      payload.density = draft.density;
    }
    // Зону и штрихкод шлём, только если их реально тронули в этой сессии
    // правки (см. ItemDraft) — иначе правка одного имени молча откатила бы
    // зону на «бар» или стёрла бы штрихкод, которых список не показал.
    if (draft.zoneTouched) payload.zone = draft.zone;
    if (draft.barcodeTouched) payload.barcode = draft.barcode.trim();
    const r = await opApi(apiKey, "opUpdateItem", payload);
    setEditBusy(false);
    if (r.ok) {
      setEditId(null);
      load();
    } else setEditErr(r.error);
  }

  const filtered = (items ?? []).filter((i) => {
    if (q && !(i.name.toLowerCase().includes(q.toLowerCase()) || (i.code ?? "").includes(q))) return false;
    if (zoneFilter && (i.zone ?? "bar") !== zoneFilter) return false;
    if (catFilter && i.category !== catFilter) return false;
    return true;
  });

  return (
    <div className="space-y-2 border-t border-[var(--op-line)] pt-4">
      <button onClick={() => setOpen((o) => !o)} className="text-sm font-medium text-[var(--op-muted)] transition-colors hover:text-[var(--op-fg)]">
        {open ? "▾" : "▸"} Каталог позиций{items ? ` · ${items.length}` : ""}
      </button>
      {open && (
        <>
          <p className="text-xs text-[var(--op-faint)]">
            Плотность и вес пустой бутылки нужны менеджеру для замера открытых
            бутылок по формуле (объём × плотность + вес пустой). Позиции без них
            замеряются только целыми бутылками.
          </p>
          <div className="flex flex-wrap gap-2">
            <input className={`${field} min-w-[160px] flex-1`} value={q} placeholder="Поиск по коду / названию" onChange={(e) => setQ(e.target.value)} />
            <select className={`${field} w-auto`} value={catFilter} onChange={(e) => setCatFilter(e.target.value)}>
              <option value="">— все категории —</option>
              {categories.map((c) => <option key={c.name} value={c.name}>{c.name}</option>)}
            </select>
            <select className={`${field} w-auto`} value={zoneFilter} onChange={(e) => setZoneFilter(e.target.value)}>
              <option value="">— все зоны —</option>
              {SHIFT_ZONES.map((z) => <option key={z.k} value={z.k}>{z.label}</option>)}
            </select>
            <button onClick={() => setAddOpen((o) => !o)} className={`${bGhost} shrink-0`}>
              {addOpen ? "Отмена" : "+ Позиция"}
            </button>
          </div>

          {addOpen && (
            <div className="space-y-2 rounded-xl bg-[var(--op-surface-2)] p-3">
              <input className={field} value={nf.name} placeholder="Название позиции" onChange={(e) => setNf({ ...nf, name: e.target.value })} />
              <div className="grid gap-2 sm:grid-cols-2">
                {/* Зона и штрихкод — свойства позиции, не зависят от режима замера
                    категории (как на сервере в createItemCore/updateItemCore). */}
                <select className={field} value={nf.zone} onChange={(e) => setNf({ ...nf, zone: e.target.value })}>
                  {SHIFT_ZONES.map((z) => <option key={z.k} value={z.k}>{z.label}</option>)}
                </select>
                <input className={field} value={nf.barcode} placeholder="Штрихкод (необязательно)" onChange={(e) => setNf({ ...nf, barcode: e.target.value })} />
              </div>
              <div className="grid gap-2 sm:grid-cols-2">
                <select className={field} value={nf.category} onChange={(e) => setNf({ ...nf, category: e.target.value })}>
                  <option value="">— категория —</option>
                  {categories.map((c) => (
                    <option key={c.name} value={c.name}>
                      {c.name} ({c.mode === "formula" ? "взвешивание" : "количество"})
                    </option>
                  ))}
                </select>
                {nfFormula ? (
                  <input className={field} value={nf.volume_ml} inputMode="decimal" placeholder="Объём бутылки, мл" onChange={(e) => setNf({ ...nf, volume_ml: e.target.value })} />
                ) : (
                  <input className={field} value={nf.unit} placeholder="Единица (шт, кг, л…)" onChange={(e) => setNf({ ...nf, unit: e.target.value })} />
                )}
              </div>
              {nfFormula && (
                <div className="grid gap-2 sm:grid-cols-3">
                  <input className={field} value={nf.empty_g} inputMode="decimal" placeholder="Вес пустой, г" onChange={(e) => setNf({ ...nf, empty_g: e.target.value })} />
                  <input className={field} value={nf.full_g} inputMode="decimal" placeholder="Вес полной, г (необяз.)" onChange={(e) => setNf({ ...nf, full_g: e.target.value })} />
                  <input className={field} value={nf.density} inputMode="decimal" placeholder="Плотность (0.95)" onChange={(e) => setNf({ ...nf, density: e.target.value })} />
                </div>
              )}
              <p className="text-[11px] leading-snug text-[var(--op-dim)]">
                {nf.category
                  ? nfFormula
                    ? "Категория со взвешиванием: нужен объём бутылки. Плотность и вес пустой нужны для замера открытых бутылок по формуле; пустая плотность — дефолт 0.95. Всё это можно дозаполнить позже."
                    : "Категория с прямым вводом количества — достаточно единицы измерения."
                  : "Выберите категорию — от её режима зависит, что нужно заполнить."}
              </p>
              {addErr && <p className="text-xs text-[var(--op-danger)]">{addErr}</p>}
              <button
                disabled={addBusy || !nf.name.trim() || !nf.category || (nfFormula ? !nf.volume_ml.trim() : !nf.unit.trim())}
                onClick={addItem}
                className={bPrimary}
              >
                {addBusy ? "Добавляю…" : "Добавить позицию"}
              </button>
            </div>
          )}

          {selectedIds.size > 0 && (
            <div className="flex flex-wrap items-center gap-2 rounded-xl bg-[var(--op-accent-soft)] px-3 py-2 text-xs">
              <span className="text-[var(--op-fg)]">Выбрано: {selectedIds.size}</span>
              <select className={`${field} w-auto py-1`} value={bulkZone} onChange={(e) => { setBulkZone(e.target.value); setBulkConfirm(false); }}>
                {SHIFT_ZONES.map((z) => <option key={z.k} value={z.k}>{z.label}</option>)}
              </select>
              {!bulkConfirm ? (
                <button className={`${bGhost} px-2.5 py-1 text-xs`} onClick={() => setBulkConfirm(true)}>Перенести в зону…</button>
              ) : (
                <span className="flex items-center gap-2">
                  <span className="text-[var(--op-faint)]">Перенести {selectedIds.size} в «{zoneLabel(bulkZone)}»?</span>
                  <button disabled={bulkBusy} className={`${bPrimary} px-3 py-1 text-xs`} onClick={doBulkZone}>{bulkBusy ? "…" : "Подтвердить"}</button>
                  <button className={`${bGhost} px-2.5 py-1 text-xs`} onClick={() => setBulkConfirm(false)}>Отмена</button>
                </span>
              )}
              <button className={`${bGhost} px-2.5 py-1 text-xs`} onClick={() => { setSelectedIds(new Set()); setBulkConfirm(false); }}>Снять выбор</button>
            </div>
          )}
          {bulkMsg && <p className="text-xs text-[var(--op-muted)]">{bulkMsg}</p>}
          {bulkErr && <p className="text-xs text-[var(--op-danger-text)]">{bulkErr}</p>}

          {!items && <p className="text-xs text-[var(--op-muted)]">Загрузка…</p>}
          <ul className="max-h-96 space-y-1 overflow-y-auto pr-1">
            {filtered.slice(0, 200).map((i) => (
              <li key={i.id} className={`rounded-lg bg-[var(--op-surface-2)] px-2.5 py-1.5 text-xs ${i.is_active ? "" : "opacity-55"}`}>
                {editId === i.id ? (
                  <div className="space-y-2 py-1">
                    <input className={field} value={draft.name} placeholder="Название" onChange={(e) => setDraft({ ...draft, name: e.target.value })} />
                    <div className="grid gap-2 sm:grid-cols-2">
                      <select className={field} value={draft.zone} onChange={(e) => setDraft({ ...draft, zone: e.target.value, zoneTouched: true })}>
                        {SHIFT_ZONES.map((z) => <option key={z.k} value={z.k}>{z.label}</option>)}
                      </select>
                      <input className={field} value={draft.barcode} placeholder="Штрихкод" onChange={(e) => setDraft({ ...draft, barcode: e.target.value, barcodeTouched: true })} />
                    </div>
                    {modeOf(i) === "count" ? (
                      <input className={`${field} max-w-[180px]`} value={draft.unit} placeholder="Единица (шт, кг, л…)" onChange={(e) => setDraft({ ...draft, unit: e.target.value })} />
                    ) : (
                      <div className="grid gap-2 sm:grid-cols-4">
                        <input className={field} value={draft.volume_ml} inputMode="decimal" placeholder="Объём, мл" onChange={(e) => setDraft({ ...draft, volume_ml: e.target.value })} />
                        <input className={field} value={draft.empty_g} inputMode="decimal" placeholder="Вес пустой, г" onChange={(e) => setDraft({ ...draft, empty_g: e.target.value })} />
                        <input className={field} value={draft.full_g} inputMode="decimal" placeholder="Вес полной, г" onChange={(e) => setDraft({ ...draft, full_g: e.target.value })} />
                        <input className={field} value={draft.density} inputMode="decimal" placeholder="Плотность (0.95)" onChange={(e) => setDraft({ ...draft, density: e.target.value })} />
                      </div>
                    )}
                    {modeOf(i) === "formula" && (
                      <p className="text-[11px] leading-snug text-[var(--op-dim)]">
                        Плотность и вес пустой нужны для замера открытых бутылок по
                        формуле. Пустое поле = «не знаю»: объём и веса сотрутся, а
                        плотность вернётся к неподтверждённому дефолту 0.95.
                      </p>
                    )}
                    {editErr && <p className="text-xs text-[var(--op-danger)]">{editErr}</p>}
                    <div className="flex gap-2">
                      <button disabled={editBusy || !draft.name.trim()} className={bPrimary} onClick={() => saveEdit(i)}>
                        {editBusy ? "Сохраняю…" : "Сохранить"}
                      </button>
                      <button className={bGhost} onClick={() => setEditId(null)}>Отмена</button>
                    </div>
                  </div>
                ) : (
                  <div className="flex flex-wrap items-center gap-2">
                    <input type="checkbox" className="shrink-0" checked={selectedIds.has(i.id)} onChange={() => toggleSelect(i.id)} aria-label={`Выбрать «${i.name}»`} />
                    <span className="w-14 shrink-0 font-mono text-[var(--op-faint)]">{i.code}</span>
                    <span className="min-w-0 flex-1">
                      <span className="block truncate text-[var(--op-fg)]">
                        {i.name}
                        {!i.is_active && <span className="ml-1.5 text-[10px] uppercase tracking-wide text-[var(--op-faint)]">отключена</span>}
                      </span>
                      <span className="block truncate text-[11px] text-[var(--op-faint)]">
                        {modeOf(i) === "formula" ? bottleMeta(i) : `единица: ${i.unit ?? "—"}`}
                        {" · "}{zoneLabel(i.zone)}
                        {i.barcode ? ` · штрихкод ${i.barcode}` : ""}
                      </span>
                    </span>
                    <select className="w-auto shrink-0 rounded-lg border border-[var(--op-line-2)] bg-[var(--op-surface)] px-2 py-1 text-xs text-[var(--op-fg)] outline-none focus:border-[var(--op-accent)]" value={i.category ?? ""} onChange={(e) => setCat(i, e.target.value)}>
                      {i.category && !categories.some((c) => c.name === i.category) && <option value={i.category}>{i.category}</option>}
                      {categories.map((c) => <option key={c.name} value={c.name}>{c.name}</option>)}
                    </select>
                    <Switch ariaLabel={i.is_active ? "В обороте" : "Убрана из оборота"} checked={i.is_active} disabled={activeBusyId === i.id} onChange={() => toggleActive(i)} />
                    <button className="shrink-0 text-xs text-[var(--op-accent)]" onClick={() => startEdit(i)}>править</button>
                  </div>
                )}
              </li>
            ))}
          </ul>
          {items && (
            <p className="text-[11px] text-[var(--op-faint)]">
              Показано {Math.min(filtered.length, 200)} из {items.length}
              {filtered.length > 200 ? " — уточните поиск или фильтр" : ""}
            </p>
          )}
        </>
      )}
    </div>
  );
}

// Ответ opSeedDemo: памятка, которую оператор отдаёт заказчику вместе со
// ссылкой. Пароли приходят открытым текстом намеренно — это учётки стенда, а
// не живых людей, и передать их нужно целиком.
type DemoSeedResult = {
  url: string | null;
  aiDailyLimit: number;
  telegram?: boolean;
  accounts: { name: string; role: string; email: string; password: string }[];
  counts: Record<string, number>;
};

// ── Заведение (3b): переименование + сброс пароля владельца + архивация + удаление ──
/**
 * Сколько заведение потратило СЕГОДНЯ и во что это обходится.
 *
 * 🔴 Зачем. Карточка показывала два потолка — «вопросов в день» и «токенов в
 * день» — и ни одной цифры о том, что происходит. На вопрос «сколько в итоге
 * стоить» она не отвечала вовсе (Никита, 21.09).
 *
 * Здесь три вещи, которых не хватало:
 *  · расход против потолка — видно, близко ли до отказа;
 *  · КАКОЙ потолок упрётся первым. Их два, они независимы, и упирается всегда
 *    один; без этого «осталось 950 вопросов» читается как «всё хорошо», когда
 *    токены уже на исходе;
 *  · деньги. Цена берётся из AI_PRICE_RUB_PER_KTOKEN и может быть не задана —
 *    тариф поставщика в коде не выдумываем, см. priceRubPerKtoken на сервере.
 */
function AiSpendLine({
  usedCalls,
  limitCalls,
  usedTokens,
  limitTokens,
  rubPerKtoken,
}: {
  usedCalls: number;
  limitCalls: number | null;
  usedTokens: number;
  limitTokens: number | null;
  rubPerKtoken: number | null;
}) {
  const callsShare = limitCalls && limitCalls > 0 ? usedCalls / limitCalls : 0;
  // Ноль у токенов означает «без потолка», а не «нельзя» — доля тогда не
  // считается вовсе, иначе делили бы на ноль и рисовали 100%.
  const tokensShare = limitTokens && limitTokens > 0 ? usedTokens / limitTokens : 0;
  const binding = tokensShare > callsShare ? "tokens" : "calls";
  const worst = Math.max(callsShare, tokensShare);
  const tone = worst >= 0.8 ? "text-[var(--op-danger-text)]" : worst >= 0.5 ? "text-[var(--op-warn,#9a6a00)]" : "text-[var(--op-muted)]";
  const rub = rubPerKtoken !== null ? (usedTokens / 1000) * rubPerKtoken : null;

  return (
    <div className={`text-[12px] leading-snug ${tone}`}>
      <b>Сегодня потрачено:</b> {usedCalls} {plural(usedCalls, "вопрос", "вопроса", "вопросов")}
      {limitCalls && limitCalls > 0 ? ` из ${limitCalls}` : ""} · {usedTokens.toLocaleString("ru-RU")} токенов
      {limitTokens && limitTokens > 0 ? ` из ${limitTokens.toLocaleString("ru-RU")}` : " (без потолка)"}
      {rub !== null ? ` · ≈ ${rub.toFixed(2)} ₽` : ""}
      {usedCalls > 0 && (
        <>
          {" · "}в среднем {Math.round(usedTokens / usedCalls).toLocaleString("ru-RU")} токенов на вопрос
        </>
      )}
      {worst >= 0.5 && (
        <>
          {" "}
          — первым упрётся потолок {binding === "tokens" ? "ТОКЕНОВ" : "ВОПРОСОВ"}.
        </>
      )}
      {rub === null && (
        <div className="mt-0.5 text-[var(--op-faint)]">
          Деньги не считаются: задайте <code>AI_PRICE_RUB_PER_KTOKEN</code> — цену тысячи токенов в
          рублях по вашему тарифу.
        </div>
      )}
    </div>
  );
}

function TenantAdmin({ apiKey, tenantId, name, slug, logo, monthlyFee, aiDailyLimit, aiLimitEffective, aiDailyTokens, aiTokensEffective, aiUsedToday, aiTokensToday, aiRubPerKtoken, demoStand, venues, onSaved, onArchived }: { apiKey: string; tenantId: string; name: string; slug: string; logo: string | null; monthlyFee: string | null; aiDailyLimit: number | null; aiLimitEffective: number | null; aiDailyTokens: number | null; aiTokensEffective: number | null; aiUsedToday: number; aiTokensToday: number; aiRubPerKtoken: number | null; demoStand: boolean; venues: VenueLite[]; onSaved: () => void; onArchived: () => void }) {
  const [tname, setTname] = useState(name);
  const [fee, setFee] = useState(monthlyFee ?? "");
  // Дневная квота обращений к модели. Действие для неё было с 19.09, а поля
  // не было вовсе — менять лимит было нечем, и «поднимите в консоли» звучало
  // как издёвка (Марат, 19.09).
  const [aiLimit, setAiLimit] = useState(aiDailyLimit === null ? "" : String(aiDailyLimit));
  const [aiTokens, setAiTokens] = useState(aiDailyTokens === null ? "" : String(aiDailyTokens));
  const [busy, setBusy] = useState("");
  const [saved, setSaved] = useState(false);
  const [pwd, setPwd] = useState<null | { login: string | null; tempPassword: string }>(null);
  // Стартовый набор (07.09): что добавили точкам клиента.
  const [starter, setStarter] = useState<{ venueName: string; added: string[] }[] | null>(null);
  const [confirmArchive, setConfirmArchive] = useState(false);
  const [delSlug, setDelSlug] = useState("");
  const [err, setErr] = useState("");
  // Демо-стенд: подтверждение слагом и раздаточная памятка после наполнения.
  const [demoSlug, setDemoSlug] = useState("");
  const [demo, setDemo] = useState<null | DemoSeedResult>(null);
  const [copied, setCopied] = useState(false);
  const [demoVenue, setDemoVenue] = useState(venues[0]?.id ?? "");
  const [demoBot, setDemoBot] = useState("");

  async function rename() {
    setBusy("rename"); setErr("");
    const r = await opApi(apiKey, "opRenameTenant", { tenant_id: tenantId, name: tname.trim() });
    setBusy("");
    if (r.ok) { setSaved(true); setTimeout(() => setSaved(false), 1500); } else setErr(r.error);
  }
  // Логотип читаем в data-URL прямо в браузере и шлём строкой — отдельного
  // файлового хранилища в проекте пока нет (появится с S3/MinIO в И1).
  // Формат и размер проверяет и сервер (opSetTenantLogo); здесь — ранний отказ,
  // чтобы не гонять мегабайты впустую.
  async function uploadLogo(file: File | null) {
    if (!file) return;
    if (!/^image\/(png|jpe?g|webp)$/.test(file.type)) {
      setErr("Поддерживаются только PNG, JPEG или WEBP");
      return;
    }
    setBusy("logo"); setErr("");
    // Ужимаем в браузере, а не требуем этого от человека: логотип обычно
    // приносят исходником на 2–5 МБ, и прежний отказ «файл больше 200 КБ»
    // означал «идите уменьшайте в редакторе». Прозрачность переживает —
    // логотип на прозрачном фоне остаётся PNG.
    const compressed = await compressImage(file).catch(() => null);
    const dataUrl = compressed?.dataUrl ?? "";
    if (!dataUrl) { setBusy(""); setErr("Не удалось прочитать файл"); return; }
    if (compressed && compressed.bytes > 220_000) {
      setBusy("");
      setErr("Картинка слишком тяжёлая даже после сжатия — возьмите файл поменьше");
      return;
    }
    const r = await opApi(apiKey, "opSetTenantLogo", { tenant_id: tenantId, logo: dataUrl });
    setBusy("");
    if (r.ok) { setSaved(true); setTimeout(() => setSaved(false), 1500); onSaved(); } else setErr(r.error);
  }

  async function saveFee() {
    setBusy("fee"); setErr("");
    const r = await opApi(apiKey, "opSetTenantFee", { tenant_id: tenantId, monthly_fee: fee.trim() });
    setBusy("");
    if (r.ok) { setSaved(true); setTimeout(() => setSaved(false), 1500); onSaved(); } else setErr(r.error);
  }

  async function saveAiTokens() {
    setBusy("aitok"); setErr("");
    const v = aiTokens.trim();
    const r = await opApi(apiKey, "opSetTenantAiLimit", {
      tenant_id: tenantId,
      // Пусто = «своего потолка нет», работает общий дефолт продукта.
      //
      // Непустое отправляем СТРОКОЙ как есть: Number("500 000") даёт NaN,
      // JSON.stringify превращает его в null, а null сервер читает как
      // «удалить ключ» — оператор видел «сохранено», а потолок молча
      // возвращался к общему (ревью 21.09). Пусть решает и отказывает сервер.
      ai_daily_tokens: v === "" ? null : v,
    });
    setBusy("");
    if (r.ok) { setSaved(true); setTimeout(() => setSaved(false), 1500); onSaved(); } else setErr(r.error);
  }

  async function saveAiLimit() {
    setBusy("ai"); setErr("");
    const v = aiLimit.trim();
    const r = await opApi(apiKey, "opSetTenantAiLimit", {
      tenant_id: tenantId,
      // Пусто = «своего числа нет», заведение живёт на общем правиле. Непустое
      // — строкой: см. соседнюю saveAiTokens, та же ловушка с NaN → null.
      ai_daily_limit: v === "" ? null : v,
    });
    setBusy("");
    if (r.ok) { setSaved(true); setTimeout(() => setSaved(false), 1500); onSaved(); } else setErr(r.error);
  }

  async function removeLogo() {
    setBusy("logo"); setErr("");
    const r = await opApi(apiKey, "opSetTenantLogo", { tenant_id: tenantId, logo: "" });
    setBusy("");
    if (r.ok) { onSaved(); } else setErr(r.error);
  }

  // Стартовый набор настроек (07.09): окна самобронирования, категории склада,
  // часовой пояс — добавляет только отсутствующее, можно жать повторно.
  async function applyStarter() {
    setBusy("starter"); setErr(""); setStarter(null);
    const r = await opApi<{ venues: { venueId: string; venueName: string; added: string[] }[] }>(apiKey, "opApplyStarterSettings", { tenant_id: tenantId });
    setBusy("");
    if (r.ok) { setStarter(r.data.venues); onSaved(); } else setErr(r.error);
  }

  async function resetPwd() {
    setBusy("pwd"); setErr(""); setPwd(null);
    const r = await opApi<{ owner: { login: string | null }; tempPassword: string }>(apiKey, "opResetOwnerPassword", { tenant_id: tenantId });
    setBusy("");
    if (r.ok) setPwd({ login: r.data.owner.login, tempPassword: r.data.tempPassword }); else setErr(r.error);
  }
  // Наполнение демо-стенда. Ответ показываем на экране и НЕ прячем по таймеру:
  // это раздаточная памятка, её переписывают или копируют, а не «мельком
  // видят» — в отличие от вспышки «Сохранено».
  async function seedDemo() {
    setBusy("demo"); setErr(""); setDemo(null);
    const r = await opApi<DemoSeedResult>(apiKey, "opSeedDemo", {
      tenant_id: tenantId,
      confirm_slug: demoSlug.trim(),
      venue_id: demoVenue || undefined,
      telegram_bot_token: demoBot.trim() || undefined,
    });
    setBusy("");
    if (r.ok) { setDemo(r.data); setDemoSlug(""); setDemoBot(""); onSaved(); } else setErr(r.error);
  }
  // Объявление стендом — отдельный шаг, потому что именно оно опасное.
  // Наполнение потом повторяется перед каждым показом и уже безобидно.
  async function markDemo() {
    setBusy("mark"); setErr("");
    const r = await opApi(apiKey, "opMarkDemoStand", {
      tenant_id: tenantId,
      confirm_slug: demoSlug.trim(),
    });
    setBusy("");
    if (r.ok) { setDemoSlug(""); onSaved(); } else setErr(r.error);
  }
  // Памятку почти всегда пересылают — заказчик открывает стенд со своего
  // устройства. Переписывать четыре пароля руками с экрана значит ошибиться в
  // одном из них, поэтому отдаём готовым текстом.
  async function copyDemo() {
    if (!demo) return;
    const lines = [
      demo.url ? `Стенд: ${demo.url}` : null,
      "",
      ...demo.accounts.map((a) => `${a.role} · ${a.name}: ${a.email} / ${a.password}`),
    ].filter((l) => l !== null);
    try {
      await navigator.clipboard.writeText(lines.join("\n"));
      setCopied(true);
      setTimeout(() => setCopied(false), 1500);
    } catch {
      setErr("Браузер не дал скопировать — выделите текст выше вручную");
    }
  }
  async function archive() {
    setBusy("archive"); setErr("");
    const r = await opApi(apiKey, "opArchiveTenant", { tenant_id: tenantId });
    setBusy("");
    if (r.ok) onArchived(); else setErr(r.error);
  }
  async function del() {
    setBusy("del"); setErr("");
    const r = await opApi(apiKey, "opDeleteTenant", { tenant_id: tenantId, confirm_slug: delSlug.trim() });
    setBusy("");
    if (r.ok) onArchived(); else setErr(r.error);
  }

  return (
    <section className={`${card} space-y-3`}>
      <CardHead title="Заведение" right={<Saved show={saved} />} />
      {/* Логотип: превью слева, действия справа. Без логотипа Avatar покажет инициалы. */}
      <div className="flex items-center gap-3">
        <Avatar name={name} logo={logo} className="h-14 w-14 text-base" />
        <div className="flex flex-wrap items-center gap-2">
          {/* Стрелка была глифом «↑»: он не красится вместе с подписью и на
              части шрифтов рисуется другой толщиной, чем соседние значки. */}
          <label className={`${bGhost} group cursor-pointer`}>
            <IcUpload className={`${motionIcon} h-[18px] w-[18px] group-hover:-translate-y-0.5`} />
            {busy === "logo" ? "Загружаю…" : logo ? "Заменить логотип" : "Загрузить логотип"}
            <input
              type="file"
              accept="image/png,image/jpeg,image/webp"
              className="hidden"
              disabled={busy === "logo"}
              onChange={(e) => { const f = e.target.files?.[0] ?? null; e.target.value = ""; uploadLogo(f); }}
            />
          </label>
          {logo && (
            <button disabled={busy === "logo"} onClick={removeLogo} className={bGhost}>Убрать</button>
          )}
          <span className="text-[11px] text-[var(--op-dim)]">PNG, JPEG или WEBP · до 200 КБ</span>
        </div>
      </div>
      <div className="flex gap-2">
        <input className={field} value={tname} onChange={(e) => setTname(e.target.value)} placeholder="Название заведения" />
        <button disabled={busy === "rename" || !tname.trim()} onClick={rename} className={`${bPrimary} shrink-0`}>Переименовать</button>
      </div>
      {/* Абонплата — сколько заведение платит НАМ за KLAIT. Ничего не
          списывает и ничего не блокирует: выставления счетов в продукте нет.
          Это число для своего учёта — суммы по сети складываются в списке
          папок. Пустое поле ≠ 0: пусто значит «тариф не назначен», ноль —
          «договорились, что платит ноль».

          Подпись добавлена вместе с подписью у лимита ИИ (19.09): пока их не
          было, поля читались как безымянные числа рядом с кнопками. */}
      <div className="space-y-1">
        <span className="text-[13px] font-semibold text-[var(--op-fg)]">Абонплата, ₽ в месяц</span>
        <div className="flex gap-2">
          <input
            className={field}
            value={fee}
            onChange={(e) => setFee(e.target.value)}
            inputMode="decimal"
            placeholder="пусто — тарифа нет"
          />
          <button disabled={busy === "fee"} onClick={saveFee} className={`${bGhost} shrink-0`}>Сохранить</button>
        </div>
        <p className="text-[12px] leading-snug text-[var(--op-faint)]">
          Сколько клиент платит за KLAIT. Ни на что в продукте не влияет — счета
          не выставляются; число нужно для учёта, суммы по сети складываются в
          списке папок.
        </p>
      </div>
      {/* Лимит ИИ.
          🔴 ПОДПИСЬ ОБЯЗАТЕЛЬНА. Сначала поле стояло без неё, с одной
          подсказкой внутри — а подсказка не видна, когда значение уже есть.
          Снаружи это выглядело как безымянное «20» рядом с кнопкой, и Марат
          спросил «где здесь лимит ИИ», стоя прямо на нём (19.09).

          Показываем ДВА числа, потому что они расходятся: сохранённое (что
          лежит в настройках) и действующее (что реально применяется). У
          демо-стенда действует константа, а старое записанное значение
          работает лишь как пол. */}
      <div className="space-y-1">
        <div className="flex items-baseline justify-between gap-2">
          <span className="text-[13px] font-semibold text-[var(--op-fg)]">Лимит ИИ, вопросов в день</span>
          {aiLimitEffective !== null && (
            <span className="text-[12px] text-[var(--op-muted)]">
              сейчас действует: <b className="text-[var(--op-fg)]">{aiLimitEffective}</b>
              {demoStand ? " · стенд" : ""}
            </span>
          )}
        </div>
        <div className="flex gap-2">
          <input
            className={field}
            value={aiLimit}
            onChange={(e) => setAiLimit(e.target.value)}
            inputMode="numeric"
            placeholder={demoStand ? "пусто — как у стенда" : "пусто — общий лимит"}
          />
          <button disabled={busy === "ai"} onClick={saveAiLimit} className={`${bGhost} shrink-0`}>Сохранить</button>
        </div>
        <p className="text-[12px] leading-snug text-[var(--op-faint)]">
          Пусто — работает общее правило{demoStand ? " для стендов" : ""}. Ноль — помощник
          выключен этому заведению совсем. Своё число здесь всегда сильнее общего.
        </p>
        {/* 🔴 РАСХОД под потолком (Никита, 21.09: «непонятно, сколько в итоге
            стоить»). Карточка показывала два разрешения и ни одной цифры о
            том, что происходит на самом деле. */}
        <AiSpendLine
          usedCalls={aiUsedToday}
          limitCalls={aiLimitEffective}
          usedTokens={aiTokensToday}
          limitTokens={aiTokensEffective}
          rubPerKtoken={aiRubPerKtoken}
        />
      </div>

      {/* Токены — вторая граница, и она про ДЕНЬГИ (21.09). Вопросы меряют
          активность, токены — расход: один разбор смены с большим контекстом
          стоит как тридцать коротких вопросов, и потолок по вопросам про это
          ничего не говорит. */}
      <div className="space-y-1">
        <div className="flex items-baseline justify-between gap-2">
          <span className="text-[13px] font-semibold text-[var(--op-fg)]">Потолок расхода, токенов в день</span>
          {aiTokensEffective !== null && (
            <span className="text-[12px] text-[var(--op-muted)]">
              сейчас действует:{" "}
              <b className="text-[var(--op-fg)]">
                {aiTokensEffective === 0 ? "без потолка" : aiTokensEffective.toLocaleString("ru-RU")}
              </b>
            </span>
          )}
        </div>
        <div className="flex gap-2">
          <input
            className={field}
            value={aiTokens}
            onChange={(e) => setAiTokens(e.target.value)}
            inputMode="numeric"
            placeholder="пусто — общий потолок"
          />
          <button disabled={busy === "aitok"} onClick={saveAiTokens} className={`${bGhost} shrink-0`}>Сохранить</button>
        </div>
        <p className="text-[12px] leading-snug text-[var(--op-faint)]">
          Пусто — общий потолок продукта. Ноль здесь означает «без потолка», а не «выключено»:
          выключает помощника поле выше.
          <br />
          <b>Зачем их два.</b> Вопросы меряют активность, токены — работу: разбор смены с большими
          данными стоит как тридцать коротких вопросов. Потолки независимы, упирается тот, что
          кончится первым — тогда помощник отвечает «дневной объём израсходован» и НЕ тратит
          вопрос из квоты. Человек в чате видит предупреждение с 80 % и может попросить больше —
          заявка придёт в «Поддержку».
        </p>
      </div>
      <div className="flex flex-wrap gap-2">
        <button disabled={busy === "pwd"} onClick={resetPwd} className={bGhost}>Сбросить пароль владельца</button>
        <button disabled={busy === "starter"} onClick={applyStarter} className={bGhost} title="Окна самобронирования, категории склада, часовой пояс — только то, чего ещё нет">Стартовый набор</button>
        {!confirmArchive ? (
          <button onClick={() => setConfirmArchive(true)} className={bDanger}>Архивировать</button>
        ) : (
          <span className="flex items-center gap-2 text-sm">
            <span className="text-[var(--op-muted)]">Точно?</span>
            <button disabled={busy === "archive"} onClick={archive} className={bDangerSolid}>Да, в архив</button>
            <button onClick={() => setConfirmArchive(false)} className={bGhost}>Отмена</button>
          </span>
        )}
      </div>
      {starter && (
        <div className="rounded-2xl border border-black/10 p-3 text-sm">
          {starter.map((v) => (
            <div key={v.venueName}>
              <b>{v.venueName}</b>: {v.added.length ? `добавлено — ${v.added.join("; ")}` : "всё уже было заполнено"}
            </div>
          ))}
        </div>
      )}
      {pwd && (
        <div className="rounded-xl bg-[var(--op-surface-2)] p-3 text-sm text-[var(--op-fg)]">
          Новый пароль владельца{pwd.login ? ` (${pwd.login})` : ""}: <b className="font-mono text-[var(--op-accent)]">{pwd.tempPassword}</b>
          <span className="mt-0.5 block text-xs text-[var(--op-muted)]">Показан один раз — передайте владельцу.</span>
        </div>
      )}
      {/* Демо-стенд. Стоит ДО опасной зоны и отдельным блоком: действие пишет
          выдуманную историю в книги заведения, поэтому подтверждается слагом
          так же, как удаление, и не должно стоять в одном ряду с обычными
          кнопками вроде «Сохранить». */}
      <div className="space-y-2 border-t border-[var(--op-line)] pt-4">
        <span className={lbl}>Демо-стенд</span>
        {!demoStand ? (
          <>
            <p className="text-xs text-[var(--op-muted)]">
              Заведение можно объявить стендом для показа заказчикам. После этого
              его разрешено наполнять выдуманной историей.
              <b className="text-[var(--op-danger-text)]"> Наполнение стирает смены,
              списания и задачи заведения без возможности вернуть.</b> Объявляйте
              стендом только заведение, заведённое специально под демонстрацию.
              Заведение с настоящей выручкой система не пропустит.
            </p>
            <div className="flex gap-2">
              <input className={field} value={demoSlug} placeholder={`подтвердите slug: ${slug}`} onChange={(e) => setDemoSlug(e.target.value)} />
              <button
                disabled={busy === "mark" || demoSlug.trim().toLowerCase() !== slug}
                onClick={markDemo}
                className={`${bDanger} shrink-0`}
              >
                {busy === "mark" ? "Помечаю…" : "Это демо-стенд"}
              </button>
            </div>
          </>
        ) : (
          <>
            <p className="text-xs text-[var(--op-muted)]">
              Заведение помечено как стенд. Наполнение соберёт команду с фото и
              контактами, меню с составом, полгода смен и график на две недели
              вперёд, накладные с движением цен, инвентаризации, выручку по дням,
              отзывы, чек-листы с прогонами, обучение, технику и заявки, планы,
              расходы, чекины и KPI.
              Считается от сегодняшнего дня, поэтому перед показом стенд пересевают
              заново. Пересев стирает прошлый прогон и <b>выдаёт новые пароли</b> —
              памятка, отданная раньше, перестанет работать.
            </p>
            {venues.length > 1 && (
              <select
                className="w-full rounded-lg border border-[var(--op-line-2)] bg-[var(--op-surface)] px-2 py-2 text-sm text-[var(--op-fg)] outline-none focus:border-[var(--op-accent)]"
                value={demoVenue}
                onChange={(e) => setDemoVenue(e.target.value)}
              >
                {venues.map((v) => <option key={v.id} value={v.id}>{v.name}</option>)}
              </select>
            )}
            <input
              className={field}
              value={demoBot}
              placeholder="токен бота Telegram из @BotFather — необязательно"
              autoComplete="off"
              onChange={(e) => setDemoBot(e.target.value)}
            />
            <div className="flex gap-2">
              <input className={field} value={demoSlug} placeholder={`подтвердите slug: ${slug}`} onChange={(e) => setDemoSlug(e.target.value)} />
              <button
                disabled={busy === "demo" || demoSlug.trim().toLowerCase() !== slug}
                onClick={seedDemo}
                className={`${bDanger} shrink-0`}
              >
                {busy === "demo" ? "Собираю…" : "Наполнить заново"}
              </button>
            </div>
          </>
        )}
        {demo && (
          <div className="space-y-2 rounded-xl bg-[var(--op-surface-2)] p-3 text-sm text-[var(--op-fg)]">
            {demo.url && (
              <div>
                Стенд: <a href={demo.url} target="_blank" rel="noreferrer" className="font-mono text-[var(--op-accent)] underline">{demo.url}</a>
              </div>
            )}
            <div className="space-y-1">
              {demo.accounts.map((a) => (
                <div key={a.email} className="flex flex-wrap items-baseline gap-x-2 text-xs">
                  <span className="text-[var(--op-muted)]">{a.role}</span>
                  <span>{a.name}</span>
                  <span className="font-mono text-[var(--op-accent)]">{a.email}</span>
                  <span className="font-mono text-[var(--op-accent)]">{a.password}</span>
                </div>
              ))}
            </div>
            <p className="text-xs text-[var(--op-muted)]">
              Смены пароля при первом входе учётки стенда не требуют — памятку можно
              отдать заказчику как есть. Вопросов к ИИ на стенде: {demo.aiDailyLimit} в день.
              {demo.telegram ? " Бот Telegram подключён: сотрудник привязывает телефон в приложении — Профиль → Telegram." : ""}
            </p>
            <button className={`${bGhost} py-1.5 text-xs`} onClick={copyDemo}>
              {copied ? "Скопировано" : "Скопировать памятку"}
            </button>
            <p className="text-[11px] text-[var(--op-faint)]">
              {Object.entries(demo.counts).map(([k, v]) => `${k}: ${v}`).join(" · ")}
            </p>
          </div>
        )}
      </div>
      <div className="space-y-2 rounded-xl border border-[var(--op-danger-soft)] bg-[var(--op-danger-soft)] p-3">
        <span className="text-xs text-[var(--op-danger-text)]">
          Удалить навсегда — данные (сотрудники, каталог, инвентаризации) НЕ восстановить. Введите slug <b className="font-mono">{slug}</b>:
        </span>
        <div className="flex gap-2">
          <input className={field} value={delSlug} placeholder={slug} onChange={(e) => setDelSlug(e.target.value)} />
          <button disabled={busy === "del" || delSlug.trim().toLowerCase() !== slug} onClick={del} className={`${bDangerSolid} shrink-0`}>Удалить</button>
        </div>
      </div>
      {err && <p className="text-sm text-[var(--op-danger-text)]">{err}</p>}
    </section>
  );
}

// ── Точка (3b): переименование + шапка iiko-бланка ──
function VenueAdmin({ apiKey, tenantId, venue, summary, onDone }: { apiKey: string; tenantId: string; venue: VenueLite; summary: VenueSummary; onDone: () => void }) {
  const venueId = venue.id;
  const [vname, setVname] = useState(summary.venue_name);
  const [vslug, setVslug] = useState(venue.slug);
  const [org, setOrg] = useState(summary.blank.org);
  const [wh, setWh] = useState(summary.blank.warehouse);
  const [busy, setBusy] = useState("");
  const [saved, setSaved] = useState(false);
  const [err, setErr] = useState("");

  function flash() { setSaved(true); setTimeout(() => setSaved(false), 1500); }

  async function renameVenue() {
    setBusy("name"); setErr("");
    // Имя и адрес идут одним запросом: это одна операция «привести карточку
    // точки в порядок», и раздельные кнопки заставляли бы жать дважды.
    const r = await opApi(apiKey, "opRenameVenue", { tenant_id: tenantId, venue_id: venueId, name: vname.trim(), slug: vslug.trim() });
    setBusy("");
    if (r.ok) { flash(); onDone(); } else setErr(r.error);
  }
  async function saveBlank() {
    setBusy("blank"); setErr("");
    const r = await opApi(apiKey, "opSetBlankMeta", { tenant_id: tenantId, venue_id: venueId, org: org.trim(), warehouse: wh.trim() });
    setBusy("");
    if (r.ok) flash(); else setErr(r.error);
  }

  return (
    <div className="space-y-2 border-t border-[var(--op-line)] pt-4">
      <div className="flex items-center justify-between">
        <span className={lbl}>Точка · название, адрес и шапка бланка</span>
        <Saved show={saved} />
      </div>
      <div className="flex flex-wrap gap-2">
        <input className={`${field} min-w-[180px] flex-1`} value={vname} onChange={(e) => setVname(e.target.value)} placeholder="Название точки" />
        <div className="flex min-w-[200px] flex-1 items-center gap-1.5">
          <span className="shrink-0 font-mono text-xs text-[var(--op-faint)]">/</span>
          <input className={`${field} w-full font-mono`} value={vslug} onChange={(e) => setVslug(e.target.value)} placeholder="patriki" aria-label="Адрес точки" />
        </div>
        <button disabled={busy === "name" || !vname.trim() || !vslug.trim()} onClick={renameVenue} className={`${bPrimary} shrink-0`}>Сохранить</button>
      </div>
      {/* Адрес точки виден целиком: иначе непонятно, что за «/patriki» и где он
          окажется. Заведение — сабдомен, точка — первый сегмент пути. */}
      <p className="font-mono text-[11px] text-[var(--op-faint)]">
        {`…/${vslug.trim() || "…"}/desk`}
      </p>
      <div className="grid gap-2 sm:grid-cols-2">
        <input className={field} value={org} onChange={(e) => setOrg(e.target.value)} placeholder="Организация (шапка бланка)" />
        <input className={field} value={wh} onChange={(e) => setWh(e.target.value)} placeholder="Склад" />
      </div>
      <button disabled={busy === "blank"} onClick={saveBlank} className={bGhost}>Сохранить шапку бланка</button>
      {err && <p className="text-xs text-[var(--op-danger-text)]">{err}</p>}
    </div>
  );
}
